import React, { useState, useEffect } from "react";
import { useParams, Link, useLocation, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { compressImage } from "@/lib/compressImage";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { STAGE_LABEL, STAGE_BADGE, STAGE_HEADER, STAGE_PILL, PRODUCTION_SUBSTAGE, PRODUCTION_SUBSTAGE_PILL, productionSubstage, packedStageOverride } from "@/constants/roles";
import { toast } from "sonner";
import ShipStationDialog from "@/components/ShipStationDialog";
import SelfCheckoutDialog from "@/components/SelfCheckoutDialog";
import ShipFromShopifyDialog from "@/components/ShipFromShopifyDialog";
import { EditContactButton } from "@/components/EditContactDialog";
import { useT } from "@/i18n/LanguageContext";
import { UsFlag, UsFlagIcon, isUsOrder } from "@/components/UsFlag";
import { holdReasonText } from "@/components/holdReason";
import { PickupBadge } from "@/components/PickupBadge";
import { EmailBouncedBadge } from "@/components/EmailBouncedBadge";
import { buildSignoffEvents, signerName, formatWhen } from "@/lib/signoffEvents";
import { useDriveUpload, uploadKindFor } from "@/lib/useDriveUpload";
import { PreorderPhotoUploadButton } from "@/components/PreorderPhotoUploadButton";
import { ArchiveReplacedButton } from "@/components/ArchiveReplacedButton";
import { VinHint } from "@/components/VinHint";
import { SideNotOrderedCard, ShopifyDiscountCard } from "@/components/ShopifyDiscount";
import { COVER_BRANDS, COVER_BRAND_CODES, SKIP_CODE, NA_CODE, brandLabel, splitCoverCode, joinCoverCode, displayCode, coverCodeBlockers, coverCodeComplete, CLAMP_CODES, clampLabel } from "@/constants/coverCodes";
import { ClampSelect } from "@/components/ClampSelect";
import { clampsOnly, clampsSide, clampsLabel, clampCountExpected, packingFieldsFor, EXTRA_STEP_META, CLAMPS_SIDE_LABEL } from "@/lib/packingFields";
import { ClampsOnlyNote, ClampsSideSelect, ShipsWithPicker, CarriesBanner } from "@/components/WarrantyClamps";
import { NoteText } from "@/components/NoteText";
import { TrackingLink } from "@/components/TrackingLink";
import { IssuePhotos } from "@/components/IssuePhotos";
import { HoldRespondButtons, QcResolveButton, ReorderDecideButtons, StoreAckButton, HOLD_RESPONDER_ROLES, canDecideReorder, canAckStore, stationForWarehouse, STATION_LABEL } from "@/components/HoldRespondDialog";
import { CancelStockStopDialog, canConfirmCancelStop } from "@/components/CancelStockStopDialog";
import { HoldStopDialog, canAckHoldStop } from "@/components/HoldStopDialog";
import { StockInDialog, canStockIn } from "@/components/StockInDialog";
import { JourneyTimeline } from "@/components/JourneyTimeline";
import { ShipHoldButton } from "@/components/ShipHoldButton";
import { ExtensionRequestButton } from "@/components/ProductionExtensionDialog";
import { printProductionSheet } from "@/lib/printProduction";
import { printStopLabel } from "@/lib/printStopLabel";
import { uploadClaimVideo, fmtBytes } from "@/lib/uploadClaimVideo";
import { ColorSwatchImage } from "@/components/ColorSwatch";
import { LogoLabelPreview } from "@/components/LogoLabel";
import { FlagResumeButton } from "@/components/FlagResumeButton";
import { ClaimBanner } from "@/components/ClaimBanner";
import { MatchSuggestionBanner } from "@/components/MatchSuggestionBanner";
import { BackToPreOrderButton } from "@/components/BackToPreOrderButton";
import { ShopifyRefundStrip } from "@/components/ShopifyRefundStrip";
import { ReviewRequestButton } from "@/components/ReviewRequestPanel";
import { Camera, Star } from "lucide-react";
import { photoUrl, isImageFile, canOpenDrive } from "@/lib/photos";
import { CatalogSyncButton } from "@/components/CatalogSyncDialog";
import { WAREHOUSES, WAREHOUSE_LABEL, WAREHOUSE_FLAG, WAREHOUSE_STATION_LABEL, STOCK_FACTORIES, FACTORY_SHORT, pickupInfo, pickupPending } from "@/constants/warehouses";
import { PickupCard } from "@/components/PickupCard";
import { CatalogSuggestions } from "@/components/CatalogSuggestions";
import { VehicleFitBanner } from "@/components/VehicleFitBanner";
import { ClampAdjustCard } from "@/components/ClampAdjustCard";
import { effectiveColor, CustomColorNote } from "@/components/CustomColorNote";
import { StatusEmailButton } from "@/components/StatusEmailButton";
import { CancelReasonRow, RemovedCancelReasons } from "@/components/CancelReasonRow";

const BADGE_CLS = {
  neutral: "bg-slate-100 text-slate-700 border-slate-200",
  blue: "bg-blue-50 text-blue-700 border-blue-200",
  warning: "bg-amber-50 text-amber-700 border-amber-200",
  success: "bg-emerald-50 text-emerald-700 border-emerald-200",
  critical: "bg-red-50 text-red-700 border-red-200",
};

const PACKING_FIELDS_BY_STAGE = {
  packing: ["po_cat", "color", "front_cover", "f_clamp", "rear_cover", "r_clamp", "quality", "lp_label", "clamp_label", "logo_label", "picture_to_drive", "spacers", "tool_kit", "gift"],
  inventory_packing: ["po_cat", "color", "front_cover", "f_clamp", "rear_cover", "r_clamp", "quality", "lp_label", "clamp_label", "logo_label", "picture_to_drive", "spacers", "tool_kit", "gift"],
};

// Cancel reasons that pre-tick the automatic Shopify full refund (mirrors backend REFUND_CANCEL_REASON_IDS).
const REFUND_CANCEL_REASON_IDS = ["reason-no-mold", "reason-client-before-start"];
// Stages before Production is signed — cancelling here = cancel + refund in Shopify, no stock (rule Oct 27, 2026).
const PRE_PRODUCTION_STAGES = ["lead", "pre_order", "pending", "advance", "na", "flagged", "abandoned"];
// Where a cancelled order's set ends up (mirrors backend CANCEL_STOCK_RULE, user rule Oct 27, 2026). Nothing is
// added to stock before the station confirms the full-screen STOP (after packing / when received).
const CANCEL_STOCK_RULE = { production: "china", product_transfer: "china", packing: "china", inventory_packing: "canada", shipping: "on_arrival", pickup_3pl: "on_arrival", completed: "on_arrival", fulfilled: "on_arrival" };
const CANCEL_STOCK_LABEL = { china: "🇨🇳 China warehouse", canada: "🇨🇦 Canada warehouse", usa: "🇺🇸 USA warehouse", on_arrival: "On arrival: 🇺🇸 USA if US flag, else 🇨🇦 Canada", none: "Don't add to stock" };
const CANCEL_STOCK_WHEN = { production: "after Finish packing", product_transfer: "after Finish packing", packing: "after Finish packing", inventory_packing: "after Finish packing", shipping: "when Marked received", pickup_3pl: "when Marked received", completed: "right away", fulfilled: "right away" };

// Human-readable label for each packing field-code — passed through t()
// at render time so the compact checklist grid gets fully translated.
const PACKING_FIELD_LABEL = {
  po_cat: "PO CAT",
  color: "COLOR",
  front_cover: "FRONT COVER",
  f_clamp: "F CLAMP",
  rear_cover: "REAR COVER",
  r_clamp: "R CLAMP",
  quality: "QUALITY",
  lp_label: "LP LABEL",
  clamp_label: "CLAMP LABEL",
  logo_label: "LOGO LABEL",
  picture_to_drive: "PICTURE TO DRIVE",
  spacers: "SPACERS",
  tool_kit: "TOOL KIT",
  gift: "GIFT",
  clamp_count: "CLAMP COUNT",
  ships_with: "SHIPS INSIDE BOX OF",
};

// Map order status → URL stage segment used by OrdersList (`/orders/:stage`).
// Used as a fallback for "← back to list" when router state is missing
// (direct URL open, page refresh, or opening in a new tab).
const STATUS_TO_STAGE = {
  lead: "pre_order",
  pre_order: "pre_order",
  pending: "pending",
  advance: "advance",
  production: "production",
  product_transfer: "product_transfer",
  inventory_packing: "inventory_packing",
  packing: "packing",
  shipping: "shipping",
  bo_shipping: "shipping",
  orders_received: "completed",
  completed: "completed",
  cancelled: "cancelled",
};

export default function OrderDetail() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, lang } = useT();
  const qc = useQueryClient();
  const { data: o, isLoading } = useQuery({ queryKey: ["order", id], queryFn: async () => (await api.get(`/orders/${id}`)).data });
  // Same key as FlagResumeButton → one request; used to hide the explicit
  // stage button that duplicates "↩ Continue → <origin>".
  const { data: flagOrigin } = useQuery({
    queryKey: ["flag-origin", id, o?.status],
    queryFn: async () => (await api.get(`/orders/${id}/flag-origin`)).data,
    enabled: o?.status === "flagged",
  });
  // Auto-clear the dashboard alert as soon as a BO / SA actually opens
  // a flagged or NA order — matches the user rule "keep alert until
  // opened and/or moved". Fires once when the order arrives + is in one
  // of those states. Silent — never toasts on the way in.
  useEffect(() => {
    if (!o) return;
    if (!(o.status === "flagged" || o.status === "na")) return;
    if (!(user?.role === "back_office" || user?.role === "super_admin")) return;
    api.post(`/orders/${id}/mark-viewed`)
      .then(() => qc.invalidateQueries({ queryKey: ["alerts"] }))
      .catch(() => {/* silent — nothing to do for the operator */});
  }, [o?.id, o?.status, user?.role, id, qc]);
  // Signatures are auto-recorded from the currently logged-in team member —
  // every operator has a unique account, so their identity IS the signature.
  // (No more free-text name inputs on Sign Off / Packing / Recheck / etc.)
  const mySignature = user?.name || user?.email || "unknown";
  const [form, setForm] = useState({});
  const [note, setNote] = useState("");
  const [editMode, setEditMode] = useState(false);
  const [activeTab, setActiveTab] = useState(() => {
    // Honor ?tab=… in the URL so callers (e.g. "▶ Start Packing" from the
    // Packing Orders list) can deep-link directly into a specific tab.
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search);
      const t = params.get("tab");
      if (t) return t;
    }
    return null;
  });
  // When the packing tab is auto-opened via ?tab=packing on an
  // inventory_packing order (i.e. Inv Packing pressed "▶ START PACKING"),
  // scroll the page to the bottom so the wizard's current step is right
  // under the operator's thumb on a tablet. Fires once per mount; the user
  // scrolls back up on their own if they need to review the header.
  useEffect(() => {
    if (!o?.id) return;
    if (activeTab !== "packing") return;
    if (o.status !== "inventory_packing") return;
    const t = setTimeout(() => {
      try {
        window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
      } catch (e) { /* ignore */ }
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [o?.id, activeTab]);
  const [delayOpen, setDelayOpen] = useState(false);
  const [delayEta, setDelayEta] = useState("");
  const [delayReason, setDelayReason] = useState("");
  const [stopOpen, setStopOpen] = useState(false);
  const [stopFiles, setStopFiles] = useState([]);
  const [stopReason, setStopReason] = useState("");
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  // Universal Cancel modal — reason checklist + "add reason" state.
  // Fetches the active cancel reasons from the backend so BO can add new
  // ones via the modal itself. Each checked reason fires an email using
  // its own template (same infra as missing-info / photo-reminder emails).
  const [cancelReasonIds, setCancelReasonIds] = useState([]);
  const [cancelAddOpen, setCancelAddOpen] = useState(false);
  const [cancelSendEmails, setCancelSendEmails] = useState(true);
  const [cancelShopifyRefund, setCancelShopifyRefund] = useState(true);
  const [cancelStockWarehouse, setCancelStockWarehouse] = useState("auto");
  const [cancelStopOpen, setCancelStopOpen] = useState(false);
  const [holdStopOpen, setHoldStopOpen] = useState(false);
  const [newReasonForm, setNewReasonForm] = useState({ label: "", description: "", email_subject: "", email_body: "" });
  const cancelReasonsQuery = useQuery({
    queryKey: ["cancel-reasons"],
    queryFn: async () => (await api.get("/cancel-reasons")).data,
    staleTime: 60_000,
    enabled: cancelOpen,
  });
  const addCancelReason = useMutation({
    mutationFn: (payload) => api.post("/cancel-reasons", payload),
    onSuccess: () => {
      toast.success("Reason added");
      qc.invalidateQueries({ queryKey: ["cancel-reasons"] });
      setCancelAddOpen(false);
      setNewReasonForm({ label: "", description: "", email_subject: "", email_body: "" });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not add reason"),
  });
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [cancelRefundAmount, setCancelRefundAmount] = useState("");
  // Cancel-email preview modal — appears BETWEEN "Confirm Cancel" and the
  // actual /cancel-with-reasons call so BO can review each reason's email
  // (subject + body) and toggle them on/off individually.
  const [cancelPreviewOpen, setCancelPreviewOpen] = useState(false);
  const [cancelPreviewEmails, setCancelPreviewEmails] = useState([]); // [{template_key, reason_label, subject, body_html, ...}]
  const [cancelPreviewChecked, setCancelPreviewChecked] = useState({}); // { template_key: true/false }
  // When true, the preview switches to "combined" mode — ONE email listing
  // every checked reason as a bullet block instead of N separate emails.
  const [cancelCombined, setCancelCombined] = useState(false);
  // Collapsed by default — the section header shows a small "more" toggle
  // that opens the full sign-off timeline.
  const [signoffsOpen, setSignoffsOpen] = useState(false);
  // Approve Lead dialog state
  const [leadApproveOpen, setLeadApproveOpen] = useState(false);
  const emptyLeadApprove = { po_number: "", shopify_order_number: "", payment_confirmed: true, payment_amount: "", payment_method: "", payment_reference: "", notes: "" };
  const [leadApproveForm, setLeadApproveForm] = useState(emptyLeadApprove);
  const approveLead = useMutation({
    mutationFn: (d) => api.post(`/orders/${id}/approve-lead`, d),
    onSuccess: () => {
      qc.invalidateQueries();
      setLeadApproveOpen(false); setLeadApproveForm(emptyLeadApprove);
      toast.success("Lead approved · promoted to Pre Order");
      // Lead → Pre Order moves the order to a different list, so bounce back.
      goBackToList();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });
  // Super Admin — Edit Customer Name dialog. Kept behind a super_admin gate
  // per user rule Feb 28, 2026 ("allow super admin only to change client name").
  const [editNameOpen, setEditNameOpen] = useState(false);
  const [editNameValue, setEditNameValue] = useState("");
  const editCustomerName = useMutation({
    mutationFn: (payload) => api.patch(`/customers/${o?.customer_id}`, payload),
    onSuccess: () => {
      qc.invalidateQueries();
      setEditNameOpen(false);
      toast.success("Customer name updated");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed to update"),
  });
  // Super Admin — Delete Lead dialog. Reuses the existing hard-delete endpoint
  // (`DELETE /orders/{oid}?confirm_po=<PO>`) which is already super_admin-only.
  const [deleteLeadOpen, setDeleteLeadOpen] = useState(false);
  const [deleteLeadConfirm, setDeleteLeadConfirm] = useState("");
  const deleteLead = useMutation({
    mutationFn: (po) => api.delete(`/orders/${id}`, { params: { confirm_po: po } }),
    onSuccess: () => {
      qc.invalidateQueries();
      toast.success(`Lead ${o?.po_number || ""} deleted`);
      setDeleteLeadOpen(false); setDeleteLeadConfirm("");
      // Lead is gone — bounce back to the Pre Orders list.
      navigate("/orders/pre_order");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Delete failed"),
  });
  // Lead → Edit Codes (Mar 2026). Compact dialog to fix bogus codes
  // (e.g. Drive-sync placeholders like "PO 1"/"PO 10") without leaving
  // the header. Reuses the standard PATCH /orders/{oid} endpoint.
  const [leadEditCodesOpen, setLeadEditCodesOpen] = useState(false);
  const [leadCodesForm, setLeadCodesForm] = useState({
    front_cover_code: "", f_clamp_code: "", rear_cover_code: "", r_clamp_code: "",
  });
  React.useEffect(() => {
    if (leadEditCodesOpen && o) {
      setLeadCodesForm({
        front_cover_code: o.front_cover_code || "",
        f_clamp_code: o.f_clamp_code || "",
        rear_cover_code: o.rear_cover_code || "",
        r_clamp_code: o.r_clamp_code || "",
      });
    }
  }, [leadEditCodesOpen, o]);
  const saveLeadCodes = useMutation({
    mutationFn: () => api.patch(`/orders/${id}`, leadCodesForm),
    onSuccess: () => {
      qc.invalidateQueries();
      toast.success("Codes updated");
      setLeadEditCodesOpen(false);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Save failed"),
  });
  // Inventory re-match + manual CAT # assign
  const [assignCatOpen, setAssignCatOpen] = useState(false);
  const [assignCatValue, setAssignCatValue] = useState("");
  const rematch = useMutation({
    mutationFn: () => api.post(`/orders/${id}/rematch-inventory`),
    onSuccess: (r) => {
      qc.invalidateQueries();
      const m = r?.data?.match;
      const spec = r?.data?.spec_mismatch?.items || [];
      if (r?.data?.auto_assigned) toast.success(`CAT # ${m?.cat_number} assigned automatically — codes and vehicle match the catalog exactly (colour is not part of the CAT #)`);
      else if (m && spec.length) toast.warning(`Codes + vehicle match CAT # ${m.cat_number}, but ${spec.map((i) => `${i.label.toLowerCase()} (order ${i.order_value || "—"} vs catalog ${i.catalog_value})`).join(", ")} — repair the order info or approve with a signature`);
      else if (m) toast.info(`Codes + vehicle match CAT # ${m.cat_number}`);
      else if (r?.data?.reason === "missing_vehicle") toast.warning("Order has no brand / model / year — the catalog is not looked up. Fill the car info or assign the CAT # manually.");
      else if (r?.data?.reason === "vehicle_mismatch") toast.warning("Codes match a CAT # for a different vehicle — approve it with a signature or assign another CAT #.");
      else if (r?.data?.reason === "stock_other_color") toast.warning(`Codes + vehicle match CAT # ${(r?.data?.suggestions || [])[0]?.cat_number || ""} — sets in stock in another colour. Sell the stock colour or approve with a signature to produce ${o?.color || "the order colour"}.`);
      else if (r?.data?.reason === "no_inventory") toast.warning("No inventory row matches these codes. See partial hits below or assign manually.");
      else toast.warning(r?.data?.reason || "No match found");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });
  const assignCat = useMutation({
    mutationFn: () => api.post(`/orders/${id}/assign-catalog`, { catalog_number: assignCatValue.trim() }),
    onSuccess: () => { qc.invalidateQueries(); setAssignCatOpen(false); setAssignCatValue(""); toast.success("CAT # assigned"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });
  // Advance-stage transition guard: before moving to Production OR Inventory
  // Packing, the order MUST have (1) a Catalog # (auto-matched from
  // Front+Rear codes OR manually assigned by Back Office), (2) a Spacer
  // type, and (3) a Spacer Qty. Missing anything → pop-up blocks the move.
  const [missingOpen, setMissingOpen] = useState(false);
  const [missingList, setMissingList] = useState([]);
  const [pendingTarget, setPendingTarget] = useState(null); // "production" | "inventory_packing" | "pickup"
  const [pickupOpen, setPickupOpen] = useState(false);
  const [pickupFixed, setPickupFixed] = useState(null); // "canada" → dialog locked to Canada storage
  const guardedTransition = (target) => {
    const missing = [];
    if (!(o?.catalog_number || "").trim()) {
      const sugg = (invMatch?.suggestions || [])[0];
      missing.push(invMatch?.reason === "vehicle_mismatch" && sugg
        ? `Catalog # — suggested CAT # ${sugg.cat_number} is for a different vehicle, approve it with a signature in the Inventory availability box (or assign another CAT #)`
        : invMatch?.reason === "stock_other_color" && sugg
          ? `Catalog # — CAT # ${sugg.cat_number} has sets in stock in another colour; approve it with a signature in the Inventory availability box (or assign another CAT #)`
          : invMatch?.reason === "codes_vehicle_match" && sugg
            ? `Catalog # — CAT # ${sugg.cat_number} matches codes + vehicle but ${(sugg.spec_mismatch || []).map((i) => i.label.toLowerCase()).join(" / ") || "the catalog specs"} differ — repair the order info or approve it with a signature in the Inventory availability box`
            : invMatch?.reason === "missing_vehicle"
              ? "Catalog # — order has no brand / model / year, so nothing is proposed; assign the CAT # manually"
              : "Catalog # (approve the proposal in the Inventory availability box or assign manually)");
    }
    if (!(o?.spacer || "").trim()) missing.push("Spacer (No spacer / 3mm / 5mm / S-3mm / S-5mm)");
    if (!o?.spacer_qty) missing.push("Spacer Qty (2 or 4)");
    if (missing.length) {
      setMissingList(missing);
      setPendingTarget(target);
      setMissingOpen(true);
      return;
    }
    if (target === "pickup") { setPickupFixed(null); setPickupOpen(true); return; }
    if (target === "pickup_canada") { setPickupFixed("canada"); setPickupOpen(true); return; }
    transition.mutate(target);
  };

  React.useEffect(() => {
    if (o) setForm({
      brand: o.brand || "", model: o.model || "", year: o.year || "", trim: o.trim || "",
      vin: o.vin || "", rim_size: o.rim_size || "", color: o.color || "", logo: o.logo || "",
      spacer: o.spacer || "", spacer_qty: o.spacer_qty ? String(o.spacer_qty) : "2",
      front_cover_code: o.front_cover_code || "", f_clamp_code: o.f_clamp_code || "",
      rear_cover_code: o.rear_cover_code || "", r_clamp_code: o.r_clamp_code || "",
      catalog_number: o.catalog_number || "",
    });
  }, [o]);

  const update = useMutation({
    mutationFn: (payload) => api.patch(`/orders/${id}`, payload),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["order", id] }); toast.success("Saved"); },
  });

  // Silent auto-save: default Spacer Qty to 2 the first time an order is
  // opened with a blank/null qty. Fires once per order (guarded by ref so
  // the query invalidation triggered by the PATCH itself doesn't re-fire).
  const spacerQtyDefaultedRef = React.useRef(null);
  React.useEffect(() => {
    if (!o?.id) return;
    if (o.spacer_qty) return;
    if (spacerQtyDefaultedRef.current === o.id) return;
    spacerQtyDefaultedRef.current = o.id;
    // Silent PATCH — no toast, no error surface (best-effort default).
    api.patch(`/orders/${o.id}`, { spacer_qty: 2 })
      .then(() => qc.invalidateQueries({ queryKey: ["order", id] }))
      .catch(() => {});
  }, [o?.id, o?.spacer_qty, id, qc]);

  // Small helper called from any mutation that moves the order to a different
  // station/list. Sends the operator back to the origin list so they can
  // continue with the next order without an extra "back" click.
  const goBackToList = React.useCallback(() => {
    const fromState = location.state?.from;
    if (fromState === "report") { navigate("/report"); return; }
    if (fromState === "rush") { navigate("/rush"); return; }
    const fallback = STATUS_TO_STAGE[o?.status] || "pending";
    navigate(`/orders/${fromState || fallback}`);
  }, [location.state, o?.status, navigate]);

  const transition = useMutation({
    mutationFn: (target) => api.post(`/orders/${id}/transition`, { target_stage: target }),
    onSuccess: () => {
      qc.invalidateQueries();
      toast.success("Stage updated");
      goBackToList();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not change stage"),
  });
  const factorySign = useMutation({
    mutationFn: () => api.post(`/orders/${id}/factory-sign`, { signature: mySignature }),
    onSuccess: () => {
      qc.invalidateQueries();
      toast.success(`Production signed & started by ${mySignature}`);
      goBackToList();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed to sign production"),
  });
  const productionComplete = useMutation({
    mutationFn: () => api.post(`/orders/${id}/production-complete`, { signature: mySignature }),
    onSuccess: () => {
      qc.invalidateQueries();
      toast.success(`Production marked complete by ${mySignature}`);
      goBackToList();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });
  // Packing Station · Mark Received in Product Transfer.
  // Auto-records the currently signed-in team member as the receiver on the
  // Approvals & Sign-offs section (no name input required).
  const packingReceive = useMutation({
    mutationFn: () => api.post(`/orders/${id}/transition`, { target_stage: "packing", signature: mySignature }),
    onSuccess: () => {
      qc.invalidateQueries();
      toast.success(`Marked received by ${mySignature}`);
      goBackToList();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not mark received"),
  });
  const usFlagToggle = useMutation({
    mutationFn: (show) => api.post(`/orders/${id}/us-flag`, { show }),
    onSuccess: (res) => {
      qc.invalidateQueries();
      toast.success(res?.data?.us_flag_override ? "USA flag shown for all users" : "USA flag hidden for all users");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not update USA flag"),
  });
  // 🏬 Pick-up flag — BO/SA can correct a mis-detected Shopify order or flag a manual one.
  const pickupToggle = useMutation({
    mutationFn: (on) => api.patch(`/orders/${id}`, { is_pickup: on }),
    onSuccess: (_r, on) => {
      qc.invalidateQueries();
      toast.success(on ? "🏬 Marked as a pick-up order" : "Pick up switched off — order will ship");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not update pick-up flag"),
  });
  const cancelOrder = useMutation({
    mutationFn: ({ reason, reason_ids, send_emails, email_template_keys, combined_email }) =>
      api.post(`/orders/${id}/cancel-with-reasons`, {
        reason_ids: reason_ids || [],
        note: reason || null,
        send_emails: send_emails !== false,
        // Optional: when set, ONLY these template_keys go out (rest are skipped).
        // Populated by the preview modal's per-email toggles.
        email_template_keys: email_template_keys || undefined,
        combined_email: !!combined_email,
        // Shopify cancel + full refund: pre-ticked for No Mold / before-start reasons and for every pre-production cancel.
        shopify_refund: ((reason_ids || []).some((id) => REFUND_CANCEL_REASON_IDS.includes(id)) || PRE_PRODUCTION_STAGES.includes(o?.status)) ? cancelShopifyRefund : false,
        // Where the physical set goes ("auto" = rule by status; BO may override in the pop-up).
        stock_warehouse: cancelStockWarehouse,
      }),
    onSuccess: (res) => {
      qc.invalidateQueries();
      const r = res?.data?.refund;
      const sr = res?.data?.shopify_refund;
      const st = res?.data?.stock;
      const emails = res?.data?.emails || [];
      const sent = emails.filter((e) => e.status === "sent").length;
      const failed = emails.filter((e) => e.status === "error").length;
      const parts = [];
      if (sent) parts.push(`${sent} email${sent === 1 ? "" : "s"} sent`);
      if (failed) parts.push(`${failed} email${failed === 1 ? "" : "s"} FAILED`);
      if (r) parts.push(`Refund $${r.amount} awaiting factory approval`);
      if (sr) parts.push(sr.error ? `Shopify cancel/refund FAILED: ${sr.error}` : `Shopify cancelled + refunded${sr.refunded_amount ? ` · ${sr.refunded_amount} ${sr.currency || ""}` : ""}`);
      if (st?.pending) parts.push(`stays in ${STAGE_LABEL[res.data.status] || res.data.status} — 🛑 STOP ${CANCEL_STOCK_WHEN[res.data.status] || "later"} → ${st.warehouse ? CANCEL_STOCK_LABEL[st.warehouse] : "USA / Canada by flag"}`);
      else if (st) parts.push(`set → ${String(st.warehouse).toUpperCase()} stock`);
      const suffix = parts.length ? ` · ${parts.join(" · ")}` : "";
      if (failed || sr?.error) toast.error(`Order cancelled${suffix}`, { duration: 12000 });
      else toast.success(`Order cancelled${suffix}`);
      setCancelOpen(false);
      setCancelPreviewOpen(false);
      setCancelReason("");
      setCancelRefundAmount("");
      setCancelReasonIds([]);
      setCancelPreviewEmails([]);
      setCancelPreviewChecked({});
      setCancelCombined(false);
      setCancelStockWarehouse("auto");
      goBackToList();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed to cancel"),
  });
  // Compose the per-reason (or combined) email previews via a dry-run
  // backend call. Non-destructive: the order stays in its current status
  // until the user confirms from the preview modal.
  const previewCancelEmails = useMutation({
    mutationFn: (opts) => api.post(`/orders/${id}/cancel-emails-preview`, {
      reason_ids: cancelReasonIds,
      note: cancelReason || null,
      combined: !!(opts && opts.combined),
    }),
    onSuccess: (res, opts) => {
      const emails = res?.data?.emails || [];
      const isCombined = !!(opts && opts.combined);
      setCancelPreviewEmails(emails);
      setCancelCombined(isCombined);
      // Default: every previewable email is ticked ON. Combined mode always
      // has exactly one card keyed by "cancel_combined".
      const initial = {};
      for (const e of emails) {
        if (e.template_key && !e.error) initial[e.template_key] = true;
      }
      setCancelPreviewChecked(initial);
      setCancelPreviewOpen(true);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed to build email preview"),
  });
  const productionDelay = useMutation({
    mutationFn: ({ new_eta, reason }) => api.post(`/orders/${id}/production-delay`, { new_eta, reason }),
    onSuccess: () => { qc.invalidateQueries(); toast.success("Delay reported — Super Admin & Back Office notified"); setDelayOpen(false); setDelayEta(""); setDelayReason(""); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });
  const stopReport = useMutation({
    mutationFn: async ({ reason, files }) => {
      const fd = new FormData();
      fd.append("reason", reason);
      for (const f of files || []) fd.append("files", await compressImage(f));
      return api.post(`/orders/${id}/report-issue`, fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
    },
    onSuccess: () => {
      qc.invalidateQueries();
      toast.success(t("Issue reported — Shipping Desk, Back Office & Super Admin notified"));
      setStopOpen(false); setStopReason(""); setStopFiles([]);
      goBackToList();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });
  const jetForm = useMutation({
    // Smart VIN Decoder: prefers the VIN typed in the Order Info field.
    // - If a valid 17-char VIN is present → hit /decode-vin (NHTSA only, no Vision, no photos).
    // - Else if the order has a linked Drive folder → fall back to /auto-decode
    //   which runs Claude Vision on the photos to READ the VIN first.
    // - Else → surface a clear error, since we have neither a VIN nor photos to work from.
    mutationFn: async () => {
      const typedVin = String(form.vin || o.vin || "").trim().toUpperCase();
      if (typedVin.length === 17) {
        // Fast path: decode straight from the VIN line (no photos, no AI).
        const res = (await api.post(`/orders/${id}/decode-vin`, { vin: typedVin }, { timeout: 20000 })).data;
        return { ok: true, source: "vin_line", decoded: { suggestions: res.suggestions, vpic: res.vpic }, applied: res.applied || [], skipped: res.skipped || [] };
      }
      if (typedVin.length > 0 && typedVin.length !== 17) {
        // Explicit "wrong VIN" — don't silently fall through to photos, that's confusing.
        const err = new Error(`VIN must be 17 characters (currently ${typedVin.length}). Please correct the VIN or clear the field to decode from photos.`);
        err.userVisible = true;
        throw err;
      }
      // Slow path: use Claude Vision on Drive photos to READ the VIN first.
      if (!o.drive_folder_id) {
        const err = new Error("Type a 17-character VIN, or link a Drive folder with photos so we can read the VIN.");
        err.userVisible = true;
        throw err;
      }
      const decoded = (await api.post(`/orders/${id}/auto-decode`, {}, { timeout: 90000 })).data;
      const suggestions = decoded.suggestions || {};
      if (Object.keys(suggestions).length === 0) {
        return { ok: false, source: "photos", decoded, applied: [], skipped: [] };
      }
      const applied = (await api.post(`/orders/${id}/apply-suggestions`, { fields: suggestions, overwrite: true })).data;
      return { ok: true, source: "photos", decoded, applied: applied.applied || [], skipped: applied.skipped || [] };
    },
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["order", id] });
      qc.invalidateQueries({ queryKey: ["orders"] });
      if (!r.ok) {
        toast.warning(`AI ran but no fields could be extracted from the photos.`);
        return;
      }
      const parts = [];
      const sourceLabel = r.source === "vin_line" ? "VIN line" : "photos";
      if (r.applied.length) parts.push(`Filled ${r.applied.length} from ${sourceLabel}: ${r.applied.join(", ")}`);
      else parts.push(`Decoded from ${sourceLabel}, but all fields already had values`);
      if (r.skipped.length) parts.push(`${r.skipped.length} skipped (already filled)`);
      const vin = r.decoded?.suggestions?.vin;
      const conf = r.decoded?.ai?.vin_confidence;
      if (vin && r.source === "photos") parts.push(`VIN=${vin}${conf ? ` (${conf})` : ""}`);
      toast.success(parts.join(" · "));
    },
    onError: (e) => {
      const msg = e?.userVisible ? e.message : (e?.response?.data?.detail || "VIN decode failed");
      toast.error(msg);
    },
  });
  const rimScan = useMutation({
    // Rim-scan Drive fallback — kept so external callers with a linked
    // Drive folder can bulk-scan without the picker. Not wired into any
    // button anymore; the RimCaptureModal is the primary UI now.
    mutationFn: async () => {
      if (!o.drive_folder_id) {
        const err = new Error("Link a Drive folder with rim / tire photos first.");
        err.userVisible = true;
        throw err;
      }
      const decoded = (await api.post(`/orders/${id}/auto-decode`, {}, { timeout: 90000 })).data;
      const rim = decoded?.suggestions?.rim_size;
      if (!rim) return { ok: false };
      const applied = (await api.post(`/orders/${id}/apply-suggestions`, { fields: { rim_size: rim }, overwrite: false })).data;
      return { ok: true, rim, applied: applied.applied || [] };
    },
    onSuccess: (r) => {
      qc.invalidateQueries();
      if (!r.ok) return toast.warning("Couldn't find a rim size in the photos.");
      toast.success(r.applied?.length ? `Rim ${r.rim} — filled from photos` : `Rim ${r.rim} — already filled`);
    },
    onError: (e) => toast.error(e?.userVisible ? e.message : (e?.response?.data?.detail || "Rim scan failed")),
  });
  // ---- Manual rim capture modal (device camera with alignment guide) ----
  const [rimCaptureOpen, setRimCaptureOpen] = useState(false);
  const sendEmail = useMutation({
    mutationFn: (body) => api.post(`/mock/email`, body),
    onSuccess: () => toast.success("Email sent"),
  });
  const shipLabel = useMutation({
    mutationFn: () => api.post(`/mock/ship-label/${id}`),
    onSuccess: () => { qc.invalidateQueries(); toast.success("Shipping label generated"); },
  });
  const pack = useMutation({
    mutationFn: ({ field, value }) => api.post(`/orders/${id}/packing-check`, { field, value, employee_signature: user.name }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["order", id] }),
  });
  const packingFinish = useMutation({
    mutationFn: () => api.post(`/orders/${id}/packing-finish`, { signature: mySignature }),
    onSuccess: (r) => {
      qc.invalidateQueries();
      // Backend now keeps inventory_packing orders in-station and stamps them
      // as "ready to ship". Packing Orders still transition to shipping.
      const status = r?.data?.status;
      if (r?.data?.cancel_pending) {
        // 🛑 Cancelled order: it never ships — the STOP screen takes over right here.
        toast.error(`Packed by ${mySignature} — 🛑 CANCELLED ORDER: do not ship, confirm the STOP`, { duration: 8000 });
        setCancelStopOpen(true);
        return;
      }
      if (r?.data?.ship_hold_stop?.due) {
        // ⏸ On hold: packed, but it is stored in the warehouse until Back Office removes the hold.
        toast.error(`Packed by ${mySignature} — ⏸ ON HOLD: do not ship, store it in the ${r.data.ship_hold_stop.warehouse_label}`, { duration: 8000 });
        setHoldStopOpen(true);
        return;
      }
      if (status === "inventory_packing") {
        toast.success(`Marked as ✅ READY TO BE SHIPPED by ${mySignature}`);
      } else {
        toast.success(`Packed by ${mySignature} — moved to Shipping`);
      }
      goBackToList();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });
  // 🏬 Pick-up order: finish the checklist (if needed), email the client,
  // move to Order Fulfilled as READY FOR PICK UP.
  const pickupReady = useMutation({
    mutationFn: async () => {
      if (!o?.inventory_packed_at) await api.post(`/orders/${id}/packing-finish`, { signature: mySignature });
      return api.post(`/orders/${id}/pickup-ready`);
    },
    onSuccess: (r) => {
      qc.invalidateQueries();
      toast.success(`🏬 Ready for pick up · ${r?.data?.emailed ? `email sent to ${r.data.emailed}` : "email sent"} → Order Fulfilled`);
      navigate("/orders/fulfilled");
    },
    onError: (e) => { qc.invalidateQueries(); toast.error(e?.response?.data?.detail || "Failed to mark ready for pick up"); },
  });
  // 🏬 Pick-up override (BO/SA, Inventory Packing only): flag as pick-up and
  // move straight to Order Fulfilled / READY FOR PICK UP — no email sent.
  // Strictly limited to the 4 orders the owner named (already contacted).
  const PICKUP_OVERRIDE_POS = new Set(["5061", "5010", "4823", "54976"]);
  const [pickupOverrideOpen, setPickupOverrideOpen] = useState(false);
  const pickupOverride = useMutation({
    mutationFn: () => api.post(`/orders/${id}/pickup-override`),
    onSuccess: () => {
      qc.invalidateQueries();
      setPickupOverrideOpen(false);
      toast.success("🏬 Marked as Pick Up → Order Fulfilled (no email sent)");
      navigate("/orders/fulfilled");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Pick-up override failed"),
  });
  // Packing Checklist camera → uploads directly to the order's Drive folder.
  // Shared hidden input + mutation reused by both the PackingWizard step and
  // the grid-view fallback (the small 📷 chip).
  const packingCameraRef = React.useRef(null);
  const drivePhotoUpload = useDriveUpload(id, { kind: "packing" });
  const openPackingCamera = () => packingCameraRef.current?.click();

  // Advance-order → per-factory stock availability (for the "Fulfil from Stock" panel)
  const isBackOfficeOrAdmin = user.role === "back_office" || user.role === "super_admin";
  const { data: stockAvail } = useQuery({
    queryKey: ["order-stock-avail", id, o?.status, o?.color, o?.catalog_number],
    queryFn: async () => (await api.get(`/orders/${id}/stock-availability`)).data,
    enabled: !!o && o.status === "advance",
    refetchOnWindowFocus: false,
  });

  // Advance order → send the part for pick-up from a chosen warehouse (+ factory). Stock is deducted when the station finds it.
  const [pickTarget, setPickTarget] = useState(null); // { warehouse, factory, color_override? }
  const [overrideSig, setOverrideSig] = useState("");
  const sendToPickup = useMutation({
    mutationFn: (force) => api.post(`/orders/${id}/send-to-pickup`, {
      warehouse: pickTarget.warehouse, factory: pickTarget.factory, force: !!force,
      color_override: pickTarget.color_override || undefined,
      employee_signature: pickTarget.color_override ? overrideSig.trim() : undefined,
      use_no_color: !!pickTarget.use_no_color,
    }),
    onSuccess: () => {
      qc.invalidateQueries();
      toast.success(`📦 ${o?.po_number} → ${WAREHOUSE_STATION_LABEL[pickTarget.warehouse]} — pick up ${pickTarget.color_override ? `${pickTarget.color_override} (different colour) ` : pickTarget.use_no_color ? "from No Color stock " : ""}from ${WAREHOUSE_LABEL[pickTarget.warehouse]} (${FACTORY_SHORT[pickTarget.factory]})`);
      setPickTarget(null);
      setOverrideSig("");
      goBackToList();
    },
    onError: (e) => {
      const detail = e?.response?.data?.detail || "Could not send for pick-up";
      if (e?.response?.status === 409) { if (window.confirm(`${detail}\n\nSend anyway?`)) sendToPickup.mutate(true); }
      else toast.error(detail);
    },
  });

  // Edit Codes + Recheck Codes (Back Office in Advance / Pending recheck)
  const [editCodesOpen, setEditCodesOpen] = useState(false);
  const [codesForm, setCodesForm] = useState({});
  const [codesPrefill, setCodesPrefill] = useState(null);
  const [codesReason, setCodesReason] = useState("");
  const [codesSignature, setCodesSignature] = useState("");
  const [recheckOpen, setRecheckOpen] = useState(false);
  const [recheckReason, setRecheckReason] = useState("");

  useEffect(() => {
    if (editCodesOpen && o) {
      setCodesForm({
        front_cover_code: o.front_cover_code || "",
        f_clamp_code: o.f_clamp_code || "",
        rear_cover_code: o.rear_cover_code || "",
        r_clamp_code: o.r_clamp_code || "",
        front_cover_code_f2: o.front_cover_code_f2 || "",
        f_clamp_code_f2: o.f_clamp_code_f2 || "",
        rear_cover_code_f2: o.rear_cover_code_f2 || "",
        r_clamp_code_f2: o.r_clamp_code_f2 || "",
        catalog_number: o.catalog_number || "",
        ...(codesPrefill || {}),
      });
      setCodesReason("");
      setCodesSignature(mySignature);
    }
    if (!editCodesOpen) setCodesPrefill(null);
    // Only re-seed when the dialog opens — a background refetch of `o` must
    // not wipe what the operator is typing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editCodesOpen]);

  const saveCodes = useMutation({
    mutationFn: async () => {
      const payload = { reason: codesReason.trim(), signature: codesSignature.trim() };
      // Only send fields that actually changed
      const codeKeys = [
        "front_cover_code", "f_clamp_code", "rear_cover_code", "r_clamp_code",
        "front_cover_code_f2", "f_clamp_code_f2", "rear_cover_code_f2", "r_clamp_code_f2", "catalog_number",
      ];
      for (const k of codeKeys) {
        if ((codesForm[k] || "") !== (o[k] || "")) payload[k] = codesForm[k] || (k === "catalog_number" ? "" : null);
      }
      return (await api.post(`/orders/${id}/edit-codes`, payload)).data;
    },
    onSuccess: (res) => {
      qc.invalidateQueries();
      const n = (res.changes || []).length;
      if (n === 0) toast.info("No changes to save");
      else toast.success(`${n} change(s) saved · Signed by ${codesSignature.trim()}${res.catalog_rematched ? ` · Catalog # re-matched → ${res.catalog_number}` : ""}`);
      setEditCodesOpen(false);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed to save codes"),
  });
  const codesDirty = ["front_cover_code", "f_clamp_code", "rear_cover_code", "r_clamp_code", "front_cover_code_f2", "f_clamp_code_f2", "rear_cover_code_f2", "r_clamp_code_f2", "catalog_number"]
    .some((k) => (codesForm[k] || "") !== (o?.[k] || ""));
  const codesCoverDirty = ["front_cover_code", "rear_cover_code"].some((k) => (codesForm[k] || "") !== (o?.[k] || ""));
  const codesCatalogDirty = (codesForm.catalog_number || "") !== (o?.catalog_number || "");
  const codesCanSave = codesDirty && codesReason.trim() && codesSignature.trim();

  const recheckCodes = useMutation({
    mutationFn: () => api.post(`/orders/${id}/recheck-codes`, {
      reason: recheckReason,
      signature: mySignature,
    }),
    onSuccess: (r) => {
      qc.invalidateQueries();
      const attempt = r?.data?.recheck_attempt || 1;
      toast.success(`Recheck Codes attempt #${attempt} — sent back to Pending Orders by ${mySignature}`);
      setRecheckOpen(false);
      setRecheckReason("");
      goBackToList();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed to recheck codes"),
  });

  // Brand list "NA" on a cover code → order moves to NA right away (rule Sep 29, 2026).
  const codeNa = useMutation({
    mutationFn: (side) => api.post(`/orders/${id}/code-na`, { side }),
    onSuccess: (_r, side) => {
      qc.invalidateQueries();
      toast.success(`${side === "front" ? "Front" : "Rear"} cover NA — ${o?.po_number || "order"} moved to NA Orders`);
      goBackToList();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed to mark NA"),
  });

  // 📷 Bad photos (Pending only) → back to Pre Orders with a translated note.
  const [badPhotosOpen, setBadPhotosOpen] = useState(false);
  const [badPhotosNote, setBadPhotosNote] = useState("");
  const badPhotos = useMutation({
    mutationFn: () => api.post(`/orders/${id}/bad-photos`, { note: badPhotosNote.trim() }),
    onSuccess: (r) => {
      qc.invalidateQueries();
      toast.success(`📷 ${o?.po_number || "Order"} sent back to Pre Orders — bad photos${r?.data?.translation ? " · translated" : ""}`);
      setBadPhotosOpen(false);
      setBadPhotosNote("");
      goBackToList();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed to send back"),
  });

  // ⚠️ After-sale claim (Order Fulfilled · BO/SA): Defective / Incompatible / Missing → note + photos.
  // handling: "factory" (Factory One / Super Admin decide) · "new_logo" / "resolved" (Back Office handled it herself).
  const [claimType, setClaimType] = useState(null);
  const [claimNote, setClaimNote] = useState("");
  const [claimFiles, setClaimFiles] = useState([]);
  const [claimVideos, setClaimVideos] = useState([]); // [{key, id?, name, size, progress, error}]
  const claimVideoBusy = claimVideos.some((v) => !v.id && !v.error);
  const addClaimVideos = (picked) => {
    const room = Math.max(0, 5 - claimVideos.length);
    picked.slice(0, room).forEach((file) => {
      const key = `${file.name}-${file.size}-${Date.now()}-${Math.random()}`;
      setClaimVideos((cur) => [...cur, { key, name: file.name, size: file.size, progress: 0 }]);
      uploadClaimVideo(id, file, (p) => setClaimVideos((cur) => cur.map((v) => (v.key === key ? { ...v, progress: p } : v))))
        .then((r) => setClaimVideos((cur) => cur.map((v) => (v.key === key ? { ...v, id: r.id, progress: 100 } : v))))
        .catch((e) => setClaimVideos((cur) => cur.map((v) => (v.key === key ? { ...v, error: e?.response?.data?.detail || e.message || "Upload failed" } : v))));
    });
  };
  const removeClaimVideo = (v) => {
    setClaimVideos((cur) => cur.filter((x) => x.key !== v.key));
    if (v.id) api.delete(`/claim-media/${v.id}`).catch(() => {});
  };
  const [claimHandling, setClaimHandling] = useState("factory");
  const [claimSolution, setClaimSolution] = useState("");
  // Claims → New claim → arrives here with ?claim=defective|incompatible|missing → open the usual claim form at once.
  React.useEffect(() => {
    const want = new URLSearchParams(location.search).get("claim");
    if (!want || !o || claimType) return;
    if (["defective", "incompatible", "missing"].includes(want) && o.status === "fulfilled" && !o.claim_open
        && (user.role === "back_office" || user.role === "super_admin")) {
      setClaimType(want); setClaimNote(""); setClaimFiles([]); setClaimHandling("factory"); setClaimSolution("");
      navigate(location.pathname, { replace: true, state: location.state });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [o?.id, o?.status, location.search]);
  const fileClaim = useMutation({
    mutationFn: async () => {
      const fd = new FormData();
      fd.append("claim_type", claimType);
      fd.append("note", claimNote.trim());
      fd.append("handling", claimHandling);
      fd.append("solution", claimSolution.trim());
      fd.append("media_ids", claimVideos.filter((v) => v.id).map((v) => v.id).join(","));
      for (const f of claimFiles) fd.append("files", await compressImage(f));
      return api.post(`/orders/${id}/claims`, fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
    },
    onSuccess: () => {
      qc.invalidateQueries();
      toast.success(claimHandling === "factory"
        ? t("Claim filed — Factory One & Super Admin notified (AFTER-SALE QC)")
        : claimHandling === "new_logo" ? t("Claim filed as New logo — mailed by Back Office") : t("Claim filed as Resolved by Back Office"));
      setClaimType(null); setClaimNote(""); setClaimFiles([]); setClaimVideos([]); setClaimHandling("factory"); setClaimSolution("");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed to file the claim"),
  });

  // Inventory lookup for spacer size and other catalog details on the packing checklist
  const { data: inventoryList = [] } = useQuery({
    queryKey: ["inventory-list"],
    queryFn: async () => (await api.get("/inventory")).data,
    enabled: !!o && (o.status === "packing" || o.status === "inventory_packing"),
  });
  const invForOrder = inventoryList.find((i) => i.cat_number && i.cat_number === o?.catalog_number);

  // Inventory match (for back_office on advance/flagged orders)
  const { data: invMatch } = useQuery({
    queryKey: ["order-inv-match", id, o?.status, o?.front_cover_code, o?.rear_cover_code, o?.color],
    queryFn: async () => {
      const d = (await api.get(`/orders/${id}/inventory-match`)).data;
      if (d?.auto_assigned) { qc.invalidateQueries({ queryKey: ["order", id] }); qc.invalidateQueries({ queryKey: ["inventory"] }); setTimeout(() => qc.invalidateQueries({ queryKey: ["order-inv-match", id] }), 800); }
      return d;
    },
    enabled: !!o && (user.role === "back_office" || user.role === "super_admin") && ["advance", "flagged"].includes(o.status),
  });

  if (isLoading || !o) return <div className="text-slate-500">Loading…</div>;

  const isBackOffice = user.role === "back_office" || user.role === "super_admin";
  const isFactory = user.role === "factory_1" || user.role === "factory_2" || user.role === "factory_3";
  const isPacking = user.role === "packing_orders";
  const isShipping = user.role === "shipping_order";

  // Lock rules:
  // - Order info (brand/model/year/trim/vin/rim/color): editable only in pre_order, pending
  //   (BO/SA get an "Edit" affordance up to Advance for codes; see below).
  // - Codes (front/f-clamp/rear/r-clamp): Factory 1 can edit in Pending.
  //   Back Office / Super Admin can edit at ANY stage before Production
  //   (pre_order, pending, advance) via the same Order Info edit button.
  // - Catalog # is auto-filled from inventory on code save; BO/SA may still
  //   override it manually while the order is pre-Production.
  // Once status >= production, codes + catalog # are frozen.
  const orderInfoLocked = !["pre_order", "pending"].includes(o.status);
  const PRE_PRODUCTION = ["pre_order", "pending", "advance"];
  const codesLocked = isBackOffice
    ? !PRE_PRODUCTION.includes(o.status)
    : (o.status !== "pending" || !isFactory);
  // Catalog #: BO/SA may change it at ANY stage (rule Sep 28, 2026 — "change
  // codes and the catalog number accordingly"). Pre-Production saves inline;
  // past Advance it goes through the same signed dialog as the codes.
  const catalogLocked = !isBackOffice;
  // Vehicle-info fields the user wants editable by BO/SA at ANY stage
  // (brand, model, year, trim, VIN, rim size, color, logo, spacer, spacer qty).
  // Factory users still respect `orderInfoLocked` — they can only edit these
  // when the order is pre-Production.
  const EDITABLE_INFO_FIELDS = new Set([
    "brand", "model", "year", "trim", "vin",
    "rim_size", "color", "logo", "spacer", "spacer_qty",
  ]);
  // Codes (front / rear cover + clamps): Back Office / Super Admin may change
  // them at ANY stage (rule Sep 24, 2026). Pre-Production edits save straight
  // away; once the order is past Advance the Save intercepts code changes and
  // routes them through the signed dialog (note + signature mandatory).
  const CODE_FIELDS = new Set(["front_cover_code", "f_clamp_code", "rear_cover_code", "r_clamp_code"]);
  const lockedFor = (k) => {
    if (CODE_FIELDS.has(k) && isBackOffice) return false;
    if (CODE_FIELDS.has(k)) return codesLocked;
    if (k === "catalog_number") return catalogLocked;
    if (EDITABLE_INFO_FIELDS.has(k) && isBackOffice) return false;
    return orderInfoLocked;
  };
  const saveOrderInfo = () => {
    const SIGNED_FIELDS = [...CODE_FIELDS, "catalog_number"];
    const changedCodes = SIGNED_FIELDS.filter((k) => (form[k] || "") !== (o[k] || ""));
    if (isBackOffice && changedCodes.length && !PRE_PRODUCTION.includes(o.status)) {
      const rest = { ...form };
      SIGNED_FIELDS.forEach((k) => delete rest[k]);
      update.mutate(rest);
      setCodesPrefill(Object.fromEntries(SIGNED_FIELDS.map((k) => [k, form[k] || ""])));
      setEditCodesOpen(true);
      setEditMode(false);
      toast.info("Code / Catalog # changes after Advance need a note + signature — confirm in the dialog");
      return;
    }
    update.mutate(form);
    setEditMode(false);
  };
  // Inline code auto-save (blur / dropdown change). Past Advance, BO/SA
  // changes are NOT saved here — they wait for "Save" → signed dialog.
  const codeAutoSave = (k, v) => {
    if (lockedFor(k)) return;
    if (isBackOffice && !PRE_PRODUCTION.includes(o.status)) return;
    update.mutate({ [k]: v });
  };

  // Header: marks row above Station + Status, one email button, equal-size tiles.
  const isLeadOrder = !!o.is_lead || o.status === "lead";
  const canToggleMarks = user.role === "back_office" || user.role === "super_admin";
  // One tile size for every header button (marks + actions): 1.6rem tall, ≥6.8rem wide, 0.6rem text (user: "20% smaller").
  const TILE = "[&_button]:h-[1.6rem] [&_button]:min-w-[6.8rem] [&_button]:justify-center [&_button]:px-1.5 [&_button]:text-[0.6rem] [&_button]:whitespace-nowrap";
  // Left-side marks hug their text (same height / text size, no minimum width).
  const TILE_LEFT = "[&_button]:h-[1.6rem] [&_button]:w-auto [&_button]:min-w-0 [&_button]:justify-center [&_button]:px-2.5 [&_button]:text-[0.6rem] [&_button]:whitespace-nowrap";
  const MARK_BTN = "inline-flex items-center justify-center gap-1.5 h-[1.6rem] px-1.5 rounded-md border text-[0.6rem] font-medium whitespace-nowrap transition-colors";
  // Every status chip shares the tile height — station, stage, waiting, type, PDF, tracking…
  const CHIP = "inline-flex items-center h-[1.6rem] px-2.5 rounded-md uppercase tracking-widest font-bold text-[0.6rem]";
  const BADGE_V2 = "h-[1.6rem] px-2.5 uppercase tracking-widest font-bold text-[0.6rem]";
  // Action bar: 3 equal columns; column width = widest label (never clips), min 8.5rem so it matches the marks tiles.
  // Nested row wrappers (NA / Advance / Flagged) are flattened with `contents` so every button is a grid cell.
  const ACTION_ROW = `grid grid-cols-3 gap-2 w-max shrink-0 [&>div]:contents [&>div>div]:contents [&>span]:col-span-3 [&>span]:text-right [&_button]:w-full ${TILE}`;

  const backBtn = (() => {
    // Return to the stage the user came from. Priority:
    //   1) React Router `location.state.from` (set by <Link state={{from:stage}}> in OrdersList)
    //   2) Derived from the order's current status
    //   3) "pending" as a last resort
    const fromState = location.state?.from;
    const backStage = fromState || STATUS_TO_STAGE[o.status] || "pending";
    const goBack = () => {
      if (fromState === "report") { navigate("/report"); return; }
      if (fromState === "rush") { navigate("/rush"); return; }
      // Prefer browser back when we have a real referrer so scroll position is restored
      if (fromState && window.history.length > 1) {
        navigate(-1);
      } else {
        navigate(`/orders/${backStage}`);
      }
    };
    return (
      <button
        type="button"
        onClick={goBack}
        data-testid="back-to-list"
        className="text-xs text-slate-500 hover:text-slate-900 cursor-pointer bg-transparent border-none p-0"
      >
        ← {t("back to list")}
      </button>
    );
  })();

  const pickupToggleBtn = canToggleMarks && (
    <button
      type="button"
      data-testid="pickup-toggle-btn"
      disabled={pickupToggle.isPending}
      onClick={() => pickupToggle.mutate(!o.is_pickup)}
      aria-pressed={!!o.is_pickup}
      title={o.is_pickup ? "Pick-up order — click to switch back to a shipping order" : "Mark this order as a pick-up order (client collects at the shop)"}
      className={`${MARK_BTN} ${o.is_pickup ? "border-violet-700 bg-violet-600 text-white hover:bg-violet-700 shadow-[0_0_10px_rgba(139,92,246,0.45)]" : "border-violet-300 bg-white text-violet-800 hover:bg-violet-50"}`}
    >
      🏬 {t("Pick up")}
    </button>
  );

  const pickupOverrideBtn = canToggleMarks && o.status === "inventory_packing" && PICKUP_OVERRIDE_POS.has(String(o.po_number || "").replace(/^PO-/i, "").replace(/^#/, "")) && (
    <Dialog open={pickupOverrideOpen} onOpenChange={setPickupOverrideOpen}>
      <DialogTrigger asChild>
        <button
          type="button"
          data-testid="pickup-override-btn"
          title="Override: mark this order as Pick Up and move it to Order Fulfilled (READY FOR PICK UP) — no email is sent"
          className={`${MARK_BTN} border-violet-700 bg-violet-600 text-white hover:bg-violet-700`}
        >
          🏬 {t("Pick up → Fulfilled")}
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-md" data-testid="pickup-override-dialog">
        <DialogHeader>
          <DialogTitle>🏬 {t("Pick up instead of shipping")}</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-slate-700">
          <span className="font-mono font-semibold">{o.po_number}</span> {t("will be flagged as a Pick Up order and moved to Order Fulfilled as READY FOR PICK UP. No shipping label is needed and no email will be sent to the client.")}
        </p>
        <div className="flex justify-end gap-2 mt-2">
          <Button variant="outline" size="sm" onClick={() => setPickupOverrideOpen(false)} data-testid="pickup-override-cancel">{t("Cancel")}</Button>
          <Button size="sm" className="bg-violet-600 hover:bg-violet-700 text-white" disabled={pickupOverride.isPending} onClick={() => pickupOverride.mutate()} data-testid="pickup-override-confirm">
            {pickupOverride.isPending ? t("Saving…") : t("Confirm pick up")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );

  const usFlagShown = isUsOrder(o);
  const usFlagToggleBtn = canToggleMarks && (
    <button
      type="button"
      data-testid="us-flag-toggle-btn"
      disabled={usFlagToggle.isPending}
      onClick={() => usFlagToggle.mutate(!usFlagShown)}
      aria-pressed={usFlagShown}
      title={usFlagShown ? "Hide the USA flag on this order for all users" : "Show the USA flag on this order for all users"}
      className={`${MARK_BTN} ${usFlagShown ? "border-blue-700 bg-blue-600 text-white hover:bg-blue-700" : "border-blue-300 bg-white text-blue-800 hover:bg-blue-50"}`}
    >
      {usFlagShown ? t("US On") : t("US Off")}
    </button>
  );

  const rushBadge = (!!o.rush_highlight || !!o.rush_top || !!o.rush_urgent || Number(o.rush_level || 0) > 0) && (
    <Badge
      className={`bg-red-100 text-red-800 border border-red-300 font-semibold uppercase tracking-widest ${BADGE_V2}`}
      data-testid="detail-rush-badge"
    >
      🚨 RUSH {[o.rush_urgent && "U", o.rush_top && "T", o.rush_highlight && "H"].filter(Boolean).join("+") || `L${o.rush_level}`}
    </Badge>
  );

  // Shipping leg with a box tracking number → ONE frame: 📍 Shipping Orders · 🚚 In transit to warehouse · tracking (user rule Oct 13, 2026).
  const shipFrame = o.status === "shipping" && !!o.shipping_tracking_number && !o.received_at && !o.on_hold;
  const stationBadge = shipFrame ? null : o.status === "fulfilled" && o.fulfilled_method === "pickup_ready" ? (
    <Badge className={`bg-violet-600 text-white border border-violet-700 font-semibold ${BADGE_V2}`} data-testid="detail-pickup-status">🏬 {t("READY FOR PICK UP")}</Badge>
  ) : (o.status !== "lead" && !o.is_lead) && (() => {
    const packed = packedStageOverride(o);
    // Station stays on the small badge; the big pill shows the live status.
    return (
      <Badge className={`${BADGE_CLS[STAGE_BADGE[o.status]]} ${BADGE_V2}`} data-testid="detail-status-badge" title={t("Station")}>
        📍 {t(STAGE_LABEL[o.status])}
      </Badge>
    );
  })();

  // BIG stage pill + optional smaller "type/source" chip on ONE line.
  // Business rule Feb 28, 2026: every tab shows the current stage
  // prominently on the order header, with a small chip beside it
  // describing HOW the order got here (Manual vs Upload for
  // production, LEAD origin for orders that came from Drive, etc.).
  const stageBlock = (() => {
    const isNoMold = Array.isArray(o.cancel_reason_ids) && o.cancel_reason_ids.includes("reason-no-mold");
    const stageKey = o.on_hold ? "on_hold" : (o.qc_hold && o.status === "production") ? "quality_control" : isNoMold ? "no_mold" : (o.status || "pre_order");
    // Production: mirror the list's sub-status pill exactly (same label + colour).
    const prodSub = stageKey === "production" ? productionSubstage(o) : null;
    const packed = !o.on_hold ? packedStageOverride(o) : null;
    const stageLabel = packed ? `${packed.icon} ${t(packed.label)}` : prodSub ? t(PRODUCTION_SUBSTAGE[prodSub]) : t(STAGE_HEADER[stageKey] || (o.status || "—").replace(/_/g, " ").toUpperCase());
    // ⏳ Waiting for customer reply → small violet chip beside the stage.
    const waiting = !!o.waiting_reply && !["cancelled", "production", "product_transfer", "packing", "inventory_packing", "shipping", "completed", "fulfilled"].includes(o.status);
    const waitingTitle = `${stageLabel} · ${o.waiting_reply_note || "waiting for customer reply"}${o.waiting_reply_at ? " · since " + o.waiting_reply_at.slice(0, 16).replace("T", " ") : ""}`;
    // 📷 Bad photos → rose pill while the order sits in Pre Orders again.
    const badPhotosMark = !waiting && !!o.bad_photos && o.status === "pre_order";
    const label = badPhotosMark ? `📷 ${t("BAD PHOTOS")}` : stageLabel;
    const pill = badPhotosMark
        ? "border-2 border-rose-500 bg-rose-50 text-rose-900 shadow-[0_0_12px_rgba(244,63,94,0.4)]"
        : prodSub ? PRODUCTION_SUBSTAGE_PILL[prodSub] : packed ? packed.pill : (STAGE_PILL[stageKey] || "border-2 border-slate-500 bg-slate-50 text-slate-800");
    // Type/source chip — only shown where meaningful.
    let chip = null;
    if (o.status === "production" && o.manual_production) {
      chip = { text: "🖐 Manual", cls: "border border-amber-400 bg-amber-50 text-amber-800",
               title: `Manual production by ${o.manual_production_by || "—"} at ${o.manual_production_at || "—"}` };
    } else if (o.status === "production" && o.batch_id) {
      chip = { text: "📦 Upload", cls: "border border-sky-400 bg-sky-50 text-sky-800",
               title: `Batch upload · batch ${o.batch_id}` };
    } else if (o.status === "lead" || o.is_lead) {
      // Lead: one chip only — the big pill already says LEAD.
      chip = { text: `💳 ${t("awaiting payment")}`, cls: "border border-teal-400 bg-teal-50 text-teal-800",
               title: "Lead — waiting for the Shopify payment before it becomes a Pre Order" };
    } else if (o.originally_lead) {
      // Post-lead orders carry the LEAD origin so BO knows this
      // order started life as a Drive-ingested lead.
      chip = { text: "🌱 Lead origin", cls: "border border-teal-400 bg-teal-50 text-teal-800",
               title: "Originally created from a Drive-ingested lead" };
    }
    return (
      <span className="inline-flex items-center gap-2 leading-none flex-wrap" data-testid="detail-stage-block">
        {!isLeadOrder && (
          <span
            className={`${CHIP} ${pill} ${shipFrame ? "gap-2" : ""}`}
            data-testid="detail-stage-main"
            title={badPhotosMark ? `${stageLabel} · ${o.bad_photos_note || ""}${o.bad_photos_note_translation ? " / " + o.bad_photos_note_translation : ""}${o.bad_photos_by ? " · " + o.bad_photos_by : ""}` : undefined}
          >
            {shipFrame ? (
              <>
                <span data-testid="detail-ship-frame-station">📍 {t(STAGE_LABEL.shipping)}</span>
                <span className="opacity-50">·</span>
                <span>{label}</span>
                <span className="opacity-50">·</span>
                <TrackingLink number={o.shipping_tracking_number} carrier={o.shipping_carrier} testid="detail-tracking-link" className="font-mono normal-case tracking-normal">
                  📦 {o.shipping_tracking_number}
                </TrackingLink>
              </>
            ) : label}
          </span>
        )}
        {waiting && (
          <span
            className={`${CHIP} border border-violet-500 bg-violet-50 text-violet-900 shadow-[0_0_10px_rgba(139,92,246,0.5)]`}
            data-testid="detail-waiting-chip"
            title={waitingTitle}
          >
            ⏳ {t("WAITING FOR REPLY")}
          </span>
        )}
        {chip && (
          <span
            className={`${CHIP} ${chip.cls}`}
            data-testid="detail-stage-type"
            title={chip.title}
          >
            {chip.text}
          </span>
        )}
        {o.defect_count > 0 && (
          <span
            className={`${CHIP} normal-case tracking-normal border border-rose-400 bg-rose-50 text-rose-800`}
            data-testid="detail-defect-chip"
            title={(o.defect_history || []).map((d) => `D${d.n} · ${d.note}`).join("\n")}
          >
            ⚠ D{o.defect_count} · {o.defect_note}
          </span>
        )}
        {(() => { const p = pickupInfo(o); return p && ["packing", "inventory_packing", "pickup_3pl", "shipping", "completed", "fulfilled"].includes(o.status) ? (
          <span className={`${CHIP} border ${p.confirmed ? "border-amber-400 bg-amber-50 text-amber-900" : "border-amber-700 bg-amber-700 text-white shadow-[0_0_10px_rgba(180,83,9,0.45)]"}`} data-testid="detail-pickup-chip"
                title={p.confirmed ? `Found in ${WAREHOUSE_LABEL[p.warehouse]} warehouse by ${p.confirmed_by || "—"}` : `Sent to ${WAREHOUSE_STATION_LABEL[p.warehouse]} by ${p.sent_by || "—"} — pick up CAT ${o.catalog_number || "—"} · ${o.color || "—"} (${FACTORY_SHORT[p.factory] || "F1"})`}>
            📦 {WAREHOUSE_FLAG[p.warehouse]} {t(`${WAREHOUSE_LABEL[p.warehouse]} WAREHOUSE`.toUpperCase())} · {FACTORY_SHORT[p.factory] || "F1"}{p.confirmed ? "" : ` · ${t("TO PICK UP")}`}
          </span>
        ) : null; })()}
        {o.production_needed?.at && o.status === "advance" && (
          <span className={`${CHIP} border border-red-700 bg-red-600 text-white shadow-[0_0_10px_rgba(220,38,38,0.45)]`} data-testid="detail-production-needed-chip"
                title={`Not found in ${WAREHOUSE_LABEL[o.production_needed.warehouse] || "—"} warehouse by ${o.production_needed.by || "—"}${o.production_needed.note ? ` · ${o.production_needed.note}` : ""}`}>
            🏭 {t("NO INVENTORY")} · {t("PRODUCTION NEEDED")}
          </span>
        )}
        {o.is_reorder && (
          <span className={`${CHIP} border border-slate-600 bg-slate-100 text-slate-800`} data-testid="detail-reorder-chip">
            🔁 {t("Re-order of")} {o.reorder_of_po}
          </span>
        )}
        {o.reorder_flag && !o.is_reorder && (
          <span className={`${CHIP} border border-red-300 bg-red-100 text-red-900`} data-testid="detail-reorder-flag-chip"
                title={`${t("Marked as Reorder by")} ${o.reorder_flag_set_by || "—"}`}>
            🔁 {t("REORDER")}
          </span>
        )}
        {o.split_total > 1 && (
          <span className={`${CHIP} border border-indigo-400 bg-indigo-50 text-indigo-900`} data-testid="detail-split-chip"
                title={`Shopify order ${o.shopify_order_number || ""} contains ${o.split_total} caliper-cover sets — one CRM order per set`}>
            🔀 {t("Set")} {o.split_index} / {o.split_total}
            {(o.split_siblings || []).map((s) => <React.Fragment key={s.id}> · <Link to={`/order/${s.id}`} className="underline" data-testid={`split-sibling-${s.po_number}`}>{s.po_number}</Link></React.Fragment>)}
          </span>
        )}
        {o.is_warranty && (
          <Link to={o.replacement_of_id ? `/order/${o.replacement_of_id}` : "/claims"} className={`${CHIP} border border-emerald-600 bg-emerald-50 text-emerald-900 hover:bg-emerald-100`} data-testid="detail-warranty-chip"
                title={`Warranty replacement of ${o.replacement_of_po || "—"} · ${o.replacement_scope || "full"}`}>
            🛠 {t("Warranty")} · {o.replacement_of_po}{o.replacement_scope && o.replacement_scope !== "full" ? ` · ${t(o.replacement_scope === "clamps" ? CLAMPS_SIDE_LABEL[clampsSide(o)] : o.replacement_scope === "front" ? "Front cover only" : "Rear cover only")}` : ""}
          </Link>
        )}
        {clampsOnly(o) && isBackOffice && <ClampsSideSelect order={o} />}
        {o.ships_inside?.order_id && (
          <Link to={`/order/${o.ships_inside.order_id}`} className={`${CHIP} border ${o.ships_inside.separated ? "border-emerald-600 bg-emerald-50 text-emerald-900" : "border-sky-600 bg-sky-600 text-white hover:bg-sky-700"}`} data-testid="detail-inside-chip"
                title={o.ships_inside.separated ? `Taken out of ${o.ships_inside.po_number}'s box by ${o.ships_inside.separated_by}` : `${clampsLabel(o)} travel inside the box of ${o.ships_inside.po_number}`}>
            📦 {o.ships_inside.separated ? t("SEPARATED FROM") : t("INSIDE")} {o.ships_inside.po_number}
          </Link>
        )}
        {(o.carries || []).some((c) => !c.removed) && (
          <span className={`${CHIP} border border-amber-500 bg-amber-500 text-white`} data-testid="detail-carries-chip">
            ➕ {t("CARRIES")} {(o.carries || []).filter((c) => !c.removed).map((c) => c.po_number).join(", ")}
          </span>
        )}
        {o.shipping_tracking_number && !shipFrame && (
          <TrackingLink number={o.shipping_tracking_number} carrier={o.shipping_carrier} testid="detail-tracking-link"
                        className="inline-flex items-center h-[1.6rem] px-2.5 rounded-md font-mono font-bold text-[0.6rem] border border-slate-400 bg-white text-slate-800 no-underline hover:bg-slate-50">
            📦 {t("Track")} · {o.shipping_tracking_number}
          </TrackingLink>
        )}
        {(o.is_lead || o.status === "lead" || o.originally_lead) && o.drive_folder_id && (
          <LeadPdfChip order={o} canScan={isBackOffice} className="h-[1.6rem] px-2.5 text-[0.6rem]" />
        )}
        {o.last_claim_status && (
          <Link to={o.last_claim_id ? `/claims?claim=${o.last_claim_id}` : "/claims"} data-testid="detail-claim-chip"
                title={o.claim_open ? "Open after-sale claim — waiting for Factory One / Super Admin" : `Claim ${o.last_claim_status}${o.replacement_po ? " → " + o.replacement_po : ""}`}
                className={`${CHIP} border ${o.claim_open ? "border-rose-600 bg-rose-600 text-white shadow-[0_0_10px_rgba(225,29,72,0.45)]" : o.last_claim_status === "declined" ? "border-slate-500 bg-slate-100 text-slate-700" : "border-emerald-600 bg-emerald-50 text-emerald-900"}`}>
            ⚠️ {t("Claim")} · {t(({ defective: "Defective", incompatible: "Incompatible", missing: "Missing" })[o.last_claim_type] || o.last_claim_type || "")}
            {o.claim_open ? ` · ${t("OPEN")}` : ` · ${t(({ warranty: "Warranty", reorder: "Re-order", refund: "Refund", new_logo: "New logo", declined: "Declined / Resolved", resolved: "Resolved" })[o.last_claim_status] || o.last_claim_status)}${o.replacement_po ? ` → ${o.replacement_po}` : ""}`}
          </Link>
        )}
        {o.reordered && (
          <span className={`${CHIP} border border-slate-600 bg-slate-100 text-slate-800`} data-testid="detail-reordered-chip">
            🔁 {t("Re-ordered")} → {o.reorder_po}
          </span>
        )}
      </span>
    );
  })();

  const notReadyBadge = o.status === "pre_order" && (() => {
    // Mirror the backend's pre_order → pending gate: show a specific
    // list of what's still missing so Back Office knows why the
    // Approve button is blocked (or will fail).
    const required = ["vin", "brand", "model", "year", "color"];
    const missingFields = required.filter((f) => !(o[f] || "").toString().trim());
    const hasPhotos = !!(o.photos_received || o.drive_folder_id);
    if (missingFields.length === 0 && hasPhotos) return null;
    const parts = [];
    if (missingFields.length) parts.push(missingFields.map((f) => f.replace("_", " ")).join(", "));
    if (!hasPhotos) parts.push("photos");
    return (
      <Badge
        className={`bg-red-50 text-red-700 border border-red-200 ${BADGE_V2}`}
        data-testid="detail-not-ready"
        title={`Missing: ${parts.join(" · ")}`}
      >
        NOT READY · missing {parts.join(" · ")}
      </Badge>
    );
  })();

  const cancelledBadge = o.cancelled && (
    <Badge className={`bg-red-100 text-red-800 border border-red-300 ${BADGE_V2}`} data-testid="detail-refunded" title={o.cancel_reason || "Order was cancelled"}>
      ✕ REFUNDED · cancelled
    </Badge>
  );
  return (
    <div className="space-y-5">
      {/* After-sale claim — light-red strip with defect note + solution (original + replacement order) */}
      <ClaimBanner order={o} user={user} />
      <MatchSuggestionBanner order={o} user={user} />
      <ShopifyRefundStrip order={o} user={user} />
      {/* Converted-Lead banner: this Lead was already promoted into a real
          PO — surface the target order with a jump link so the operator
          never lands on a stale Lead. (Mar 2026 rule.) */}
      {o.converted_to_po && (
        <div
          className="rounded-lg border-2 border-teal-500 bg-teal-50 text-teal-900 px-4 py-3 flex items-center justify-between gap-3"
          data-testid="converted-lead-banner"
        >
          <div className="text-sm font-medium">
            🌱 <span className="font-bold">{o.po_number}</span> was converted into Order{" "}
            <span className="font-bold">{o.converted_to_po}</span>
            {o.converted_at && (
              <span className="text-xs text-teal-800 ml-2">
                · {new Date(o.converted_at).toLocaleString()}
              </span>
            )}
            {o.converted_by && (
              <span className="text-xs text-teal-800 ml-1">by {o.converted_by}</span>
            )}
          </div>
          {o.converted_to_id && (
            <Link
              to={`/orders/${o.converted_to_id}`}
              className="text-sm font-semibold underline hover:text-teal-700"
              data-testid="converted-lead-open-po"
            >
              Open {o.converted_to_po} →
            </Link>
          )}
        </div>
      )}
      {/* 🛑 Cancelled, set stays in a warehouse — order keeps its station until the STOP is confirmed (rule Oct 27, 2026) */}
      {o.cancel_pending && (
        <div className="rounded-lg border-2 border-red-700 bg-red-50 text-red-900 px-4 py-3 flex flex-wrap items-center justify-between gap-3" data-testid="cancel-pending-panel">
          <div className="min-w-0 text-sm">
            <div className="font-black uppercase tracking-widest text-xs">
              🛑 {t("Cancelled")} → {o.cancel_stop?.warehouse ? `${o.cancel_stop.warehouse_label}` : t("USA / CANADA stock on arrival")}
              <span className="ml-2 normal-case tracking-normal font-semibold">
                · {o.cancel_stop?.due ? t("STOP due now — do not ship, confirm storage") : `${t("STOP appears")} ${t(CANCEL_STOCK_WHEN[o.status] || "later")}`}
              </span>
            </div>
            <div className="mt-1 text-xs">
              {t("Catalog #")} <b className="font-mono">{o.catalog_number || "— missing —"}</b> · {t("colour")} <b>{o.color || "—"}</b>
              {o.cancel_stop?.missing_catalog && <span className="ml-2 font-semibold text-amber-800">⚠ {t("Back Office must add the Catalog # before the set can be stored")}</span>}
              {o.cancel_requested_by && <span className="ml-2 text-red-700">· {t("cancelled by")} {o.cancel_requested_by}{o.cancel_requested_at ? ` · ${new Date(o.cancel_requested_at).toLocaleString()}` : ""}</span>}
              {o.cancel_stop?.station_label && <span className="ml-2 text-red-700" data-testid="cancel-stop-station">· {t("confirmed by")} <b>{t(o.cancel_stop.station_label)}</b></span>}
            </div>
          </div>
          {canConfirmCancelStop(user, o.cancel_stop?.warehouse) && (
            <Button className="bg-red-600 hover:bg-red-700 text-white font-black" onClick={() => setCancelStopOpen(true)} data-testid="cancel-stop-open-detail">
              🛑 {o.cancel_stop?.due ? t("STOP — confirm storage") : t("View STOP")}
            </Button>
          )}
        </div>
      )}
      <CancelStockStopDialog orderId={o.id} open={cancelStopOpen} onOpenChange={setCancelStopOpen} onDone={() => goBackToList()} />
      {/* ⏸ ON HOLD (rule Jun 2026) — the set is stored in a warehouse at its stop point until Back Office removes the hold */}
      {o.ship_hold && (
        <div className={`rounded-lg border-2 px-4 py-3 flex flex-wrap items-center justify-between gap-3 ${o.ship_hold_stop?.due ? "border-red-700 bg-red-50 text-red-900" : "border-amber-500 bg-amber-50 text-amber-950"}`} data-testid="ship-hold-panel">
          <div className="min-w-0 text-sm">
            <div className="font-black uppercase tracking-widest text-xs">
              ⏸ {t("ON HOLD")} → {o.ship_hold_stop?.warehouse ? t(o.ship_hold_stop.warehouse_label) : t("USA / CANADA warehouse on arrival")}
              <span className="ml-2 normal-case tracking-normal font-semibold" data-testid="ship-hold-panel-state">
                · {o.ship_hold_stop?.stored
                  ? `📦 ${t("stored")} · ${o.ship_hold_stop.stored.by}${o.ship_hold_stop.stored.at ? ` · ${new Date(o.ship_hold_stop.stored.at).toLocaleString()}` : ""}`
                  : o.ship_hold_stop?.due ? t("STOP due now — do not ship, store the set") : `${t("STOP")} ${t(o.ship_hold_stop?.when || "later")}`}
              </span>
            </div>
            <div className="mt-1 text-xs">
              {t("Reason")}: <b>{o.ship_hold_stop?.reason || "—"}</b> · {t("by")} {o.ship_hold_stop?.by || "—"}{o.ship_hold_stop?.at ? ` · ${new Date(o.ship_hold_stop.at).toLocaleString()}` : ""}
              {" · "}{t("Catalog #")} <b className="font-mono">{o.catalog_number || "— missing —"}</b> · {t("colour")} <b>{o.color || "—"}</b>
              {o.ship_hold_stop?.station_label && <span className="ml-2" data-testid="ship-hold-station">· {o.ship_hold_stop?.stored ? t("stored by") : t("to be stored by")} <b>{t(o.ship_hold_stop.station_label)}</b></span>}
            </div>
          </div>
          {canAckHoldStop(user, o.ship_hold_stop?.warehouse) && (
            <Button className={`${o.ship_hold_stop?.due && !o.ship_hold_stop?.stored ? "bg-red-600 hover:bg-red-700" : "bg-amber-600 hover:bg-amber-700"} text-white font-black`} onClick={() => setHoldStopOpen(true)} data-testid="hold-stop-open-detail">
              🛑 {o.ship_hold_stop?.due && !o.ship_hold_stop?.stored ? t("STOP — store the set") : t("View STOP")}
            </Button>
          )}
        </div>
      )}
      <HoldStopDialog orderId={o.id} open={holdStopOpen} onOpenChange={setHoldStopOpen} />
      {/* 📦 STOCK UNIT (batch order, Jun 2026) — a set made for stock: destination warehouse, STOCK-IN at arrival, then assignable from the Pick-up dialog */}
      {o.is_stock_unit && <StockUnitPanel o={o} user={user} />}
      {!o.ship_hold && o.ship_hold_released?.stored && (
        <div className="rounded-lg border border-amber-400 bg-amber-50 text-amber-950 px-4 py-2 text-xs" data-testid="ship-hold-released-note">
          ▶ {t("Hold removed")} {t("by")} {o.ship_hold_released.by}{o.ship_hold_released.at ? ` · ${new Date(o.ship_hold_released.at).toLocaleString()}` : ""} — {o.ship_hold_released.reason} · 📦 {t("pull the box from the")} {t(({ china: "CHINA WAREHOUSE", usa: "AMERICA WAREHOUSE", canada: "CANADIAN WAREHOUSE" })[o.ship_hold_released.stored.warehouse] || "WAREHOUSE")} {t("shelf and continue")}
        </div>
      )}
      {/* Re-order links + warehouse storage banner */}
      {(o.reordered || o.is_reorder || o.transferred_to_inventory) && (
        <div className="rounded-lg border-2 border-slate-700 bg-slate-50 text-slate-900 px-4 py-3 flex flex-wrap items-center justify-between gap-3" data-testid="reorder-panel">
          <div className="min-w-0 text-sm">
            {o.reordered && o.reorder_order_id && (
              <div className="font-black uppercase tracking-widest text-xs">
                🔁 {t("Re-ordered as")}{" "}
                <Link to={`/order/${o.reorder_order_id}`} className="underline" data-testid="reorder-open-new">{o.reorder_po} →</Link>
              </div>
            )}
            {o.is_reorder && o.reorder_of_id && (
              <div className="font-black uppercase tracking-widest text-xs">
                🔁 {t("Re-order of")}{" "}
                <Link to={`/order/${o.reorder_of_id}`} className="underline" data-testid="reorder-open-original">{o.reorder_of_po} →</Link>
                {o.reorder_reason && <span className="ml-2 normal-case font-semibold tracking-normal">· {o.reorder_reason}{o.reorder_reason_label ? ` · ${t(o.reorder_reason_label)}` : o.reorder_received_color ? ` (${t("received")}: ${o.reorder_received_color})` : ""}</span>}
              </div>
            )}
            {o.transferred_to_inventory && (
              <div className="mt-1 text-xs" data-testid="inventory-transfer-line">
                {o.transferred_to_inventory.kind === "spare_parts"
                  ? <>🧩 {t("Set goes to SPARE PARTS")} · <b>{String(o.transferred_to_inventory.warehouse || "china").toUpperCase()}</b> · {t("Catalog #")} <b>{o.transferred_to_inventory.cat_number || "—"}</b> · <b>{t(o.transferred_to_inventory.reason_label || "")}</b></>
                  : <>📦 {t("Item transferred to Inventory")} · <b>{String(o.transferred_to_inventory.warehouse || "china").toUpperCase()}</b> · {t("Catalog #")} <b>{o.transferred_to_inventory.cat_number || "—"}</b> · {t("colour")} <b>{o.transferred_to_inventory.color}</b></>}
                {o.store_pending
                  ? <span className="ml-2 font-semibold text-amber-800" data-testid="store-pending-station">— {t("waiting for")} <b>{t(STATION_LABEL[stationForWarehouse(o.transferred_to_inventory.warehouse)])}</b> {t(o.transferred_to_inventory.kind === "spare_parts" ? "to put it in spare parts" : "to store it")}</span>
                  : o.stored_at && <span className="ml-2 text-emerald-800">✓ {t(o.transferred_to_inventory.kind === "spare_parts" ? "In spare parts" : "Stored")} · {o.stored_by} · {new Date(o.stored_at).toLocaleString()}</span>}
              </div>
            )}
          </div>
          {o.store_pending && canAckStore(user, o.transferred_to_inventory?.warehouse) && (
            <StoreAckButton orderId={o.id} size="default" spare={o.transferred_to_inventory?.kind === "spare_parts"} />
          )}
          {o.transferred_to_inventory && o.transferred_to_inventory.kind !== "spare_parts" && ["canada", "usa"].includes(String(o.transferred_to_inventory.warehouse || "").toLowerCase()) && canAckStore(user, o.transferred_to_inventory.warehouse) && (
            <Button variant="outline" size="sm" onClick={() => printStopLabel({ po: o.po_number, cat: o.transferred_to_inventory.cat_number || o.catalog_number, color: o.transferred_to_inventory.color || o.color, warehouse: o.transferred_to_inventory.warehouse, lang })} data-testid="stored-print-label">
              🖨 {t("Print label")}
            </Button>
          )}
        </div>
      )}
      {/* QC hold / Quality Control panel — reason, photos, response + actions */}
      {(o.on_hold || (o.qc_hold && o.status === "production")) && (() => {
        const qcMode = !o.on_hold;
        const isFactory = String(user.role || "").startsWith("factory_");
        return (
          <div
            className={`rounded-lg border-2 px-4 py-3 ${qcMode ? "border-red-600 bg-red-50 text-red-900" : "border-amber-500 bg-amber-50 text-amber-900"}`}
            data-testid={qcMode ? "qc-panel" : "hold-panel"}
          >
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="font-black uppercase tracking-widest text-sm">
                  {qcMode ? `🔴 ${t("QUALITY CONTROL")}` : `🛑 ${t("ON HOLD · QC ISSUE")}`}
                </div>
                <div className="mt-1 text-sm font-semibold" data-testid="qc-reason">{holdReasonText(qcMode ? { hold_reason: o.qc_reason, hold_reason_translation: o.qc_reason_translation } : o)}</div>
                <div className="text-xs mt-0.5">
                  {t("Reported by")} <b>{qcMode ? o.qc_reported_by : o.hold_reported_by}</b>
                  {" · "}{new Date(qcMode ? (o.qc_reported_at || o.qc_sent_back_at) : o.hold_reported_at).toLocaleString()}
                  {qcMode && o.qc_sent_back_by && <> · {t("Sent back by")} <b>{o.qc_sent_back_by}</b></>}
                </div>
                {(qcMode ? o.qc_note : o.hold_response_note) && (
                  <div className="text-xs mt-1 italic" data-testid="qc-note">{t("Response")}: "{qcMode ? o.qc_note : o.hold_response_note}"</div>
                )}
                {!qcMode && !o.reorder_request?.status && <div className="text-xs mt-1 opacity-80">{t("Waiting for Shipping Desk / Back Office / Super Admin to respond.")}</div>}
                {!qcMode && o.reorder_request?.status === "pending" && (
                  <div className="text-xs mt-1 font-semibold" data-testid="reorder-pending-line">
                    🔁 {t("Re-order requested")} · {o.reorder_request.requested_by} · {t("colour received")}: <b>{o.reorder_request.received_color}</b>
                    {o.reorder_request.note && <> · "{o.reorder_request.note}"</>} — {t("waiting for Factory One administrator")}
                  </div>
                )}
                {qcMode && isFactory && <div className="text-xs mt-1 font-semibold">{t("Resolve the Quality Control issue before completing production.")}</div>}
                <div className="mt-2"><IssuePhotos photos={qcMode ? o.qc_photo_ids : o.hold_photo_ids} size="h-20" testid="qc-photos" /></div>
              </div>
              <div className="flex flex-wrap gap-2">
                {!qcMode && HOLD_RESPONDER_ROLES.includes(user.role) && (
                  <HoldRespondButtons orderId={o.id} poNumber={o.po_number} size="default"
                    reorderStatus={o.reorder_request?.status}
                    reorderNote={o.reorder_request?.status === "rejected" ? o.reorder_request?.reject_note : o.reorder_request?.note}
                    reorderBy={o.reorder_request?.status === "rejected" ? o.reorder_request?.rejected_by : o.reorder_request?.requested_by} />
                )}
                {!qcMode && o.reorder_request?.status === "pending" && canDecideReorder(user, o.assigned_factory) && (
                  <ReorderDecideButtons orderId={o.id} poNumber={o.po_number} size="default" onDone={goBackToList} />
                )}
                {qcMode && (isFactory || user.role === "super_admin") && (
                  <QcResolveButton orderId={o.id} size="default" />
                )}
              </div>
            </div>
          </div>
        );
      })()}
      {/* Hidden camera input for the Packing Checklist "Take Photo" flow */}
      <input
        ref={packingCameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={(e) => {
          const files = Array.from(e.target.files || []);
          if (files.length) drivePhotoUpload.mutate(files);
          e.target.value = "";
        }}
        className="hidden"
        data-testid="packing-camera-input"
      />
      {/* Header */}
      <div className="flex flex-col gap-2">
        <div data-testid="detail-back-row">{backBtn}</div>
        <div className="flex items-start justify-between gap-6">
        <div className="min-w-0">
          <h1 className="font-display text-3xl font-bold tracking-tight flex items-center gap-3 flex-wrap h-[1.6rem] leading-none">
            {(!!o.rush_urgent || Number(o.rush_level || 0) >= 3) && (
              <span title="URGENT" data-testid="detail-rush-flag" className="text-red-700 text-3xl">🚩</span>
            )}
            <span className={`whitespace-nowrap ${(!!o.rush_highlight || Number(o.rush_level || 0) >= 1) ? "text-red-700" : ""}`} data-testid="detail-po-number">
              {o.po_number}
            </span>
            <UsFlag order={o} size="lg" testid="detail-us-flag" />
            {!canToggleMarks && <PickupBadge order={o} size="lg" testid="detail-pickup-badge" />}
            {/* Row 1 marks — USA flag · Rush — sit right next to the flag. */}
            <span className={`inline-flex items-center gap-2 ${TILE_LEFT} [&_button]:font-medium [&_button]:tracking-normal [&_button]:[font-family:inherit]`} style={{ fontFamily: '"IBM Plex Sans", system-ui, sans-serif' }} data-testid="detail-title-marks">
              {usFlagToggleBtn}
              {isBackOffice && <RushLevelButton order={o} />}
              {isBackOffice && <ShipHoldButton order={o} />}
            </span>
          </h1>
          {/* Row 2 marks — Pick up · Reminder · Fit — above Station + Status. */}
          <div className={`flex items-center gap-2 flex-wrap mt-2 ${TILE_LEFT}`} data-testid="detail-marks-row">
            {!o.is_stock_unit && pickupToggleBtn}
            {!o.is_stock_unit && pickupOverrideBtn}
            {!o.is_stock_unit && <RemindersButton orderId={id} poNumber={o.po_number} label={t("Reminder")} />}
            <FitButton order={o} canEdit={isBackOffice} />
            {/* ⭐ Review — next to FIT: send / status of the photo review request (BO / SA, fulfilled orders) */}
            {!o.is_stock_unit && <ReviewRequestButton order={o} user={user} />}
            {isBackOffice && o.customer?.email && !o.is_stock_unit && <StatusEmailButton order={o} className="h-9 px-3 text-xs" />}
          </div>
          <div className="font-display tracking-tight flex items-center gap-2 flex-wrap mt-2" data-testid="detail-station-status-row">
            {stationBadge}
            {stageBlock}
            {!canToggleMarks && rushBadge}
            {notReadyBadge}
            {cancelledBadge}
          </div>
          <p className="text-sm text-slate-500 mt-1 flex items-center gap-2">
            {isBackOffice ? (
              <>
                <span>{o.customer?.name} · Shopify {o.shopify_order_number || "—"}</span>
                {user.role === "super_admin" && o.customer_id && (
                  <button
                    type="button"
                    data-testid="edit-customer-name-btn"
                    title="Edit customer name (Super Admin only)"
                    onClick={() => { setEditNameValue(o.customer?.name || ""); setEditNameOpen(true); }}
                    className="text-slate-400 hover:text-slate-700 leading-none"
                  >
                    ✎
                  </button>
                )}
                <EditContactButton order={o} compact testid="edit-contact-header" />
                {o.customer?.email && <span className="text-slate-400 text-xs" data-testid="header-customer-email">{o.customer.email}</span>}
                <EmailBouncedBadge customer={o.customer} testid="header-email-bounced" />
              </>
            ) : (
              <span>Order {o.po_number}</span>
            )}
          </p>
        </div>
        {/* Every button in the header action bar shares one height (user rule Oct 7, 2026). */}
        <div className={ACTION_ROW} data-testid="detail-action-bar">
          {/* Rush escalation (Back Office only).
              Suppressed for Advance / Flagged — they render their own two-row layouts below. */}
          {isBackOffice && o.status === "pending" && <BackToPreOrderButton order={o} testid="pending-back-to-pre-order" />}
          {/* Stage action buttons */}
          {isBackOffice && o.status === "na" && (
            <div className="flex flex-col items-end w-full gap-2" data-testid="na-action-rows">
              {/* Row 1 — resolution moves */}
              <div className="flex flex-wrap gap-2 justify-end">
                <BackToPreOrderButton order={o} testid="na-back-to-pre-order" />
                <Button
                  data-testid="na-back-to-advance"
                  onClick={() =>
                    api.post(`/orders/${id}/na-back-to-advance`)
                      .then(() => {
                        qc.invalidateQueries();
                        toast.success("Returned to Advance Orders · row un-painted");
                        navigate("/orders/advance");
                      })
                      .catch((e) => toast.error(e?.response?.data?.detail || "Failed"))
                  }
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  ← Back to Advance
                </Button>
                <Button data-testid="reassign-factory-2" onClick={() => api.post(`/orders/${id}/reassign-factory`, { factory_role: "factory_2" }).then(() => { qc.invalidateQueries(); toast.success("Reassigned to Factory 2"); goBackToList(); }).catch(e => toast.error(e?.response?.data?.detail || "Failed"))} className="bg-sky-600 hover:bg-sky-700 text-white">→ Factory 2</Button>
                <Button data-testid="reassign-factory-3" onClick={() => api.post(`/orders/${id}/reassign-factory`, { factory_role: "factory_3" }).then(() => { qc.invalidateQueries(); toast.success("Reassigned to Factory 3"); goBackToList(); }).catch(e => toast.error(e?.response?.data?.detail || "Failed"))} className="bg-sky-600 hover:bg-sky-700 text-white">→ Factory 3</Button>
              </div>
              {/* Row 2 — support actions */}
              <div className="flex flex-wrap gap-2 justify-end">
                <Button
                  data-testid="na-edit-codes"
                  onClick={() => setEditCodesOpen(true)}
                  className="bg-sky-600 hover:bg-sky-700 text-white"
                >
                  Edit Codes
                </Button>
                <Button
                  data-testid="na-cancel"
                  onClick={() => {
                    setCancelRefundAmount("");
                    setCancelReason("");
                    setCancelReasonIds([]);
                    setCancelOpen(true);
                  }}
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  ✕ {t("Cancel Order")}
                </Button>
                {o.customer?.email && !isLeadOrder && (
                  <Button
                    data-testid="send-email-btn"
                    variant="outline"
                    className="border-slate-400 whitespace-nowrap"
                    onClick={() => setEmailDialogOpen(true)}
                  >
                    📧 {t("Send Email")}
                  </Button>
                )}
              </div>
            </div>
          )}
          {(user.role === "back_office" || user.role === "super_admin") && o.status === "fulfilled" && !o.claim_open && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button data-testid="make-claim-btn" className="bg-rose-600 hover:bg-rose-700 text-white">⚠️ {t("Make a claim")} ▾</Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" data-testid="make-claim-menu">
                {[["defective", "Defective"], ["incompatible", "Incompatible"], ["missing", "Missing"]].map(([k, l]) => (
                  <DropdownMenuItem key={k} data-testid={`make-claim-${k}`} onClick={() => { setClaimType(k); setClaimNote(""); setClaimFiles([]); }}>{t(l)}</DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {(user.role === "back_office" || user.role === "super_admin") && (o.is_lead || o.status === "lead") && (
            <Button
              data-testid="approve-lead-btn"
              onClick={() => setLeadApproveOpen(true)}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              title={o.status === "advance" ? "Convert this LD to a real PO — required to move past Advance" : "Convert this LD to a real PO"}
            >
              🔁 Convert LD → PO
            </Button>
          )}
          {/* Edit Codes on a Lead — lets BO fix bogus codes (e.g. Drive-sync
              placeholders like "PO 1"/"PO 10") without leaving the header.
              Opens a compact dialog with 4 code inputs. */}
          {(user.role === "back_office" || user.role === "super_admin") && (o.is_lead || o.status === "lead") && (
            <Button
              data-testid="lead-edit-codes-btn"
              onClick={() => setLeadEditCodesOpen(true)}
              variant="outline"
              className="border-amber-400 text-amber-800 hover:bg-amber-50"
              title="Edit front / rear / clamp codes on this lead"
            >
              ✏️ Edit Codes
            </Button>
          )}
          {/* Send Email on a Lead — always available to BO+SA regardless of
              whether the customer.email is set. The Send Email dialog now
              carries an inline recipient input so BO can type the address
              and we auto-save it to the customer record. */}
          {(user.role === "back_office" || user.role === "super_admin") && (o.is_lead || o.status === "lead") && (
            <Button
              data-testid="send-email-btn"
              onClick={() => setEmailDialogOpen(true)}
              variant="outline"
              className="border-slate-400 whitespace-nowrap"
              title={o.customer?.email ? `Send email to ${o.customer.email}` : "Customer has no email — enter one inline in the dialog"}
            >
              📧 Send Email{!o.customer?.email ? " · needs email" : ""}
            </Button>
          )}
          {/* Delete Lead — available to Back Office AND Super Admin (Mar 2026
              rule). Both roles can clean up stale / stuck LD placeholders. */}
          {(user.role === "back_office" || user.role === "super_admin") && (o.is_lead || o.status === "lead") && (
            <Button
              data-testid="delete-lead-btn"
              onClick={() => { setDeleteLeadConfirm(""); setDeleteLeadOpen(true); }}
              className="bg-red-600 hover:bg-red-700 text-white"
              title="Permanently delete this lead"
            >
              🗑 Delete Lead
            </Button>
          )}
          {user.role === "super_admin" && !["cancelled", "lead"].includes(o.status) && <ArchiveReplacedButton order={o} />}
          {isBackOffice && (o.status === "pre_order" || o.status === "lead") && (
            <>
              <WaitingReplyButton order={o} />
              <Button
                data-testid="approve-preorder"
                onClick={() => transition.mutate("pending")}
                className="bg-emerald-600 hover:bg-emerald-700 text-white whitespace-nowrap"
              >
                ✓ Approve → Pending
              </Button>
              <Button data-testid="to-na" onClick={() => transition.mutate("na")} className="bg-red-600 hover:bg-red-700 text-white whitespace-nowrap">Send to NA</Button>
            </>
          )}
          {isFactory && o.status === "pending" && (() => {
            // Cover codes must be BRAND + NUMBER (or FF / RR) before the factory can submit.
            const blockers = coverCodeBlockers({
              front_cover_code: form.front_cover_code ?? o.front_cover_code,
              rear_cover_code: form.rear_cover_code ?? o.rear_cover_code,
            });
            const ready = blockers.length === 0;
            return (
            <>
              {!ready && (
                <span className="text-[11px] font-medium text-amber-700 self-center max-w-[260px] text-right" data-testid="codes-incomplete-hint">
                  ⚠ {t("Cover codes need a brand code + number")}: {blockers.join(" · ")}
                </span>
              )}
              <Button
                data-testid="codes-to-advance"
                disabled={!ready}
                title={ready ? undefined : blockers.join(" · ")}
                onClick={async () => {
                  const codePayload = {
                    front_cover_code: form.front_cover_code || "",
                    f_clamp_code: form.f_clamp_code || "",
                    rear_cover_code: form.rear_cover_code || "",
                    r_clamp_code: form.r_clamp_code || "",
                  };
                  const late = coverCodeBlockers(codePayload);
                  if (late.length) { toast.error(`${t("Cover codes need a brand code + number")}: ${late.join(" · ")}`); return; }
                  try {
                    await api.patch(`/orders/${id}`, codePayload);
                    await api.post(`/orders/${id}/transition`, { target_stage: "advance" });
                    qc.invalidateQueries();
                    toast.success("Codes saved and moved to Advance");
                    goBackToList();
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || "Failed");
                  }
                }}
                className="bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-40"
              >
                ✓ {t("Submit Codes → Advance")}
              </Button>
              <Button data-testid="codes-not-available" onClick={() => transition.mutate("na")} className="bg-red-600 hover:bg-red-700 text-white">{t("Not Available → NA")}</Button>
              <Button data-testid="bad-photos-btn" onClick={() => setBadPhotosOpen(true)} className="bg-rose-500 hover:bg-rose-600 text-white">📷 {t("Bad photos")}</Button>
            </>
            );
          })()}
          {isBackOffice && o.status === "advance" && o.is_lead && (
            /* LD orders in Advance get a restricted 3-button set (user rule
               Feb 28, 2026): Rush, Convert LD→PO, Send Email. The full
               advance-stage bar (production/inv-packing/na/flag/edit-codes/
               recheck/cancel) is intentionally hidden — the reviewer must
               convert the LD to a real PO first before it can travel. The
               Convert LD→PO button lives above at line ~787 and is always
               visible for is_lead orders, so we only need Rush + email here. */
            <div className="flex flex-wrap gap-2 justify-end" data-testid="advance-lead-action-row">
              <BackToPreOrderButton order={o} testid="advance-lead-back-to-pre-order" />
            </div>
          )}
          {isBackOffice && o.status === "advance" && !o.is_lead && (
            <div className="flex flex-col items-end w-full gap-2" data-testid="advance-action-rows">
              {/* Row 1 — forward moves */}
              <div className="flex flex-wrap gap-2 justify-end">
                <BackToPreOrderButton order={o} testid="advance-back-to-pre-order" />
                <Button data-testid="to-production" onClick={() => guardedTransition("production")} className="bg-emerald-600 hover:bg-emerald-700 text-white">→ Production</Button>
                <Button data-testid="to-invpack" onClick={() => guardedTransition("pickup_canada")} className="bg-orange-500 hover:bg-orange-600 text-white" title="Pick the part from Canada storage → Inventory Packing confirms it (stock −1) or sends it back here as NO INVENTORY">📦 {t("Pick from Canada storage")}</Button>
                <Button data-testid="to-pickup" onClick={() => guardedTransition("pickup")} className="bg-amber-700 hover:bg-amber-800 text-white" title="Pick the part up from the China warehouse (→ Packing Orders) or USA (→ 3PL) instead of producing it">📦 {t("Pick from China / USA warehouse")}</Button>
                <Button data-testid="advance-to-na" onClick={() => transition.mutate("na")} className="bg-red-600 hover:bg-red-700 text-white">→ NA Orders</Button>
                <Button data-testid="to-flagged" onClick={() => transition.mutate("flagged")} className="bg-red-600 hover:bg-red-700 text-white">Flag</Button>
              </div>
              {/* Row 2 — support actions */}
              <div className="flex flex-wrap gap-2 justify-end">
                <Button data-testid="edit-codes-btn" onClick={() => setEditCodesOpen(true)} className="bg-sky-600 hover:bg-sky-700 text-white">
                  Edit Codes
                </Button>
                <Button
                  data-testid="recheck-codes-btn"
                  onClick={() => setRecheckOpen(true)}
                  className="bg-sky-600 hover:bg-sky-700 text-white"
                  title="Send back to Pending Orders so the factory re-enters the codes"
                >
                  ↩ Recheck CODES{o.recheck_attempt ? ` (#${(o.recheck_attempt || 0) + 1})` : ""}
                </Button>
                <Button
                  data-testid="cancel-order-btn"
                  className="bg-red-600 hover:bg-red-700 text-white"
                  onClick={() => {
                    setCancelRefundAmount("");
                    setCancelReason("");
                    setCancelReasonIds([]);
                    setCancelOpen(true);
                  }}
                >
                  ✕ {t("Cancel Order")}
                </Button>
                {o.customer?.email && !isLeadOrder && (
                  <Button
                    data-testid="send-email-btn"
                    variant="outline"
                    className="border-slate-400 whitespace-nowrap"
                    onClick={() => setEmailDialogOpen(true)}
                  >
                    📧 {t("Send Email")}
                  </Button>
                )}
              </div>
            </div>
          )}
          {/* Recheck CODES for Advance/Flagged is now rendered inside each
              status's own two-row layout above so the button lives on the
              correct visual row. No shared conditional here. */}
          {isBackOffice && o.status === "pending" && o.codes_recheck_requested && (
            <Button data-testid="edit-codes-btn-pending" onClick={() => setEditCodesOpen(true)} className="bg-sky-600 hover:bg-sky-700 text-white">
              Edit Codes
            </Button>
          )}
          {isBackOffice && o.status === "pending" && (() => {
            // BO/SA can push the order to Advance the moment all 4 codes are filled.
            // Sits right next to Cancel Order (the pre-existing red button below)
            // so the two most common next-steps are colocated.
            const codesReady = ["front_cover_code", "f_clamp_code", "rear_cover_code", "r_clamp_code"]
              .every((k) => (o[k] || "").trim()) && coverCodeBlockers(o).length === 0;
            const boBlockers = coverCodeBlockers(o);
            return (
              <>
              <Button data-testid="bad-photos-btn-bo" onClick={() => setBadPhotosOpen(true)} className="bg-rose-500 hover:bg-rose-600 text-white">📷 {t("Bad photos")}</Button>
              <Button
                data-testid="pending-submit-codes-advance"
                disabled={!codesReady}
                title={codesReady ? "Move this order to Advance" : boBlockers.length ? boBlockers.join(" · ") : "Fill all four cover/clamp codes first (Order Info tab)"}
                onClick={async () => {
                  try {
                    await api.post(`/orders/${id}/transition`, {
                      target_stage: "advance",
                      reason: "Submitted codes → Advance from Order detail",
                    });
                    qc.invalidateQueries();
                    toast.success(`${o.po_number} moved to Advance`);
                    goBackToList();
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || "Transition failed");
                  }
                }}
                className="bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-40"
              >
                ⏩ {t("Submit Codes → Advance")}
              </Button>
              </>
            );
          })()}
          {isBackOffice && o.status === "flagged" && (() => {
            // "↩ Continue → <origin>" already moves the order back where it came
            // from, so the explicit button for that same stage is hidden (one
            // button per function — user rule Oct 7, 2026).
            const origin = flagOrigin?.stage;
            return (
            <div className="flex flex-col items-end w-full gap-2" data-testid="flagged-action-rows">
              {/* Row 1 — forward moves */}
              <div className="flex flex-wrap gap-2 justify-end">
                <FlagResumeButton order={o} onDone={goBackToList} />
                {origin !== "pre_order" && <BackToPreOrderButton order={o} testid="flagged-back-to-pre-order" />}
                {origin !== "inventory_packing" && (
                <Button
                  data-testid="flagged-to-invpacking"
                  onClick={() => guardedTransition("inventory_packing")}
                  className="bg-orange-500 hover:bg-orange-600 text-white"
                >
                  → Inv. Packing
                </Button>
                )}
                {origin !== "production" && (
                <Button
                  data-testid="flagged-to-production"
                  onClick={() => guardedTransition("production")}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  → Production
                </Button>
                )}
                {origin !== "packing" && (
                <Button
                  data-testid="flagged-to-packing"
                  onClick={() => guardedTransition("packing")}
                  className="bg-teal-600 hover:bg-teal-700 text-white"
                  title={t("Move straight to the packing station — order will be packed")}
                >
                  → {t("Packing Orders")}
                </Button>
                )}
                {origin !== "advance" && (
                <Button
                  data-testid="flagged-to-advance"
                  onClick={() => transition.mutate("advance")}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  ← Advance Order
                </Button>
                )}
                {origin !== "na" && (
                <Button
                  data-testid="flagged-to-na"
                  onClick={() => transition.mutate("na")}
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  → NA Order
                </Button>
                )}
              </div>
              {/* Row 2 — support actions */}
              <div className="flex flex-wrap gap-2 justify-end">
                <Button
                  data-testid="recheck-codes-btn"
                  onClick={() => setRecheckOpen(true)}
                  className="bg-sky-600 hover:bg-sky-700 text-white"
                  title={`Attempt #${(o.recheck_attempt || 0) + 1} — re-verify codes`}
                >
                  ↩ Recheck CODES{o.recheck_attempt ? ` (#${(o.recheck_attempt || 0) + 1})` : ""}
                </Button>
                <Button
                  data-testid="cancel-order-btn"
                  className="bg-red-600 hover:bg-red-700 text-white"
                  onClick={() => {
                    setCancelRefundAmount("");
                    setCancelReason("");
                    setCancelReasonIds([]);
                    setCancelOpen(true);
                  }}
                >
                  ✕ {t("Cancel Order")}
                </Button>
                {o.customer?.email && !isLeadOrder && (
                  <Button
                    data-testid="send-email-btn"
                    variant="outline"
                    className="border-slate-400 whitespace-nowrap"
                    onClick={() => setEmailDialogOpen(true)}
                  >
                    📧 {t("Send Email")}
                  </Button>
                )}
              </div>
            </div>
            );
          })()}
          {isFactory && o.status === "production" && !o.factory_signature && (
            <span className="text-xs text-emerald-700 font-mono">↓ Sign below to start</span>
          )}
          {isFactory && o.status === "production" && o.factory_signature && (
            <span className="text-xs text-blue-700 font-mono">↓ Sign below to complete</span>
          )}
          {/* Return to Advance — visible on a Production order that has NOT
              yet been signed by the factory (i.e. work has not started).
              Available to the assigned factory + Back Office + Super Admin so
              anyone reviewing the order pre-start can push it back for edits. */}
          {(isFactory || isBackOffice) && o.status === "production" && !o.factory_signature && !o.claim_id && !o.is_stock_unit && (
            <Button
              data-testid="return-to-advance"
              variant="outline"
              className="border-amber-500 text-amber-700 hover:bg-amber-50"
              title="Send back to Advance Orders (only before production is signed to start)"
              onClick={() => {
                const reason = window.prompt(
                  t("Reason for sending this order back? (optional)"),
                  "",
                );
                if (reason === null) return; // user hit Cancel
                api.post(`/orders/${id}/transition`, {
                  target_stage: "advance",
                  reason: reason || "Returned to Advance from Production (pre-sign)",
                })
                  .then(() => {
                    qc.invalidateQueries();
                    toast.success(`${o.po_number} returned to Advance`);
                    goBackToList();
                  })
                  .catch((e) => toast.error(e?.response?.data?.detail || "Could not return to Advance"));
              }}
            >
              ↩ {t("Return")} {t("Advance Orders")}
            </Button>
          )}
          {isBackOffice && o.status === "production" && (o.factory_signature || o.production_completed_by) && (
            <Button
              data-testid="restart-production"
              variant="outline"
              className="border-rose-500 text-rose-700 hover:bg-rose-50"
              title={t("Clear the factory signature / print / sign-off so Factory One can start production again")}
              onClick={() => {
                const reason = window.prompt(t("Reason for restarting production? (optional)"), "");
                if (reason === null) return;
                api.post(`/orders/${id}/restart-production`, { reason: reason || "" })
                  .then(() => {
                    qc.invalidateQueries();
                    toast.success(`${o.po_number} — ${t("production restarted")}`);
                  })
                  .catch((e) => toast.error(e?.response?.data?.detail || t("Could not restart production")));
              }}
            >
              ↺ {t("Restart production")}
            </Button>
          )}
          {isPacking && o.status === "product_transfer" && (
            <Button
              data-testid="packing-received"
              onClick={() => packingReceive.mutate()}
              disabled={packingReceive.isPending}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              title={`Will be recorded as: ${mySignature}`}
            >
              {packingReceive.isPending ? "Marking..." : `✓ Mark Received (as ${mySignature})`}
            </Button>
          )}
          {isShipping && o.status === "shipping" && (
            <Button data-testid="generate-label" onClick={() => shipLabel.mutate()} className="bg-sky-600 hover:bg-sky-700 text-white">Print Label (mock)</Button>
          )}
          {/* Cancel Order — Super Admin / Back Office only, on any active order that isn't already cancelled/lead/na/advance/flagged.
              (Advance / Flagged / NA render their own Cancel button inside their two-row layouts above.)
              Pre-orders (→ Shopify cancel + refund) and received orders (→ STOP, stock on the shelf) are included (rule Oct 27, 2026). */}
          {isBackOffice && !o.cancelled && !o.cancel_pending && !["lead", "na", "cancelled", "flagged", "advance"].includes(o.status) && (
            <Button
              data-testid="cancel-order-btn"
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={() => {
                setCancelRefundAmount(""); // let backend derive from batch line price
                setCancelReason("");
                setCancelOpen(true);
              }}
            >
              ✕ {t("Cancel Order")}
            </Button>
          )}

          {/* Universal "Send Email" — opens the template picker dialog.
              (Advance / Flagged / NA render their own Send Email inside their two-row layouts above.) */}
          {isBackOffice && o.customer?.email && !o.is_stock_unit && !["advance", "flagged", "na"].includes(o.status) && !isLeadOrder && (
            <Button
              data-testid="send-email-btn"
              variant="outline"
              className="border-slate-400 whitespace-nowrap"
              onClick={() => setEmailDialogOpen(true)}
            >
              📧 {t("Send Email")}
            </Button>
          )}
        </div>
        </div>
      </div>

      {/* Send-Email dialog (template picker + preview + one-off overrides) */}
      {isBackOffice && (
        <SendEmailDialog
          open={emailDialogOpen}
          onOpenChange={setEmailDialogOpen}
          order={o}
        />
      )}


      {/* ---------- Advance Orders → Inventory Cross-Reference panel ---------- */}
      {isBackOfficeOrAdmin && o.status === "advance" && o.production_needed?.at && (
        <section className="rounded-md border-2 border-red-600 bg-red-50 p-4" data-testid="production-needed-banner">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="text-[13px] font-black uppercase tracking-widest text-red-700">🏭 Production needed</div>
              <div className="text-sm text-red-900 mt-1">
                <b>{WAREHOUSE_STATION_LABEL[o.production_needed.warehouse] || "The station"}</b> could not find{" "}
                <span className="font-mono font-bold">{o.production_needed.cat_number || o.catalog_number || "—"}</span> · <b>{o.production_needed.color || o.color || "—"}</b> ({FACTORY_SHORT[o.production_needed.factory] || "F1"}) in the{" "}
                <b>{WAREHOUSE_LABEL[o.production_needed.warehouse] || "—"}</b> warehouse — that colour's count there is now 0.
                {o.production_needed.note && <> Note: <i>{o.production_needed.note}</i>.</>}
              </div>
              <div className="text-xs text-red-700 mt-1 font-mono">{o.production_needed.by || "—"} · {String(o.production_needed.at).slice(0, 16).replace("T", " ")}</div>
            </div>
            <Button data-testid="production-needed-to-production" onClick={() => guardedTransition("production")} className="bg-emerald-600 hover:bg-emerald-700 text-white">→ Production</Button>
          </div>
        </section>
      )}
      {isBackOfficeOrAdmin && o.status === "advance" && stockAvail && (
        <section
          className="rounded-md border-2 p-5"
          style={{ borderColor: "#C30000", background: "#fff5f5" }}
          data-testid="inv-match-panel"
        >
          <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
            <div>
              <div className="text-[13px] font-bold uppercase tracking-widest" style={{ color: "#C30000" }}>
                Inventory Cross-Reference
              </div>
              <div className="text-xs text-slate-600 mt-0.5">
                CAT # <span className="font-mono font-bold">{stockAvail.cat_number || "—"}</span>
                {stockAvail.color && <> · Colour <span className="font-mono font-bold">{stockAvail.color}</span></>}
                {stockAvail.matched_colour && stockAvail.matched_colour !== stockAvail.color && (
                  <> · matched to <span className="font-mono">{stockAvail.matched_colour}</span></>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => rematch.mutate()}
                disabled={rematch.isPending}
                data-testid="inv-rematch-btn"
                title="Re-run the inventory lookup using the current front/rear codes"
              >
                🔄 {rematch.isPending ? "Matching…" : "Re-run Match"}
              </Button>
              {(o.catalog_number || "").trim() && <CatalogSyncButton order={o} />}
              {(user.sub_role === "administrator" || !user.sub_role || user.role === "super_admin") && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setAssignCatOpen(true)}
                  data-testid="inv-assign-btn"
                >
                  Assign CAT # Manually
                </Button>
              )}
            </div>
          </div>

          {stockAvail.cat_number && invMatch?.assigned_vehicle && <VehicleFitBanner order={o} fit={invMatch.assigned_vehicle} />}
          {!stockAvail.cat_number && (invMatch?.suggestions || []).length > 0 ? (
            <CatalogSuggestions order={o} suggestions={invMatch.suggestions} reason={invMatch.reason} />
          ) : !stockAvail.cat_number && (
            <NoMatchDiagnostic invMatch={invMatch} order={o} />
          )}
          {stockAvail.inventory_id && <ClampAdjustCard order={o} mismatch={stockAvail.clamp_mismatch} />}
          {stockAvail.cat_number && !stockAvail.inventory_id && (
            <div className="text-sm text-slate-600 italic">
              CAT # <span className="font-mono font-bold">{stockAvail.cat_number}</span> is not in the inventory catalog — item will be produced.
            </div>
          )}
          {stockAvail.inventory_id && !stockAvail.matched && !stockAvail.no_color && (
            <div className="text-sm text-slate-600 italic">
              No stock recorded for colour <span className="font-mono">{stockAvail.color}</span> on this CAT # — item will be produced.
            </div>
          )}
          {stockAvail.inventory_id && stockAvail.matched && stockAvail.total === 0 && !stockAvail.no_color && (
            <div className="text-sm text-slate-600 italic">
              Stock is 0 for this colour in all three warehouses — item will be produced.
            </div>
          )}

          {stockAvail.matched && stockAvail.total > 0 && (
            <>
              <div className="text-[11px] text-slate-500 mb-3">
                Stock available — send the order for pick-up instead of producing it. The station picks the part up and presses
                <b> Product found</b> (stock −1) or <b>Not found</b> (count → 0, order back here as PRODUCTION NEEDED).
                China → <b>Packing Orders</b> · Canada → <b>Inventory Packing</b> · USA → <b>3PL</b>. Black number = F1 codes, white number = F2 codes.
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {WAREHOUSES.filter((w) => (stockAvail.stock?.[w] || 0) > 0).map((w) => (
                  <div
                    key={w}
                    className="rounded-lg border-2 p-4 bg-white"
                    style={{ borderColor: "#0f172a" }}
                    data-testid={`invmatch-${w}-card`}
                  >
                    <div className="text-[10px] uppercase tracking-widest font-mono text-slate-500 flex items-center gap-1.5">
                      <span>{WAREHOUSE_FLAG[w]}</span> {WAREHOUSE_LABEL[w]} · in stock · → {WAREHOUSE_STATION_LABEL[w]}
                    </div>
                    <div className="text-3xl font-bold font-mono text-slate-900 mt-1" data-testid={`invmatch-${w}-qty`}>
                      {stockAvail.stock[w]}
                    </div>
                    <div className="text-[11px] text-slate-500 font-mono mt-0.5 mb-3">
                      units of {stockAvail.matched_colour || stockAvail.color}
                      {stockAvail.bins?.[w] && <span className="ml-2 text-slate-700" data-testid={`invmatch-${w}-bin`}>📍 Bin {stockAvail.bins[w]}</span>}
                    </div>
                    <div className="flex gap-2">
                      {STOCK_FACTORIES.map((f) => {
                        const q = stockAvail.per_factory?.[w]?.[f] || 0;
                        const isF2 = f === "factory_2";
                        return (
                          <Button
                            key={f}
                            data-testid={`pickup-${w}-${f}-btn`}
                            onClick={() => setPickTarget({ warehouse: w, factory: f })}
                            disabled={q <= 0}
                            className={`flex-1 font-mono font-semibold ${isF2 ? "bg-neutral-900 hover:bg-neutral-700 text-white" : "bg-white hover:bg-neutral-100 text-neutral-900 border-2 border-neutral-900"} disabled:opacity-30`}
                            title={q > 0 ? `Send for pick-up — ${FACTORY_SHORT[f]} cover from ${WAREHOUSE_LABEL[w]}` : `No ${FACTORY_SHORT[f]} stock in ${WAREHOUSE_LABEL[w]}`}
                          >
                            {FACTORY_SHORT[f]} · {q}
                          </Button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {stockAvail.inventory_id && stockAvail.no_color && (
            <div className="mt-4" data-testid="no-color-stock">
              <div className="text-[11px] uppercase tracking-widest font-mono text-teal-800 font-bold mb-1">
                ◌ No Color stock of CAT # {stockAvail.cat_number} — usable for any colour, incl. {o.color || "—"}
              </div>
              <div className="text-[11px] text-slate-500 mb-3">
                {stockAvail.total === 0 ? "No stock of the ordered colour — " : ""}No Color covers fulfil any colour request. No approval needed; the station is told the order colour.
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {WAREHOUSES.filter((w) => (stockAvail.no_color.stock?.[w] || 0) > 0).map((w) => (
                  <div key={`nc-${w}`} className="rounded-lg border-2 border-teal-500 bg-teal-50/40 p-4" data-testid={`nocolor-${w}-card`}>
                    <div className="text-[10px] uppercase tracking-widest font-mono text-slate-500 flex items-center gap-1.5">
                      <span>{WAREHOUSE_FLAG[w]}</span> {WAREHOUSE_LABEL[w]} · → {WAREHOUSE_STATION_LABEL[w]}
                    </div>
                    <div className="flex items-baseline gap-2 mt-1">
                      <span className="text-3xl font-bold font-mono text-slate-900" data-testid={`nocolor-${w}-qty`}>{stockAvail.no_color.stock[w]}</span>
                      <span className="font-bold uppercase text-teal-900">No Color</span>
                    </div>
                    <div className="text-[11px] text-slate-500 font-mono mt-0.5 mb-3">
                      for {o.color || "—"}{stockAvail.no_color.bins?.[w] && <span className="ml-2 text-slate-700">📍 Bin {stockAvail.no_color.bins[w]}</span>}
                    </div>
                    <div className="flex gap-2">
                      {STOCK_FACTORIES.map((f) => {
                        const q = stockAvail.no_color.per_factory?.[w]?.[f] || 0;
                        const isF2 = f === "factory_2";
                        return (
                          <Button key={f} data-testid={`pickup-nocolor-${w}-${f}-btn`} onClick={() => setPickTarget({ warehouse: w, factory: f, use_no_color: true })} disabled={q <= 0}
                            className={`flex-1 font-mono font-semibold ${isF2 ? "bg-neutral-900 hover:bg-neutral-700 text-white" : "bg-white hover:bg-neutral-100 text-neutral-900 border-2 border-neutral-900"} disabled:opacity-30`}>
                            {FACTORY_SHORT[f]} · {q}
                          </Button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {stockAvail.inventory_id && Object.keys(stockAvail.other_colors || {}).length > 0 && (
            <div className="mt-4" data-testid="other-colors-stock">
              <div className="text-[11px] uppercase tracking-widest font-mono text-amber-800 font-bold mb-1">
                ⚠ Other colours of CAT # {stockAvail.cat_number} in stock — different from the ordered colour ({o.color || "—"})
              </div>
              <div className="text-[11px] text-slate-500 mb-3">
                You decide: the part can still be picked up in another colour — it needs a signed “Are you sure?” approval and is recorded on the order.
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {Object.entries(stockAvail.other_colors).flatMap(([colour, info]) =>
                  WAREHOUSES.filter((w) => (info.stock?.[w] || 0) > 0).map((w) => (
                    <div key={`${colour}-${w}`} className="rounded-lg border-2 border-amber-400 bg-amber-50/40 p-4" data-testid={`othercolor-${colour.replace(/\s+/g, "-").toLowerCase()}-${w}-card`}>
                      <div className="text-[10px] uppercase tracking-widest font-mono text-slate-500 flex items-center gap-1.5">
                        <span>{WAREHOUSE_FLAG[w]}</span> {WAREHOUSE_LABEL[w]} · → {WAREHOUSE_STATION_LABEL[w]}
                      </div>
                      <div className="flex items-baseline gap-2 mt-1">
                        <span className="text-3xl font-bold font-mono text-slate-900">{info.stock[w]}</span>
                        <span className="font-bold uppercase text-amber-900">{colour}</span>
                      </div>
                      <div className="text-[11px] text-slate-500 font-mono mt-0.5 mb-3">
                        instead of {o.color || "—"}{info.bins?.[w] && <span className="ml-2 text-slate-700">📍 Bin {info.bins[w]}</span>}
                      </div>
                      <div className="flex gap-2">
                        {STOCK_FACTORIES.map((f) => {
                          const q = info.per_factory?.[w]?.[f] || 0;
                          const isF2 = f === "factory_2";
                          return (
                            <Button
                              key={f}
                              data-testid={`pickup-other-${colour.replace(/\s+/g, "-").toLowerCase()}-${w}-${f}-btn`}
                              onClick={() => setPickTarget({ warehouse: w, factory: f, color_override: colour })}
                              disabled={q <= 0}
                              className={`flex-1 font-mono font-semibold ${isF2 ? "bg-neutral-900 hover:bg-neutral-700 text-white" : "bg-white hover:bg-neutral-100 text-neutral-900 border-2 border-neutral-900"} disabled:opacity-30`}
                            >
                              {FACTORY_SHORT[f]} · {q}
                            </Button>
                          );
                        })}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </section>
      )}

      {/* Send-for-pick-up confirmation dialog (from the Cross-Reference cards) */}
      <Dialog open={!!pickTarget} onOpenChange={(v) => { if (!v) { setPickTarget(null); setOverrideSig(""); } }}>
        <DialogContent data-testid="pickup-confirm-dialog">
          <DialogHeader>
            <DialogTitle>
              {pickTarget?.color_override ? "⚠ Are you sure? Different colour · " : "📦 "}Pick up from {WAREHOUSE_LABEL[pickTarget?.warehouse] || ""} warehouse · {FACTORY_SHORT[pickTarget?.factory] || ""}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-slate-600">
              <b>{WAREHOUSE_STATION_LABEL[pickTarget?.warehouse] || ""}</b> will be asked to pick up{" "}
              <span className="font-mono font-bold">{pickTarget?.color_override || o.color}</span>{pickTarget?.use_no_color && <> from the <b>No Color</b> stock</>} (<span className="font-mono">{o.catalog_number}</span>) made with{" "}
              <b>{FACTORY_SHORT[pickTarget?.factory] || ""} codes</b> from the <b>{WAREHOUSE_LABEL[pickTarget?.warehouse] || ""}</b> warehouse.
              Stock is reduced by 1 when they press <b>Product found</b>; <b>Not found</b> brings the order back here as PRODUCTION NEEDED.
            </p>
            {pickTarget?.color_override ? (
              <div className="rounded-md border-2 border-amber-500 bg-amber-50 p-3 space-y-2" data-testid="pickup-override-warning">
                <div className="text-sm font-bold text-amber-900">
                  The customer ordered <span className="uppercase">{o.color || "—"}</span> — you are picking <span className="uppercase">{pickTarget.color_override}</span>.
                </div>
                <div className="text-xs text-amber-800">Inventory colours stay as they are; the different colour and your signature are recorded on the order and shown to the station.</div>
                <Input value={overrideSig} onChange={(e) => setOverrideSig(e.target.value)} placeholder={`Sign to approve — ${mySignature}`} data-testid="pickup-override-signature" />
              </div>
            ) : (
              <div className="bg-slate-50 border border-slate-200 rounded-md p-3 text-xs" data-testid="pickup-signed-as">
                <span className="text-slate-500 uppercase tracking-widest font-mono text-[10px]">Signed as</span>
                <div className="font-semibold text-slate-900 mt-0.5">{mySignature}</div>
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2 pt-3 border-t border-slate-200">
            <Button data-testid="pickup-cancel" variant="outline" onClick={() => { setPickTarget(null); setOverrideSig(""); }} disabled={sendToPickup.isPending}>
              Cancel
            </Button>
            <Button data-testid="pickup-confirm" onClick={() => sendToPickup.mutate(false)} disabled={sendToPickup.isPending || (!!pickTarget?.color_override && !overrideSig.trim())} className={`text-white ${pickTarget?.color_override ? "bg-amber-600 hover:bg-amber-700" : "bg-amber-700 hover:bg-amber-800"}`}>
              {sendToPickup.isPending ? "Sending…" : pickTarget?.color_override ? `⚠ Yes — send ${pickTarget.color_override} to ${WAREHOUSE_STATION_LABEL[pickTarget?.warehouse] || ""}` : `📦 Send to ${WAREHOUSE_STATION_LABEL[pickTarget?.warehouse] || ""}`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Codes dialog */}
      {/* ⚠️ After-sale claim dialog (Order Fulfilled) */}
      <Dialog open={!!claimType} onOpenChange={(v) => { if (!v) { setClaimType(null); setClaimFiles([]); } }}>
        <DialogContent className="max-w-lg" data-testid="claim-dialog">
          <DialogHeader>
            <DialogTitle>⚠️ {t("Claim")} · {t(({ defective: "Defective", incompatible: "Incompatible", missing: "Missing" })[claimType] || "")} · {o.po_number}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div>
              <Label className="text-xs">{t("Who handles it?")}</Label>
              <div className={`mt-1 grid gap-2 ${user.role === "back_office" ? "grid-cols-3" : "grid-cols-2"}`} data-testid="claim-handling">
                {[["factory", "⚠️ Factory One / Super Admin decide", "border-rose-600 bg-rose-50 text-rose-900"], ["new_logo", "🏷 I sent a new logo", "border-amber-600 bg-amber-50 text-amber-900"], ["resolved", "✓ I resolved it myself", "border-emerald-600 bg-emerald-50 text-emerald-900"]].filter(([k]) => k !== "new_logo" || user.role === "back_office").map(([k, l, active]) => (
                  <button key={k} type="button" onClick={() => setClaimHandling(k)} data-testid={`claim-handling-${k}`}
                          className={`rounded-md border px-2 py-2 text-left text-xs font-semibold leading-tight ${claimHandling === k ? active : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50"}`}>
                    {t(l)}
                  </button>
                ))}
              </div>
            </div>
            {claimHandling === "factory" ? (
              <div className="rounded-md border border-rose-300 bg-rose-50 p-3 text-xs text-rose-900">
                {t("Goes to the Claims tab for Factory One / Super Admin (AFTER-SALE QC alert). They answer with a warranty replacement, re-order, refund, new logo or declined/resolved — you get the decision on the Dashboard. The customer gets the \"we're sorry, typically 48 h\" e-mail now.")}
              </div>
            ) : (
              <div className="rounded-md border border-emerald-300 bg-emerald-50 p-3 text-xs text-emerald-900">
                {t("Filed straight into the")} <b>{claimHandling === "new_logo" ? t("New logo") : t("Resolved")}</b> {t("folder with your solution. No alert goes to Factory One or Super Admin and no customer e-mail is sent.")}
              </div>
            )}
            <div>
              <Label className="text-xs">{t("What happened?")} <span className="text-red-600">*</span></Label>
              <Textarea data-testid="claim-note" value={claimNote} onChange={(e) => setClaimNote(e.target.value)} rows={3} className="mt-1"
                        placeholder={claimType === "missing" ? t("e.g. rear clamps were not in the box") : claimType === "incompatible" ? t("e.g. front covers do not fit the caliper — 2 mm gap") : t("e.g. paint peeling on the rear left cover")} />
              <div className="text-[11px] text-slate-500 mt-1">🌐 {t("Your note is translated automatically (English ⇄ 中文) for the factory.")}</div>
            </div>
            {claimHandling !== "factory" && (
              <div>
                <Label className="text-xs">{t("Solution performed")} <span className="text-red-600">*</span></Label>
                <Textarea data-testid="claim-solution" value={claimSolution} onChange={(e) => setClaimSolution(e.target.value)} rows={2} className="mt-1"
                          placeholder={claimHandling === "new_logo" ? t("e.g. mailed 2 new logos by Canada Post on Oct 2") : t("e.g. sent the customer fitting instructions — fits now")} />
              </div>
            )}
            <div>
              <Label className="text-xs">{t("Photos (optional, up to 20)")}</Label>
              <div className="mt-1 flex flex-wrap items-center gap-2" data-testid="claim-photo-list">
                {claimFiles.map((f, i) => (
                  <div key={`${f.name}-${i}`} className="relative">
                    <img src={URL.createObjectURL(f)} alt="" className="h-16 w-16 object-cover rounded border border-slate-300" />
                    <button type="button" data-testid={`claim-photo-remove-${i}`} onClick={() => setClaimFiles(claimFiles.filter((_, j) => j !== i))}
                            className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-red-600 text-white text-xs leading-none" title={t("Remove")}>×</button>
                  </div>
                ))}
                {claimFiles.length < 20 && (
                  <label className="h-16 w-16 rounded border-2 border-dashed border-slate-300 hover:border-rose-400 flex items-center justify-center text-[10px] text-slate-500 cursor-pointer text-center leading-tight" data-testid="claim-photo-add">
                    {t("📷 Add photo")}
                    <input type="file" accept="image/*" multiple className="hidden" data-testid="claim-photo-input"
                           onChange={(e) => { const picked = Array.from(e.target.files || []); setClaimFiles([...claimFiles, ...picked].slice(0, 20)); e.target.value = ""; }} />
                  </label>
                )}
              </div>
            </div>
            <div>
              <Label className="text-xs">{t("Videos (optional, up to 5 · 300 MB each)")}</Label>
              <div className="mt-1 space-y-1.5" data-testid="claim-video-list">
                {claimVideos.map((v) => (
                  <div key={v.key} className="flex items-center gap-2 rounded border border-slate-300 bg-slate-50 px-2 py-1 text-xs" data-testid={`claim-video-row-${v.key}`}>
                    <span>🎥</span>
                    <span className="truncate flex-1" title={v.name}>{v.name} <span className="text-slate-500">· {fmtBytes(v.size)}</span></span>
                    {v.error ? <span className="text-rose-700 font-semibold" data-testid="claim-video-error">{v.error}</span>
                      : v.id ? <span className="text-emerald-700 font-semibold" data-testid="claim-video-done">✓ {t("uploaded")}</span>
                      : <span className="w-28 h-2 rounded bg-slate-200 overflow-hidden" data-testid="claim-video-progress"><span className="block h-full bg-rose-500" style={{ width: `${v.progress}%` }} /></span>}
                    <button type="button" onClick={() => removeClaimVideo(v)} className="h-5 w-5 rounded-full bg-red-600 text-white leading-none" title={t("Remove")} data-testid={`claim-video-remove-${v.key}`}>×</button>
                  </div>
                ))}
                {claimVideos.length < 5 && (
                  <label className="inline-flex items-center gap-1 rounded border-2 border-dashed border-slate-300 hover:border-rose-400 px-3 py-1.5 text-xs text-slate-600 cursor-pointer" data-testid="claim-video-add">
                    🎥 {t("Add video")}
                    <input type="file" accept="video/*" multiple className="hidden" data-testid="claim-video-input"
                           onChange={(e) => { addClaimVideos(Array.from(e.target.files || [])); e.target.value = ""; }} />
                  </label>
                )}
                <p className="text-[11px] text-slate-500">{t("Videos play inside the CRM for Back Office and for the factory in China; a copy also goes to the order's Drive folder.")}</p>
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-3 border-t border-slate-200">
            <Button variant="outline" onClick={() => setClaimType(null)} disabled={fileClaim.isPending} data-testid="claim-cancel">{t("Cancel")}</Button>
            <Button data-testid="claim-submit" onClick={() => fileClaim.mutate()} disabled={fileClaim.isPending || claimVideoBusy || !claimNote.trim() || (claimHandling !== "factory" && !claimSolution.trim())}
                    className={claimHandling === "factory" ? "bg-rose-600 hover:bg-rose-700 text-white" : claimHandling === "new_logo" ? "bg-amber-600 hover:bg-amber-700 text-white" : "bg-emerald-600 hover:bg-emerald-700 text-white"}>
              {fileClaim.isPending ? t("Sending…") : claimVideoBusy ? t("Uploading video…") : claimHandling === "factory" ? `⚠️ ${t("File claim")}` : claimHandling === "new_logo" ? `🏷 ${t("File as New logo")}` : `✓ ${t("File as Resolved")}`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* 📷 Bad photos dialog — Pending only */}
      <Dialog open={badPhotosOpen} onOpenChange={(v) => { setBadPhotosOpen(v); if (!v) setBadPhotosNote(""); }}>
        <DialogContent className="max-w-lg" data-testid="bad-photos-dialog">
          <DialogHeader>
            <DialogTitle>📷 {t("Bad photos — send back to Pre Orders")} · {o.po_number}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="rounded-md border border-rose-300 bg-rose-50 p-3 text-xs text-rose-900">
              {t("The order returns to Pre Orders with a 📷 BAD PHOTOS mark and Back Office gets an alert to ask the customer for new photos.")}
            </div>
            <div>
              <Label className="text-xs">{t("What is wrong with the photos?")} <span className="text-red-600">*</span></Label>
              <Textarea
                data-testid="bad-photos-note"
                value={badPhotosNote}
                onChange={(e) => setBadPhotosNote(e.target.value)}
                placeholder={t("e.g. rear caliper photo is blurry, cannot read the brand")}
                className="mt-1"
                rows={4}
              />
              <div className="text-[11px] text-slate-500 mt-1">🌐 {t("Your note is translated automatically (English ⇄ 中文) for the Back Office.")}</div>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-3 border-t border-slate-200">
            <Button variant="outline" onClick={() => setBadPhotosOpen(false)} disabled={badPhotos.isPending} data-testid="bad-photos-cancel">{t("Cancel")}</Button>
            <Button
              data-testid="bad-photos-confirm"
              onClick={() => badPhotos.mutate()}
              disabled={badPhotos.isPending || !badPhotosNote.trim()}
              className="bg-rose-500 hover:bg-rose-600 text-white"
            >
              {badPhotos.isPending ? t("Translating…") : `📷 ${t("Send back — bad photos")}`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={editCodesOpen} onOpenChange={setEditCodesOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Codes · {o.po_number || o.id?.slice(0, 8)}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-sm">
            <div className="rounded-md border-2 p-3 text-xs" style={{ borderColor: "#C30000", background: "#fff5f5", color: "#7c1010" }}>
              Every change is logged with <b>before → after</b> in Approvals &amp; Sign-offs.
            </div>

            <div>
              <div className="text-[11px] uppercase tracking-widest font-mono text-slate-500 mb-2 flex items-center gap-1.5">
                <span className="inline-block w-2 h-2 rounded-full bg-slate-900" /> Factory 1 codes
              </div>
              <div className="grid grid-cols-2 gap-3">
                {[
                  ["front_cover_code", "Front Cover", "edit-code-f1-front"],
                  ["f_clamp_code", "F.Clamp", "edit-code-f1-fclamp"],
                  ["rear_cover_code", "Rear Cover", "edit-code-f1-rear"],
                  ["r_clamp_code", "R.Clamp", "edit-code-f1-rclamp"],
                ].map(([k, lbl, testid]) => (
                  <div key={k}>
                    <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">{t(lbl)}</Label>
                    {k.includes("clamp") ? (
                      <ClampSelect testid={testid} value={codesForm[k] ?? ""} onChange={(v) => setCodesForm((f) => ({ ...f, [k]: v }))} className="mt-1" />
                    ) : (
                      <Input
                        data-testid={testid}
                        value={codesForm[k] ?? ""}
                        onChange={(e) => setCodesForm((f) => ({ ...f, [k]: e.target.value }))}
                        className="mt-1 font-mono"
                      />
                    )}
                    {(o[k] || "") !== (codesForm[k] || "") && (
                      <div className="text-[10px] font-mono text-red-600 mt-0.5">
                        was: <span className="line-through">{o[k] || "—"}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div>
              <div className="text-[11px] uppercase tracking-widest font-mono text-slate-500 mb-2 flex items-center gap-1.5">
                <span className="inline-block w-2 h-2 rounded-full border-2 border-slate-900 bg-white" /> Factory 2 codes
              </div>
              <div className="grid grid-cols-2 gap-3">
                {[
                  ["front_cover_code_f2", "Front Cover", "edit-code-f2-front"],
                  ["f_clamp_code_f2", "F.Clamp", "edit-code-f2-fclamp"],
                  ["rear_cover_code_f2", "Rear Cover", "edit-code-f2-rear"],
                  ["r_clamp_code_f2", "R.Clamp", "edit-code-f2-rclamp"],
                ].map(([k, lbl, testid]) => (
                  <div key={k}>
                    <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">{t(lbl)}</Label>
                    {k.includes("clamp") ? (
                      <ClampSelect testid={testid} value={codesForm[k] ?? ""} onChange={(v) => setCodesForm((f) => ({ ...f, [k]: v }))} className="mt-1" />
                    ) : (
                      <Input
                        data-testid={testid}
                        value={codesForm[k] ?? ""}
                        onChange={(e) => setCodesForm((f) => ({ ...f, [k]: e.target.value }))}
                        className="mt-1 font-mono"
                      />
                    )}
                    {(o[k] || "") !== (codesForm[k] || "") && (
                      <div className="text-[10px] font-mono text-red-600 mt-0.5">
                        was: <span className="line-through">{o[k] || "—"}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-md border border-emerald-300 bg-emerald-50 p-3" data-testid="edit-codes-catalog-group">
              <div className="text-[11px] uppercase tracking-widest font-mono text-emerald-800 mb-2 flex items-center gap-1.5">
                <span className="inline-block w-2 h-2 rounded-full bg-emerald-600" /> Catalog #
              </div>
              <Input
                data-testid="edit-codes-catalog"
                value={codesForm.catalog_number ?? ""}
                onChange={(e) => setCodesForm((f) => ({ ...f, catalog_number: e.target.value }))}
                className="font-mono bg-white"
                placeholder="Leave as is to re-match from the new codes"
              />
              {codesCatalogDirty ? (
                <div className="text-[10px] font-mono text-red-600 mt-0.5">
                  was: <span className="line-through">{o.catalog_number || "—"}</span>
                </div>
              ) : codesCoverDirty ? (
                <div className="text-[10px] text-emerald-800 mt-1" data-testid="edit-codes-catalog-hint">
                  ⟲ Catalog # will be re-matched from inventory for the new Front / Rear codes on save (type a value to override).
                </div>
              ) : null}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Note <span className="text-red-600">*</span></Label>
                <Input
                  data-testid="edit-codes-reason"
                  value={codesReason}
                  onChange={(e) => setCodesReason(e.target.value)}
                  placeholder="Why are the codes changing? (required)"
                />
              </div>
              <div>
                <Label className="text-xs">Signature <span className="text-red-600">*</span></Label>
                <Input
                  data-testid="edit-codes-signature"
                  value={codesSignature}
                  onChange={(e) => setCodesSignature(e.target.value)}
                  placeholder="Type your name to sign"
                  className="font-semibold"
                />
                <div className="text-[10px] text-slate-500 mt-0.5" data-testid="edit-codes-signed-as">Logged in as {mySignature}</div>
              </div>
            </div>
            {codesDirty && !codesCanSave && (
              <div className="text-[11px] text-amber-700" data-testid="edit-codes-missing">Add a note and signature to save the change.</div>
            )}
          </div>
          <div className="flex justify-end gap-2 pt-3 border-t border-slate-200">
            <Button variant="outline" onClick={() => setEditCodesOpen(false)} disabled={saveCodes.isPending} data-testid="edit-codes-cancel">Cancel</Button>
            <Button
              data-testid="edit-codes-save"
              onClick={() => saveCodes.mutate()}
              disabled={saveCodes.isPending || !codesCanSave}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              {saveCodes.isPending ? "Saving…" : "✓ Save Codes & Sign Off"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Recheck CODES dialog — sends the order BACK TO PENDING so the factory
          re-enters its codes (any factory, BO, or SA can trigger). */}
      <Dialog open={recheckOpen} onOpenChange={setRecheckOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>↩ Recheck CODES{o.recheck_attempt ? ` · attempt #${(o.recheck_attempt || 0) + 1}` : ""}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-slate-600">
              This will send the order back to <b>Pending Orders</b> so the factory can re-enter and re-submit the cover / clamp codes.
              The row will show "↩ Recheck codes · your reason" until the codes are submitted to Advance again.
            </p>
            <div>
              <Label className="text-xs">Reason (recommended)</Label>
              <Input
                data-testid="recheck-reason"
                value={recheckReason}
                onChange={(e) => setRecheckReason(e.target.value)}
                placeholder="e.g. Codes may not match — Factory 1 flagged"
              />
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded-md p-3 text-xs" data-testid="recheck-signed-as">
              <span className="text-slate-500 uppercase tracking-widest font-mono text-[10px]">Signed as</span>
              <div className="font-semibold text-slate-900 mt-0.5">{mySignature}</div>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-3 border-t border-slate-200">
            <Button variant="outline" onClick={() => setRecheckOpen(false)} disabled={recheckCodes.isPending} data-testid="recheck-cancel">Cancel</Button>
            <Button
              data-testid="recheck-confirm"
              onClick={() => recheckCodes.mutate()}
              disabled={recheckCodes.isPending}
              className="bg-sky-600 hover:bg-sky-700 text-white"
            >
              {recheckCodes.isPending ? "Sending…" : "↩ Send back to Pending"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Production delay banner — shows when a delay was reported */}
      {isFactory && o.status === "production" && o.production_estimated_completion && (
        <section className="border border-red-300 bg-red-50 rounded-md p-4" data-testid="delay-banner">
          <div className="text-[11px] uppercase tracking-widest font-mono text-red-700">Delay reported</div>
          <div className="text-sm text-red-900 mt-1">
            New ETA <span className="font-mono font-semibold">{o.production_estimated_completion}</span>
            {o.production_delay_reason && <> · {o.production_delay_reason}</>}
          </div>
          <div className="text-xs text-red-700 mt-1 font-mono">
            reported by {o.production_delay_reported_by} · {o.production_delay_reported_at?.slice(0, 19).replace("T", " ")}
          </div>
        </section>
      )}

      {/* To Print CTA — signed but the production sheet is not printed yet */}
      {isFactory && o.status === "production" && productionSubstage(o) === "to_print" && (
        <section className="border-2 border-violet-500 bg-violet-50 rounded-md p-6" data-testid="production-print-cta">
          <div className="flex items-start justify-between gap-6 flex-wrap">
            <div className="flex-1 min-w-[240px]">
              <div className="text-[11px] uppercase tracking-widest font-mono text-violet-700 mb-1">{t("To Print")}</div>
              <h2 className="font-display text-2xl font-bold text-violet-900">{t("Signed — now print the production sheet")}</h2>
              <p className="text-sm text-violet-800 mt-1">{t("Signed as")}: <b>{o.factory_signature}</b> · {o.factory_signed_at?.slice(0, 19).replace("T", " ")}</p>
            </div>
            <Button
              data-testid="production-print"
              onClick={() => {
                if (!printProductionSheet({ orders: [o], user, lang, t })) { toast.error("Popup blocked — allow popups to print"); return; }
                api.post("/orders/mark-printed", { ids: [o.id] }).then(() => { qc.invalidateQueries(); toast.success(t("Production sheet printed")); });
              }}
              className="h-11 bg-violet-600 hover:bg-violet-700 text-white font-semibold px-6"
            >
              🖨 {t("Print")}
            </Button>
          </div>
        </section>
      )}

      {/* Production Complete CTA — Factory 1 only, when signed + printed but not yet completed */}
      {isFactory && o.status === "production" && productionSubstage(o) === "in_production" && (
        <section className="border-2 border-blue-500 bg-blue-50 rounded-md p-6" data-testid="production-complete-cta">
          <div className="flex items-start justify-between gap-6 flex-wrap">
            <div className="flex-1 min-w-[240px]">
              <div className="text-[11px] uppercase tracking-widest font-mono text-blue-700 mb-1">Ready to close production</div>
              <h2 className="font-display text-2xl font-bold text-blue-900">{t("Sign Off — Production Complete")}</h2>
              <p className="text-sm text-blue-800 mt-1">
                Confirm production of {o.po_number} is finished and ready for Product Transfer. Your sign-off name and timestamp will be recorded on the order.
              </p>
              <div className="mt-3 flex flex-wrap gap-3 text-xs font-mono">
                <span className="px-2 py-0.5 rounded bg-white border border-blue-200 text-blue-800">
                  Started by <span className="font-semibold">{o.factory_signature}</span>
                </span>
                <span className="px-2 py-0.5 rounded bg-white border border-blue-200 text-blue-800">
                  {o.factory_signed_at?.slice(0, 19).replace("T", " ")}
                </span>
              </div>
            </div>
            <div className="flex flex-col gap-2 min-w-[260px]">
              <div
                className="h-11 rounded-md border border-blue-300 bg-white flex items-center px-3 text-base font-mono text-slate-800"
                data-testid="complete-signature"
              >
                <span className="text-slate-400 text-[11px] uppercase tracking-widest mr-2">Signed as</span>
                <span className="font-semibold">{mySignature}</span>
              </div>
              <Button
                data-testid="production-complete"
                onClick={() => productionComplete.mutate()}
                disabled={!!o.qc_hold}
                title={o.qc_hold ? t("Resolve the Quality Control issue before completing production.") : undefined}
                className="h-11 bg-blue-600 hover:bg-blue-700 text-white font-semibold"
              >
                ✓ {t("Sign Off & Transfer")}
              </Button>
              <ExtensionRequestButton order={o} size="default" className="h-10" />
            </div>
          </div>
        </section>
      )}

      {/* Production Start CTA — Factory 1 only, when order in production but not yet signed */}
      {isFactory && o.status === "production" && !o.factory_signature && (
        <section className="border-2 border-emerald-500 bg-emerald-50 rounded-md p-6" data-testid="production-start-cta">
          <div className="flex items-start justify-between gap-6 flex-wrap">
            <div className="flex-1 min-w-[240px]">
              <div className="text-[11px] uppercase tracking-widest font-mono text-emerald-700 mb-1">Awaiting your approval</div>
              <h2 className="font-display text-2xl font-bold text-emerald-900">{t("Sign & Start Production")}</h2>
              <p className="text-sm text-emerald-800 mt-1">
                Confirm codes and sign to authorize production of {o.po_number}. Date &amp; time will be stamped automatically.
              </p>
              <div className="mt-3 flex flex-wrap gap-2 text-xs font-mono">
                <span className={`px-2 py-0.5 rounded bg-white border ${String(o.front_cover_code || "").toUpperCase() === "FF" ? "border-blue-300 text-blue-800" : "border-emerald-200 text-emerald-800"}`} data-testid="prod-panel-front-code">{t("Front Cover")}: {displayCode(o.front_cover_code, "front", lang) || "—"}</span>
                <span className="px-2 py-0.5 rounded bg-white border border-emerald-200 text-emerald-800">{t("F-Clamp")}: {o.f_clamp_code || "—"}</span>
                <span className={`px-2 py-0.5 rounded bg-white border ${String(o.rear_cover_code || "").toUpperCase() === "RR" ? "border-blue-300 text-blue-800" : "border-emerald-200 text-emerald-800"}`} data-testid="prod-panel-rear-code">{t("Rear Cover")}: {displayCode(o.rear_cover_code, "rear", lang) || "—"}</span>
                <span className="px-2 py-0.5 rounded bg-white border border-emerald-200 text-emerald-800">{t("R-Clamp")}: {o.r_clamp_code || "—"}</span>
                {(String(o.front_cover_code || "").toUpperCase() === "FF" || String(o.rear_cover_code || "").toUpperCase() === "RR") && (
                  <span className="px-2 py-0.5 rounded bg-blue-100 border border-blue-300 text-blue-900 font-semibold" data-testid="single-side-tag">
                    ℹ️ {t("Single-side order")}
                    {String(o.front_cover_code || "").toUpperCase() === "FF" ? ` · ${t("no front")}` : ""}
                    {String(o.rear_cover_code || "").toUpperCase() === "RR" ? ` · ${t("no rear")}` : ""}
                  </span>
                )}
              </div>
            </div>
            <div className="flex flex-col gap-2 min-w-[260px]">
              <div
                className="h-11 rounded-md border border-emerald-300 bg-white flex items-center px-3 text-base font-mono text-slate-800"
                data-testid="factory-signature"
              >
                <span className="text-slate-400 text-[11px] uppercase tracking-widest mr-2">Signed as</span>
                <span className="font-semibold">{mySignature}</span>
              </div>
              <Button
                data-testid="factory-sign-btn"
                onClick={() => factorySign.mutate()}
                className="h-11 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold"
              >
                ✍ {t("Sign & Start Production")}
              </Button>
              {/* Second copy of Return-to-Advance sitting inside the sign CTA
                  so the assigned factory sees it right where the sign button
                  is — no scrolling required. Hidden the moment work is
                  signed off (same rule as the header button). */}
              {!o.claim_id && !o.is_stock_unit && (
              <Button
                data-testid="factory-return-to-advance"
                variant="outline"
                className="h-10 border-amber-500 text-amber-700 hover:bg-amber-50"
                title="Send this order back to Advance Orders (before signing to start)"
                onClick={() => {
                  const reason = window.prompt(
                    t("Reason for sending this order back? (optional)"),
                    "",
                  );
                  if (reason === null) return;
                  api.post(`/orders/${id}/transition`, {
                    target_stage: "advance",
                    reason: reason || "Returned to Advance from Production (pre-sign)",
                  })
                    .then(() => {
                      qc.invalidateQueries();
                      toast.success(`${o.po_number} returned to Advance`);
                      goBackToList();
                    })
                    .catch((e) => toast.error(e?.response?.data?.detail || "Could not return to Advance"));
                }}
              >
                ↩ {t("Return")} {t("Advance Orders")}
              </Button>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Inventory match panel (back office / super admin only, advance + flagged) */}
      {isBackOffice && invMatch && ["advance", "flagged"].includes(o.status) && (
        <section className="bg-white border border-slate-200 rounded-md p-5" data-testid="inventory-match-panel">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-[11px] uppercase tracking-widest font-mono text-slate-500">Inventory cross-reference</div>
              <h2 className="font-display text-lg font-semibold">
                {invMatch.spec_mismatch ? `Catalog spec mismatch — check ${invMatch.spec_mismatch.items.map((i) => i.label.toLowerCase()).join(" / ")} · ${invMatch.spec_mismatch.cat_number}${invMatch.spec_mismatch.acknowledged ? " — signed off" : ""}` :
                 invMatch.match && (o.catalog_number || "").trim() ? `Catalog: ${invMatch.match.cat_number}${invMatch.auto_assigned ? " — assigned automatically" : ""}` :
                 invMatch.match ? `Proposed: ${invMatch.match.cat_number} — approve to assign` :
                 invMatch.reason === "vehicle_mismatch" ? `Vehicle mismatch — ${invMatch.vehicle?.cat_number || ""} needs approval` :
                 invMatch.reason === "multi_option_codes" ? "Multi-option match — flagged" :
                 invMatch.reason === "no_inventory" ? "No matching inventory" :
                 invMatch.reason === "missing_vehicle" ? "Car info missing — brand / model / year needed" :
                 invMatch.reason === "missing_codes" ? "Awaiting Factory codes" : "—"}
              </h2>
            </div>
            <Badge className={invMatch.spec_mismatch?.acknowledged ? "bg-amber-100 text-amber-900 border-amber-400" : invMatch.spec_mismatch ? "bg-red-600 text-white border-red-700" : invMatch.flagged ? "bg-red-50 text-red-700 border-red-200" : invMatch.match && (o.catalog_number || "").trim() ? "bg-emerald-50 text-emerald-700 border-emerald-200" : invMatch.match ? "bg-amber-50 text-amber-800 border-amber-300" : "bg-amber-50 text-amber-700 border-amber-200"} data-testid="inventory-match-badge">
              {invMatch.spec_mismatch?.acknowledged ? "SIGNED OFF" : invMatch.spec_mismatch ? "SPEC ALERT" : invMatch.flagged ? "FLAGGED" : invMatch.match && (o.catalog_number || "").trim() ? "READY" : invMatch.match ? "PROPOSED" : "REVIEW"}
            </Badge>
          </div>

          {invMatch.spec_mismatch && (
            <div className={`mb-3 rounded-md border-2 p-3 text-sm ${invMatch.spec_mismatch.acknowledged ? "border-amber-400 bg-amber-50 text-amber-950" : "border-red-600 bg-red-50 text-red-900"}`} data-testid="inventory-spec-alert">
              <div className={`font-black uppercase tracking-widest text-[11px] ${invMatch.spec_mismatch.acknowledged ? "text-amber-800" : "text-red-700"}`}>
                {invMatch.spec_mismatch.acknowledged ? "⚠" : "⛔"} Order info does not match catalog CAT # {invMatch.spec_mismatch.cat_number}
              </div>
              <div className="mt-1.5 flex flex-wrap gap-2 text-xs font-mono">
                {invMatch.spec_mismatch.items.map((i) => (
                  <span key={i.field} className={`px-2 py-1 rounded bg-white border text-slate-800 ${invMatch.spec_mismatch.acknowledged ? "border-amber-300" : "border-red-300"}`} data-testid={`inventory-spec-alert-${i.field}`}>
                    {i.label}: order <b className="text-red-700">{i.order_value || "—"}</b> · catalog <b className="text-emerald-700">{i.catalog_value}</b>
                  </span>
                ))}
              </div>
              <div className="mt-1.5" data-testid="inventory-spec-alert-hint">
                {invMatch.spec_mismatch.acknowledged
                  ? <>Approved with a signature by <b>{invMatch.spec_mismatch.acknowledged_by || "—"}</b>{invMatch.spec_mismatch.acknowledged_at ? ` · ${new Date(invMatch.spec_mismatch.acknowledged_at).toLocaleString()}` : ""} — the CAT # stays on the order. Repairing the order info (rim size / spacers) clears this note.</>
                  : <>Alert only — {(o.catalog_number || "").trim() ? "the CAT # stays on the order." : "the CAT # is not assigned automatically."} Repair the order info (rim size / spacers with the customer's photos) or approve the CAT # with your signature.</>}
              </div>
            </div>
          )}

          {invMatch.match && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm" data-testid="inventory-match-single">
              <Field label="Catalog #" value={invMatch.match.cat_number} />
              <Field label="Color" value={invMatch.match.color} />
              <Field label="Min Rim" value={`${invMatch.match.min_rim_size}"`} />
              <Field label="Warehouse" value={invMatch.match.warehouse} />
              <Field label="Front / F-Clamp" value={`${invMatch.match.front_cover_code} / ${invMatch.match.f_clamp_code}`} />
              <Field label="Rear / R-Clamp" value={`${invMatch.match.rear_cover_code} / ${invMatch.match.r_clamp_code}`} />
              <Field label="Spacers" value={`${invMatch.match.spacers || "—"}${invMatch.match.spacers_qty ? ` × ${invMatch.match.spacers_qty}` : ""}`} />
              <Field label="Available qty" value={String(invMatch.match.qty)} />
              <Field label="Vehicle fit" value={invMatch.vehicle ? (invMatch.vehicle.ok ? `✓ ${invMatch.vehicle.catalog_all || [invMatch.match.brand, invMatch.match.model, invMatch.match.year].filter(Boolean).join(" ")}` : "⚠ different vehicle (approved)") : "— no vehicle info on catalog"} />
            </div>
          )}
          {!invMatch.match && invMatch.reason === "vehicle_mismatch" && invMatch.vehicle && (
            <div className="mb-2 text-sm text-amber-900" data-testid="inventory-vehicle-mismatch-hint">
              Cover codes + colour match CAT # <b className="font-mono">{invMatch.vehicle.cat_number}</b>, but it is for{" "}
              <b>{invMatch.vehicle.catalog_all || [invMatch.vehicle.catalog?.brand, invMatch.vehicle.catalog?.model, invMatch.vehicle.catalog?.year].filter(Boolean).join(" ") || "—"}</b> and this order is{" "}
              <b>{[o.brand, o.model, o.year].filter(Boolean).join(" ") || "—"}</b>. Approve it with a signature in the Inventory availability box above, or assign another CAT #.
            </div>
          )}

          {!invMatch.match && invMatch.reason !== "vehicle_mismatch" && (invMatch.matches || []).length > 0 && (
            <div className="mt-2">
              <p className="text-sm text-slate-600 mb-2">
                {invMatch.reason === "multi_option_codes" && "Multiple catalog options share the same Front/Rear codes — auto-flagged for manual review."}
              </p>
              <table className="w-full text-sm border border-slate-200 rounded">
                <thead className="bg-slate-50">
                  <tr>{["Catalog #", "Min Rim", "Color", "Qty", "Warehouse"].map(h => <th key={h} className="px-3 py-2 text-left text-[10px] font-semibold text-slate-500 uppercase tracking-wider">{h}</th>)}</tr>
                </thead>
                <tbody>
                  {invMatch.matches.map((m) => (
                    <tr key={m.id} className="border-t border-slate-100">
                      <td className="px-3 py-2 font-mono font-semibold">{m.cat_number}</td>
                      <td className="px-3 py-2">{m.min_rim_size}&quot;</td>
                      <td className="px-3 py-2">{m.color}</td>
                      <td className="px-3 py-2 font-semibold">{m.qty}</td>
                      <td className="px-3 py-2 capitalize">{m.warehouse}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {!invMatch.match && (invMatch.matches || []).length === 0 && !(o.catalog_number || "").trim() && !(invMatch.suggestions || []).length && (
            <div data-testid="inventory-match-empty">
              <p className="text-sm text-slate-500 mb-3">
                {invMatch.reason === "missing_codes" ? "Front/Rear cover codes or color are missing on this order." : "No inventory matches the current codes + color. Create a new catalog item before moving to Production."}
              </p>
              {invMatch.reason !== "missing_codes" && (
                <AddInventoryQuickCreate order={o} onCreated={() => qc.invalidateQueries()} />
              )}
            </div>
          )}
        </section>
      )}

      {/* Photo Reminder status strip — visible for pre_order/lead/abandoned so
          Back Office can see how many nudges have been sent + when the next
          one is due. Manual sends now happen from the header "📧 Send Email"
          button (unified with the Template Library). */}
      {isBackOffice && ["pre_order", "lead", "abandoned"].includes(o.status) && !o.photos_received && (
        <div className="flex flex-wrap items-stretch gap-3" data-testid="waiting-photos-row">
          <div className="flex-1 min-w-[260px]"><PhotoReminderStrip o={o} /></div>
          <div className="flex items-center rounded-md border border-sky-200 bg-sky-50 px-4 py-3" data-testid="waiting-photos-upload">
            <div className="mr-4">
              <div className="text-[11px] uppercase tracking-widest font-mono text-slate-500">Got the photos by email?</div>
              <div className="text-sm text-slate-700">Upload them here → saved to Drive › <b className="font-mono">{o.po_number}</b> and marked Photos Received.</div>
            </div>
            <PreorderPhotoUploadButton order={o} />
          </div>
        </div>
      )}

      {/* Photo Review — 4-checkbox panel is Pre-Order-only per user request.
          Reviewer ticks the items that ARE present (VIN photo, Rim size,
          Front caliper, Rear caliper). Anything unchecked triggers a Missing
          Info email and flags the order "Waiting Photos" until new files land
          or VIN/rim is manually filled in. */}
      {isBackOffice && o.status === "pre_order" && (o.photos_received || o.drive_folder_id) && (
        <PhotoReviewPanel order={o} />
      )}

      {/* Host order: this box also carries clamps for a warranty — visible at every station. */}
      {(o.carries || []).some((c) => !c.removed) && (
        <CarriesBanner order={o} mySignature={mySignature} canRemove={isBackOffice || ["packing_orders", "inventory_packing", "three_pl", "shipping_order"].includes(user.role)} />
      )}

      {/* ✉️ customer journey e-mails — 7 steps, sent / opened, Send-now (BO / SA) — below the header, above the tabs */}
      {!o.is_stock_unit && isBackOffice && <JourneyTimeline orderId={o.id} user={user} />}

      {/* Tabs — the detail view always opens on Order Info first, per user
          preference. If the user manually switches to another tab (e.g. Packing
          Checklist), `activeTab` overrides the default. */}
      <Tabs
        value={activeTab || (pickupPending(o) && !isBackOffice ? "packing" : "order_info")}
        onValueChange={(v) => {
          if (editMode) {
            update.mutate(form);
            setEditMode(false);
          }
          setActiveTab(v);
        }}
        className="bg-white border border-slate-200 rounded-md"
      >
        <TabsList className="border-b border-slate-200 px-3 rounded-none bg-white">
          {isBackOffice && <TabsTrigger value="customer" data-testid="tab-customer">{t("Customer")}</TabsTrigger>}
          {isBackOffice && !o.is_stock_unit && <TabsTrigger value="shopify" data-testid="tab-shopify">Shopify</TabsTrigger>}
          <TabsTrigger value="order_info" data-testid="tab-orderinfo">{t("Order Info")}</TabsTrigger>
          <TabsTrigger value="drive" data-testid="tab-drive">{t("Photos")}</TabsTrigger>
          <TabsTrigger value="notes" data-testid="tab-notes">{t("Notes")}</TabsTrigger>
          {isBackOffice && (
            <TabsTrigger value="emails" data-testid="tab-emails">{t("📧 Emails")}</TabsTrigger>
          )}
          {isBackOffice && (
            <TabsTrigger value="history" data-testid="tab-history">{t("Change History")}</TabsTrigger>
          )}
          {(isPacking || isBackOffice || user.role === "inventory_packing") && (o.status === "packing" || o.status === "inventory_packing" || (o.status === "shipping" && (o.packed_at || o.inventory_packed_at))) && (
            <TabsTrigger value="packing" data-testid="tab-packing">{t("Packing Checklist")}</TabsTrigger>
          )}
        </TabsList>

        {isBackOffice && (
        <TabsContent value="customer" className="p-5">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <Field label="Name" value={o.customer?.name} />
            <Field label="Email" value={o.customer?.email} />
            <Field label="Phone" value={o.customer?.phone} />
            <Field label="Shopify ID" value={o.customer?.shopify_id} />
          </div>
          <div className="mt-4 flex items-center gap-3">
            <EditContactButton order={o} testid="edit-contact" />
            <span className="text-xs text-slate-500">{t("Wrong e-mail or phone vs. Shopify? Fix it here — applies to this order only.")}</span>
          </div>
          {o.customer?.logo_url && <img src={o.customer.logo_url} alt="logo" className="mt-4 w-16 h-16 border border-slate-200 rounded p-1" />}
        </TabsContent>
        )}

        <TabsContent value="order_info" className="p-5">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <p className="text-sm text-slate-600" data-testid="order-info-hint">
              Vehicle details, codes, and catalog assignment.
            </p>
            {(isBackOffice || user.role === "super_admin") && ["lead", "pre_order", "pending"].includes(o.status) && (() => {
              // Compute VIN validity from the live form/order so the button reflects
              // its own smart mode: 17 chars → decode from VIN line; else photos.
              const typedVin = String(form.vin || o.vin || "").trim().toUpperCase();
              const vinValid17 = typedVin.length === 17;
              const vinWrong = typedVin.length > 0 && !vinValid17;
              const canUsePhotos = !!o.drive_folder_id;
              const canRun = vinValid17 || (typedVin.length === 0 && canUsePhotos);
              const hint = vinValid17
                ? "Decode from the VIN line (NHTSA, instant, no photos needed)"
                : vinWrong
                  ? `Wrong VIN (${typedVin.length}/17 chars). Fix or clear to decode from photos.`
                  : canUsePhotos
                    ? "Read VIN + rim from Drive photos, then decode via NHTSA"
                    : "Type a 17-character VIN, or link a Drive folder with photos";
              return (
                <div className="flex items-center gap-2">
                  {vinWrong && (
                    <span
                      className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1 font-mono"
                      data-testid="vin-wrong-hint"
                      title={hint}
                    >
                      ⚠ Wrong VIN · {typedVin.length}/17
                    </span>
                  )}
                  <Button
                    data-testid="jetform-mock-orderinfo"
                    onClick={() => jetForm.mutate()}
                    disabled={jetForm.isPending || !canRun}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white disabled:opacity-60"
                    size="sm"
                    title={hint}
                  >
                    🤖 {jetForm.isPending ? "Decoding…" : "VIN Decoder"}
                  </Button>
                  <Button
                    data-testid="rim-scanner-btn"
                    onClick={() => setRimCaptureOpen(true)}
                    className="bg-sky-600 hover:bg-sky-700 text-white disabled:opacity-60"
                    size="sm"
                    title="Click any Drive photo — AI auto-detects the black tire sidewall and reads the rim size"
                  >
                    📐 Scan Rim Size
                  </Button>
                </div>
              );
            })()}
          </div>
          {/* Layout uses a 12-col grid so all fields align on 2 tidy rows:
              Row 1 (4 fields × col-span-3):  Brand · Model · Year · Trim
              Row 2 (6 fields × col-span-2):  VIN · Rim Size · Color · Logo · Spacer · Spacer Qty
              (+ Catalog # inline when it becomes relevant, from Production onward) */}
          <div className="grid grid-cols-2 md:grid-cols-12 gap-4">
            {(() => {
              const baseFields = [
                ["brand", "Brand"], ["model", "Model"], ["year", "Year"], ["trim", "Trim"],
                ["vin", "VIN"], ["rim_size", "Rim Size"], ["color", "Color"], ["logo", "Logo"],
                ["spacer", "Spacer"], ["spacer_qty", "Spacer Qty"],
              ];
              const catalogField = [["catalog_number", "Catalog #"]];
              let fields = [...baseFields];
              // Catalog # is now rendered inside the codes row (see below) —
              // on the LEFT of the Front Cover band — so we intentionally
              // DON'T add it to this main fields grid.
              return fields;
            })().map(([k, label]) => {
              const hasSaved = k === "spacer_qty" ? !!o.spacer_qty : !!o[k];
              const readOnly = lockedFor(k) || (!editMode && hasSaved);
              // Row-2 fields (VIN → Spacer Qty) span 2/12; Row-1 fields (Brand
              // → Trim) and Catalog # span 3/12.
              const isRow2 = ["vin", "rim_size", "color", "logo", "spacer", "spacer_qty"].includes(k);
              const colSpan = isRow2 ? "md:col-span-2" : "md:col-span-3";
              // Spacer & Spacer Qty are fixed-vocabulary dropdowns. They stay
              // editable through the whole lifecycle so Back Office can
              // update them at any time; both values are surfaced in the
              // Packing / Inventory Packing checklists as "Spacer Type × Qty".
              if (k === "spacer") {
                const SPACER_OPTIONS = ["No spacer", "3mm", "5mm", "S-3mm", "S-5mm"];
                return (
                  <div key={k} className={colSpan}>
                    <Label className="text-xs">{t(label)}</Label>
                    <Select
                      value={form.spacer || ""}
                      onValueChange={(v) => {
                        setForm({ ...form, spacer: v });
                        if (v !== o.spacer) update.mutate({ spacer: v });
                      }}
                    >
                      <SelectTrigger data-testid="field-spacer" className="mt-1 font-mono text-sm">
                        <SelectValue placeholder="Pick spacer…" />
                      </SelectTrigger>
                      <SelectContent>
                        {SPACER_OPTIONS.map((opt) => (
                          <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                );
              }
              if (k === "spacer_qty") {
                const QTY_OPTIONS = ["2", "4"];
                return (
                  <div key={k} className={colSpan}>
                    <Label className="text-xs">{t(label)}</Label>
                    <Select
                      value={form.spacer_qty || ""}
                      onValueChange={(v) => {
                        setForm({ ...form, spacer_qty: v });
                        const num = parseInt(v, 10);
                        if (num !== o.spacer_qty) update.mutate({ spacer_qty: num });
                      }}
                    >
                      <SelectTrigger data-testid="field-spacer-qty" className="mt-1 font-mono text-sm">
                        <SelectValue placeholder="Qty…" />
                      </SelectTrigger>
                      <SelectContent>
                        {QTY_OPTIONS.map((opt) => (
                          <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                );
              }
              return (
                <div key={k} className={colSpan}>
                  <Label className="text-xs inline-flex items-center gap-1.5">
                    {t(label)}
                    {k === "color" && <CustomColorNote ec={effectiveColor(o)} testid="order-info-custom-color-note" />}
                  </Label>
                  <Input
                    data-testid={`field-${k}`}
                    value={readOnly ? (k === "color" ? effectiveColor(o).color : (o[k] || "")) : (form[k] || "")}
                    onChange={(e) => {
                      let v = e.target.value;
                      // VIN normalization: uppercase + swap look-alikes
                      // (letters I/O/Q are illegal in VINs).
                      if (k === "vin") v = v.toUpperCase().replace(/O/g, "0").replace(/I/g, "1").replace(/Q/g, "0");
                      setForm({ ...form, [k]: v });
                    }}
                    onBlur={() => { if (!lockedFor(k) && form[k] !== undefined && form[k] !== o[k]) { update.mutate({ [k]: form[k] }); } }}
                    className={`mt-1 font-mono text-sm ${k === "vin" ? "vin-glyphs" : ""} ${readOnly ? "bg-slate-50 cursor-not-allowed" : ""}`}
                    readOnly={readOnly}
                    maxLength={k === "vin" ? 17 : undefined}
                  />
                  {k === "vin" && !readOnly && <VinHint value={form[k] ?? o[k]} />}
                </div>
              );
            })}
          </div>

          {/* ---- Cover + Clamp row (Factory 1 inputs — Pending onward) ---- */}
          {/* Layout: 1 row × 2 grouped bands. */}
          {/*   Left band (blue outline)   → Front Cover + F-Clamp side by side */}
          {/*   Right band (yellow outline) → Rear Cover + R-Clamp side by side */}
          {/* One glance tells Factory 1 which side each code belongs to. */}
          {/* Both clamp fields are a fixed-vocabulary <select> to prevent typos. */}
          {o.status !== "pre_order" && (() => {
            const BAND = {
              front: "bg-sky-50 border border-sky-200 rounded-md p-3",
              rear: "bg-amber-50 border border-amber-200 rounded-md p-3",
            };
            const renderCover = (k, label) => {
              const hasSaved = !!o[k];
              let readOnly = lockedFor(k) || (!editMode && hasSaved);
              if (o.status !== "pending") {
                return (
                  <div>
                    <Label className="text-xs">{t(label)}</Label>
                    <Input
                      data-testid={`field-${k}`}
                      value={readOnly ? displayCode(o[k], k === "front_cover_code" ? "front" : "rear", lang) : (form[k] || "")}
                      onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                      onBlur={() => { if (form[k] !== undefined && form[k] !== o[k]) codeAutoSave(k, form[k]); }}
                      className={`mt-1 font-mono text-sm bg-white ${readOnly ? "bg-slate-50 cursor-not-allowed" : ""}`}
                      readOnly={readOnly}
                    />
                  </div>
                );
              }
              // Pending Orders only: Brand dropdown + Model # input, saved combined ("AC 15")
              // so the catalog scan / auto-fill keeps matching on the full code.
              const side = k === "front_cover_code" ? "front" : "rear";
              const skip = SKIP_CODE[side];
              // An incomplete saved code (brand only / number only) stays editable so it can be finished.
              readOnly = lockedFor(k) || (!editMode && hasSaved && coverCodeComplete(o[k], side));
              const current = readOnly ? (o[k] || "") : (form[k] ?? o[k] ?? "");
              const { brand, num } = splitCoverCode(current);
              const isSkip = brand.toUpperCase() === skip;
              const isNa = brand.toUpperCase() === NA_CODE;
              const legacyBrand = brand && !COVER_BRAND_CODES.includes(brand.toUpperCase()) && !isSkip && !isNa ? brand : null;
              const commit = (b, n) => {
                const v = joinCoverCode(b, n);
                setForm({ ...form, [k]: v });
                return v;
              };
              const selectCls = `mt-1 font-mono text-sm w-full border border-slate-300 rounded-md h-9 px-2 bg-white ${readOnly ? "bg-slate-50 cursor-not-allowed" : ""}`;
              return (
                <div>
                  <Label className="text-xs">{t(label)}</Label>
                  <div className="grid grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)] gap-1.5">
                    <select
                      data-testid={`field-${k}-brand`}
                      value={isSkip ? skip : isNa ? NA_CODE : (brand || "")}
                      disabled={readOnly || codeNa.isPending}
                      onChange={(e) => {
                        const b = e.target.value;
                        if (b === NA_CODE) { codeNa.mutate(side); return; }
                        const v = commit(b, b === skip ? "" : num);
                        if (v !== (o[k] || "") && (b === skip || !b || num)) codeAutoSave(k, v);
                      }}
                      className={selectCls}
                      title={t("Brand code")}
                    >
                      <option value="">{t("— brand —")}</option>
                      {COVER_BRANDS.map((b) => (
                        <option key={b.code} value={b.code}>{brandLabel(b, side, lang)}</option>
                      ))}
                      <option value={skip}>{skip} · {t(side === "front" ? "no front" : "no rear")}</option>
                      <option value={NA_CODE} data-testid={`field-${k}-brand-na`}>{NA_CODE} · {t("not available")}</option>
                      {legacyBrand && <option value={legacyBrand}>{legacyBrand} (legacy)</option>}
                    </select>
                    <Input
                      data-testid={`field-${k}-num`}
                      value={isSkip || isNa ? "" : num}
                      placeholder={t("Model #")}
                      disabled={isSkip || isNa}
                      onChange={(e) => commit(brand, e.target.value)}
                      onBlur={() => { if (form[k] !== undefined && form[k] !== (o[k] || "")) codeAutoSave(k, form[k]); }}
                      className={`mt-1 font-mono text-sm bg-white ${readOnly || isSkip || isNa ? "bg-slate-50 cursor-not-allowed" : ""}`}
                      readOnly={readOnly}
                    />
                  </div>
                </div>
              );
            };
            const renderClamp = (k, label) => {
              const hasSaved = !!o[k];
              const readOnly = lockedFor(k) || (!editMode && hasSaved);
              const currentValue = readOnly ? (o[k] || "") : (form[k] || "");
              return (
                <div>
                  <Label className="text-xs">{t(label)}</Label>
                  <select
                    data-testid={`field-${k}`}
                    value={currentValue}
                    disabled={readOnly}
                    onChange={(e) => {
                      const val = e.target.value;
                      setForm({ ...form, [k]: val });
                      // Save immediately on change — a dropdown has no blur pattern
                      if (val !== o[k]) codeAutoSave(k, val);
                    }}
                    className={`mt-1 font-mono text-sm w-full border border-slate-300 rounded-md h-9 px-2 bg-white ${readOnly ? "bg-slate-50 cursor-not-allowed" : ""}`}
                  >
                    <option value="">{t("— select —")}</option>
                    {CLAMP_CODES.map((opt) => (
                      <option key={opt} value={opt}>{clampLabel(opt, lang)}</option>
                    ))}
                    {/* Preserve any legacy value that isn't in the canonical list */}
                    {currentValue && !CLAMP_CODES.includes(currentValue) && (
                      <option value={currentValue}>{currentValue} (legacy)</option>
                    )}
                  </select>
                </div>
              );
            };
            return (
              <div className="mt-6" data-testid="cover-clamp-block">
              {isBackOffice && (
                <div className="flex items-center justify-between gap-2 mb-2 flex-wrap" data-testid="codes-change-bar">
                  <div className="text-[11px] text-slate-500" data-testid="codes-last-changed">
                    {editMode && !PRE_PRODUCTION.includes(o.status) && (
                      <span className="text-amber-700 mr-2" data-testid="codes-signed-hint">✎ Code changes will ask for a note + signature on Save.</span>
                    )}
                    {o.codes_changed_at
                      ? <>✎ Codes changed by <b className="text-slate-800">{o.codes_changed_by}</b> · {String(o.codes_changed_at).slice(0, 10)}{o.codes_changed_note ? <> · <i>“{o.codes_changed_note}”</i></> : null}</>
                      : null}
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    data-testid="order-info-change-codes"
                    onClick={() => setEditCodesOpen(true)}
                    className="h-7 px-2 text-xs border-sky-600 text-sky-800 hover:bg-sky-50"
                    title="Change any code at any stage — requires a note and your signature"
                  >
                    ✎ Change codes / Catalog # (sign + note)
                  </Button>
                </div>
              )}
              <div className="grid grid-cols-1 lg:grid-cols-[minmax(220px,1fr)_2fr_2fr] gap-3" data-testid="cover-clamp-row">
                {/* Catalog # band (emerald outline): sits on the LEFT of the
                    Front Cover / Rear Cover bands so BO/SA can see the assigned
                    catalog number in the same eye-line as the codes that
                    produced it. Auto-fills from inventory when both covers
                    match a row (backend _autofill_catalog_number). Manually
                    editable at pre-production stages (respects catalogLocked). */}
                <div className="bg-emerald-50 border border-emerald-300 rounded-md p-3" data-testid="catalog-group">
                  {(() => {
                    const readOnly = catalogLocked;
                    const val = readOnly ? (o.catalog_number || "") : (form.catalog_number ?? o.catalog_number ?? "");
                    return (
                      <div>
                        <Label className="text-xs flex items-center justify-between">
                          <span>Catalog #</span>
                          {!readOnly && (o.front_cover_code && o.rear_cover_code) && (
                            <button
                              type="button"
                              data-testid="catalog-rescan"
                              onClick={async () => {
                                try {
                                  const r = await api.post(`/orders/${id}/scan-inventory`);
                                  if (r.data?.matched) {
                                    toast.info(r.data.message || `CAT # ${r.data.catalog_number} proposed — approve it in the cross-reference panel`);
                                    qc.invalidateQueries();
                                  } else if (r.data?.reason === "missing_vehicle") {
                                    toast.warning("Order has no brand / model / year — nothing is proposed. Type the CAT # manually.");
                                  } else {
                                    toast.warning("No inventory match — type it in manually.");
                                  }
                                } catch (e) {
                                  toast.error(e?.response?.data?.detail || "Scan failed");
                                }
                              }}
                              className="text-[10px] font-mono uppercase tracking-widest text-emerald-700 hover:text-emerald-900 underline underline-offset-2"
                              title="Re-scan inventory for a matching catalog number"
                            >
                              ⟲ Scan inventory
                            </button>
                          )}
                        </Label>
                        <Input
                          data-testid="field-catalog_number"
                          value={val}
                          onChange={(e) => setForm({ ...form, catalog_number: e.target.value })}
                          onBlur={() => {
                            if (readOnly) return;
                            const v = (form.catalog_number ?? "").trim() || null;
                            if (v === (o.catalog_number || null)) return;
                            if (!PRE_PRODUCTION.includes(o.status)) {
                              setCodesPrefill({ catalog_number: v || "" });
                              setEditCodesOpen(true);
                              toast.info("Catalog # changes after Advance need a note + signature — confirm in the dialog");
                              return;
                            }
                            update.mutate({ catalog_number: v });
                          }}
                          className={`mt-1 font-mono text-sm bg-white ${readOnly ? "bg-slate-50 cursor-not-allowed" : ""}`}
                          readOnly={readOnly}
                          placeholder={readOnly ? "" : "Auto-fills on code save, or type your own"}
                        />
                        <div className="text-[10px] text-emerald-700 mt-1 leading-tight">
                          {o.catalog_number
                            ? "✓ Assigned"
                            : (o.front_cover_code && o.rear_cover_code)
                              ? "Enter codes above to auto-fill · or type below"
                              : "Fill both cover codes to auto-scan inventory"}
                        </div>
                      </div>
                    );
                  })()}
                </div>
                {/* Front group (blue outline): Front Cover + F-Clamp share one banded container.
                    Hidden when the customer bought the REAR pair only (front_cover_code = FF). */}
                {String(o.front_cover_code || "").trim().toUpperCase() === "FF" ? (
                  <SideNotOrderedCard side="front" order={o} onRestore={isBackOffice && PRE_PRODUCTION.includes(o.status) ? () => codeAutoSave("front_cover_code", "") : null} />
                ) : (
                <div className={BAND.front} data-testid="front-group">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div data-testid="front-cover-cell">
                      {renderCover("front_cover_code", "Front Cover")}
                    </div>
                    <div data-testid="front-clamp-cell">
                      {renderClamp("f_clamp_code", "F-Clamp")}
                    </div>
                  </div>
                </div>
                )}
                {/* Rear group (yellow outline) — hidden when the customer bought the FRONT pair only (RR). */}
                {String(o.rear_cover_code || "").trim().toUpperCase() === "RR" ? (
                  <SideNotOrderedCard side="rear" order={o} onRestore={isBackOffice && PRE_PRODUCTION.includes(o.status) ? () => codeAutoSave("rear_cover_code", "") : null} />
                ) : (
                <div className={BAND.rear} data-testid="rear-group">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div data-testid="rear-cover-cell">
                      {renderCover("rear_cover_code", "Rear Cover")}
                    </div>
                    <div data-testid="rear-clamp-cell">
                      {renderClamp("r_clamp_code", "R-Clamp")}
                    </div>
                  </div>
                </div>
                )}
              </div>
              </div>
            );
          })()}
          {(!(orderInfoLocked && codesLocked) || isBackOffice) && (
            editMode ? (
              <Button
                data-testid="save-order-info"
                onClick={saveOrderInfo}
                className="mt-4 bg-emerald-600 hover:bg-emerald-700"
              >
                ✓ Save
              </Button>
            ) : (
              <Button
                data-testid="edit-order-info"
                onClick={() => setEditMode(true)}
                variant="outline"
                className="mt-4"
              >
                ✎ Edit
              </Button>
            )
          )}

          {/* ---- Photos preview (Order Info tab, right above Approvals & Sign-offs) ---- */}
          <OrderInfoPhotos orderId={id} />

          {/* ---- Approvals & Sign-offs + Status History — always rendered, every stage incl. Fulfilled ---- */}
          {(() => {
            const events = buildSignoffEvents(o, STAGE_LABEL);
            const latest = events[0];
            const latestWhen = latest ? formatWhen(latest.at) : null;
            return (
            <section className="mt-6 bg-white border border-slate-200 rounded-md p-5" data-testid="signoffs-panel">
              <div className="flex items-center gap-3 mb-3 flex-wrap">
                <div className="text-xl font-bold uppercase tracking-widest text-slate-700" data-testid="signoffs-title">
                  Approvals & Sign-offs
                </div>
                <button
                  type="button"
                  onClick={() => setSignoffsOpen((v) => !v)}
                  className="text-xs font-mono text-slate-500 hover:text-slate-800 underline underline-offset-2 cursor-pointer"
                  data-testid="signoffs-toggle"
                >
                  {signoffsOpen ? "less ▴" : `show all (${events.length}) ▾`}
                </button>
                {o.status === "completed" && (
                  <span className="ml-auto inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium uppercase tracking-wider border bg-emerald-50 text-emerald-700 border-emerald-200">
                    Order Received
                  </span>
                )}
              </div>
              {!signoffsOpen && (latest ? (
                <button type="button" onClick={() => setSignoffsOpen(true)} className="w-full text-left flex flex-wrap items-center gap-2 rounded-md border px-3 py-2 hover:bg-slate-50" style={{ borderColor: "#fecaca", background: "#fff5f5" }} data-testid="signoffs-summary">
                  <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded text-white" style={{ background: "#C30000" }}>★ Latest</span>
                  <span className="text-xs font-bold uppercase tracking-wide text-slate-800" data-testid="signoffs-summary-label">{latest.label}</span>
                  <span className="font-mono text-xs text-slate-700">{latestWhen.date}{latestWhen.time ? ` · ${latestWhen.time}` : ""}</span>
                  <span className="text-xs text-slate-600">by <b className="text-slate-900">{latest.status === "sync" ? latest.signer : signerName(latest.signer)}</b></span>
                  <span className="ml-auto text-[11px] font-mono text-slate-500">{events.length} record{events.length === 1 ? "" : "s"} ▾</span>
                </button>
              ) : (
                <div className="text-sm text-slate-400 italic" data-testid="signoffs-empty">No sign-offs recorded yet.</div>
              ))}
              {signoffsOpen && (
                <>
                  {(() => {
                    if (events.length === 0) {
                      return <div className="text-sm text-slate-400 italic" data-testid="signoffs-empty">No sign-offs recorded yet.</div>;
                    }

                    return (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {events.map((ev) => {
                          const { date, time } = formatWhen(ev.at);
                          const display = ev.status === "sync" ? ev.signer : signerName(ev.signer);
                          const isCurrent = ev.isCurrent;
                          return (
                            <div
                              key={ev.key}
                              data-testid={`signoff-${ev.status}`}
                              data-current={isCurrent || undefined}
                              className={`relative rounded-lg p-4 border-2 transition-all ${isCurrent ? "shadow-lg ring-2 ring-red-100" : "shadow-sm"}`}
                              style={{
                                borderColor: isCurrent ? "#C30000" : ev.tone === "emerald" ? "#a7f3d0" : ev.tone === "amber" ? "#fcd34d" : "#e5e7eb",
                                background: isCurrent ? "#fff5f5" : ev.tone === "emerald" ? "#ecfdf5" : ev.tone === "amber" ? "#fffbeb" : "white",
                              }}
                            >
                              {isCurrent && (
                                <div className="absolute -top-2 left-3 text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded text-white" style={{ background: "#C30000" }} data-testid="signoff-current-badge">
                                  ★ Current
                                </div>
                              )}
                              <div className={`text-sm font-bold uppercase tracking-wide ${isCurrent ? "" : "text-slate-800"}`} style={isCurrent ? { color: "#C30000" } : {}}>
                                {ev.label}
                              </div>
                              <div className="mt-2 font-mono">
                                <div className={`font-bold ${isCurrent ? "text-lg" : "text-base"} text-slate-900 leading-tight`}>{date}</div>
                                {time && <div className={`font-bold ${isCurrent ? "text-base" : "text-sm"} text-slate-700 leading-tight mt-0.5`}>{time}</div>}
                              </div>
                              <div className="mt-3 pt-2 border-t text-xs text-slate-600 flex items-baseline gap-1.5" style={{ borderColor: isCurrent ? "#fecaca" : "#e5e7eb" }}>
                                <span className="uppercase tracking-widest text-[9px] text-slate-400 font-mono">by</span>
                                <span className="font-semibold text-sm text-slate-900">{display}</span>
                              </div>
                              {ev.reason && (
                                <div className="mt-1.5 text-[11px] text-slate-600 italic border-l-2 pl-2" style={{ borderColor: isCurrent ? "#C30000" : "#94a3b8" }}>
                                  “{ev.reason}”
                                </div>
                              )}
                              {Array.isArray(ev.code_diff) && ev.code_diff.length > 0 && (
                                <div className="mt-2 rounded border bg-slate-50 p-2 space-y-0.5" style={{ borderColor: "#e2e8f0" }} data-testid={`signoff-code-diff-${ev.status}`}>
                                  <div className="text-[9px] uppercase tracking-widest text-slate-500 font-mono mb-1">Code changes</div>
                                  {ev.code_diff.map((d, di) => (
                                    <div key={di} className="text-[11px] font-mono text-slate-800 flex items-center gap-1.5 flex-wrap" data-testid={`signoff-code-diff-row-${di}`}>
                                      <span className="font-semibold text-slate-600">{d.label || d.field}:</span>
                                      <span className="line-through text-slate-400">{d.from || "∅"}</span>
                                      <span className="text-slate-400">→</span>
                                      <span className="font-bold text-emerald-700">{d.to || "∅"}</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                              {ev.extra && <div className="mt-1 text-[10px] text-slate-500 font-mono">{ev.extra}</div>}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}

                  {/* Status History — nested inside the same "more" toggle */}
                  {(o.status_history || []).length > 0 && (
                    <div className="mt-6 pt-4 border-t border-slate-200" data-testid="status-history-block">
                      <h3 className="font-display font-semibold mb-3 text-sm uppercase tracking-widest text-slate-500">Status history</h3>
                      <ul className="text-sm space-y-1">
                        {(o.status_history || []).map((s, i) => (
                          <li key={i} className="font-mono text-xs flex gap-3 text-slate-600">
                            <span className="text-slate-400 w-44">{s.at?.slice(0, 19)}</span>
                            <span className="w-32 uppercase">{s.status}</span>
                            <span>{s.by}</span>
                            {s.repeats > 1 && <span className="text-slate-400" title={`repeated until ${s.last_at?.slice(0, 19) || ""}`}>×{s.repeats}</span>}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </>
              )}
            </section>
            );
          })()}
        </TabsContent>

        <TabsContent value="shopify" className="p-5">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <Field label="Shopify Order #" value={o.shopify_order_number} />
            <Field label="Synced" value={o.shopify_synced ? "Yes" : "No"} />
            <Field label="Synced At" value={o.shopify_synced_at} />
            <Field label="Tracking #" value={o.shipping_tracking_number ? <TrackingLink number={o.shipping_tracking_number} carrier={o.shipping_carrier} testid="shopify-tab-tracking" /> : null} />
          </div>
          <ShopifyDiscountCard order={o} />
          <ShippingAddressCard order={o} orderId={id} />
        </TabsContent>

        <TabsContent value="drive" className="p-5">
          <DrivePhotosPanel orderId={id} order={o} />
        </TabsContent>

        <TabsContent value="notes" className="p-5">
          <Textarea data-testid="note-input" placeholder="Add a note…" value={note} onChange={(e) => setNote(e.target.value)} className="mb-2" />
          <Button data-testid="note-save" onClick={() => { update.mutate({ note }); setNote(""); }} disabled={!note}>Add note</Button>
          <ul className="mt-5 space-y-2">
            {(o.notes_log || []).map((n, i) => (
              <li key={i} className="text-sm border border-slate-200 rounded-md p-3 bg-slate-50">
                <div className="text-[11px] uppercase tracking-widest text-slate-500 font-mono flex items-center gap-2">
                  <span>{n.by} · {n.at?.slice(0, 19)}</span>
                  {n.tag && (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase tracking-widest bg-amber-100 text-amber-800 border border-amber-300" data-testid={`note-tag-${i}`}>
                      {n.tag === "packing" ? "📦 packing" : n.tag === "bad_photos" ? `📷 ${t("Bad photos")}` : n.tag}
                    </span>
                  )}
                </div>
                <div className="mt-1">
                  <NoteText n={n} lang={lang} testid={`note-text-${i}`} />
                </div>
              </li>
            ))}
          </ul>
        </TabsContent>

        {isBackOffice && (
          <TabsContent value="emails" className="p-5">
            <OrderEmailsPanel orderId={o.id} order={o} />
          </TabsContent>
        )}

        {isBackOffice && (
          <TabsContent value="history" className="p-5">
            <ChangeHistoryPanel changeLog={o.change_log || []} />
          </TabsContent>
        )}

        <TabsContent value="packing" className="p-5">
          {clampsOnly(o) && !o.on_hold && <ClampsOnlyNote order={o} />}
          {o.on_hold && (
            <div className="mb-4 rounded-md border-2 border-amber-400 bg-amber-50 p-4" data-testid="packing-on-hold-banner">
              <div className="font-bold text-amber-900">🛑 {t("Order is ON HOLD — packing is locked until the issue is answered.")}</div>
              <div className="text-sm text-amber-800 mt-1" data-testid="hold-reason-line">{holdReasonText(o)} · {t("Reported by")} {o.hold_reported_by}</div>
              <div className="text-xs text-amber-700 mt-1">{t("Waiting for Shipping Desk / Back Office / Super Admin to respond.")}</div>
            </div>
          )}
          {o.on_hold ? null : pickupPending(o) ? (
            <PickupCard order={o} mySignature={mySignature} canConfirm={isBackOffice || (o.status === "packing" && isPacking) || (o.status === "inventory_packing" && user.role === "inventory_packing") || (o.status === "pickup_3pl" && user.role === "three_pl")} onNotFound={goBackToList} />
          ) : (isPacking || user.role === "inventory_packing") && ["packing", "inventory_packing"].includes(o.status) ? (
            <PackingWizard
              order={o}
              invForOrder={invForOrder}
              mySignature={mySignature}
              onConfirm={(f) => pack.mutate({ field: f, value: true })}
              onUncheck={(f) => pack.mutate({ field: f, value: false })}
              onFinish={() => packingFinish.mutate()}
              onPickupReady={o.is_pickup && o.status === "inventory_packing" ? () => pickupReady.mutate() : null}
              pickupBusy={pickupReady.isPending}
              onUploadPhoto={openPackingCamera}
              onReportIssue={() => setStopOpen(true)}
              uploading={drivePhotoUpload.isPending}
            />
          ) : (
            <>
          <p className="text-sm text-slate-500 mb-3">{t("Tap each item as you complete it. All items required before signing off.")}</p>
          {(() => {
            const fs = String(o.front_cover_code || "").trim().toUpperCase() === "FF";
            const rs = String(o.rear_cover_code || "").trim().toUpperCase() === "RR";
            if (fs || rs) {
              return (
                <div className="mb-3 rounded-md border border-blue-300 bg-blue-50 px-3 py-2 text-sm text-blue-900" data-testid="single-side-notice">
                  ℹ️ {t("Single-side order")}
                  {fs ? ` · ${t("front pair not ordered")} (FF)` : ""}
                  {rs ? ` · ${t("rear pair not ordered")} (RR)` : ""}
                </div>
              );
            }
            return null;
          })()}
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-2">
            {packingFieldsFor(o, PACKING_FIELDS_BY_STAGE[o.status] || [])
              .map((f) => {
              const v = o.packing_checklist?.[f]?.value;
              const isPhoto = f === "picture_to_drive";
              if (f === "ships_with") {
                return (
                  <div key={f} className={`lg:col-span-2 border-2 rounded-md px-2.5 py-2 flex items-center gap-2 ${v ? "bg-emerald-50 border-emerald-400 ring-1 ring-emerald-300" : "bg-sky-50 border-sky-500"}`} data-testid="pack-ships_with">
                    <Checkbox data-testid="pack-cb-ships_with" checked={!!v} className="pointer-events-none shrink-0" tabIndex={-1} />
                    <span className="text-[12px] uppercase tracking-wide text-sky-800 font-semibold whitespace-nowrap">{t(PACKING_FIELD_LABEL[f])}</span>
                    <ShipsWithPicker order={o} mySignature={mySignature} compact />
                  </div>
                );
              }
              const expected = {
                po_cat: `${o.po_number || "—"} / ${o.catalog_number || invForOrder?.cat_number || "—"}`,
                color: effectiveColor(o).color || "—",
                front_cover: o.front_cover_code || "—",
                f_clamp: o.f_clamp_code || "—",
                rear_cover: o.rear_cover_code || "—",
                r_clamp: o.r_clamp_code || "—",
                clamp_count: clampCountExpected(o),
                spacers: o.spacer
                  ? (o.spacer_qty ? `${o.spacer} × ${o.spacer_qty}` : o.spacer)
                  : (invForOrder?.spacers ? `${invForOrder.spacers} × ${invForOrder.spacers_qty ?? "—"}` : "—"),
                logo_label: o.customer?.logo_url ? "see logo →" : (o.logo || "no logo"),
              }[f];
              const hasCode = expected !== undefined;
              const photoCount = (o.packing_photos || []).length + (o.photos || []).length;
              const photoBlocked = isPhoto && !v && photoCount === 0;
              return (
                <button
                  key={f}
                  type="button"
                  data-testid={`pack-${f}`}
                  onClick={() => {
                    if (photoBlocked) {
                      toast.error("Take at least one photo before confirming this step.");
                      return;
                    }
                    pack.mutate({ field: f, value: !v });
                  }}
                  className={`text-left w-full border rounded-md px-2.5 py-2 transition-colors flex items-center gap-2 ${v ? "bg-emerald-50 border-emerald-400 ring-1 ring-emerald-300" : photoBlocked ? "bg-white border-amber-300 opacity-70" : "bg-white border-slate-200 hover:bg-slate-50 hover:border-slate-300"}`}
                  title={photoBlocked ? "Take at least one photo before confirming this step" : undefined}
                >
                  <Checkbox
                    data-testid={`pack-cb-${f}`}
                    checked={!!v}
                    className="pointer-events-none shrink-0"
                    tabIndex={-1}
                  />
                  <div className="flex-1 min-w-0 flex items-baseline gap-2">
                    <span className="text-[12px] uppercase tracking-wide text-slate-600 font-semibold whitespace-nowrap">{t(PACKING_FIELD_LABEL[f] || f.replace(/_/g, " "))}</span>
                    {hasCode && (
                      <span
                        className="font-mono font-bold text-slate-900 text-sm truncate"
                        data-testid={`expected-${f}`}
                        title={expected}
                      >
                        {expected}
                      </span>
                    )}
                  </div>
                  {f === "logo_label" && (
                    <LogoLabelPreview logo={o.logo} brand={o.brand} className="h-9 w-14" testid="compact-logo-label" />
                  )}
                  {f === "logo_label" && o.customer?.logo_url && (
                    <img src={o.customer.logo_url} alt="logo" className="w-7 h-7 border border-slate-200 rounded p-0.5 bg-white shrink-0" data-testid="logo-preview" />
                  )}
                  {f === "color" && (
                    <>
                      <ColorSwatchImage color={effectiveColor(o).color} className="h-9" testid="compact-color-swatch" />
                      <CustomColorNote ec={effectiveColor(o)} testid="compact-custom-color-note" />
                    </>
                  )}
                  {isPhoto && (
                    <span
                      role="button"
                      data-testid="upload-drive-photo"
                      onClick={(e) => { e.stopPropagation(); openPackingCamera(); }}
                      className="shrink-0 text-[11px] border border-slate-300 rounded px-1.5 py-0.5 hover:bg-slate-100"
                    >
                      📷
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Photos uploaded */}
          {(o.photos || []).length > 0 && (
            <div className="mt-4 text-xs text-slate-500 font-mono">{o.photos.length} photo(s) uploaded to Drive</div>
          )}

          {/* Finish + signature */}
          {(() => {
            const fields = packingFieldsFor(o, PACKING_FIELDS_BY_STAGE[o.status] || []);
            const checkedCount = fields.filter((f) => o.packing_checklist?.[f]?.value).length;
            const allChecked = checkedCount === fields.length && fields.length > 0;
            return (
              <div className="mt-6 border-t border-slate-200 pt-4">
                <div className="text-xs text-slate-500 font-mono mb-2" data-testid="checklist-progress">
                  {checkedCount}/{fields.length} {t("items checked")}
                  {!allChecked && <span className="ml-2 text-amber-700">— {t("complete all items to enable Finish")}</span>}
                </div>
                <div className="flex flex-col md:flex-row gap-3 md:items-center">
                  <div
                    className="rounded-md border border-slate-300 bg-white px-3 h-9 md:w-64 flex items-center text-sm font-mono text-slate-800"
                    data-testid="packing-employee-sig"
                  >
                    <span className="text-slate-400 text-[10px] uppercase tracking-widest mr-2">{t("Signed as")}</span>
                    <span className="font-semibold truncate">{mySignature}</span>
                  </div>
                  <Button
                    data-testid="packing-finish-btn"
                    disabled={!allChecked}
                    onClick={() => packingFinish.mutate()}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  >
                    {clampsOnly(o) && o.ships_inside?.po_number ? `✓ ${t("Finish packing")} → ${t("inside")} ${o.ships_inside.po_number}` : t("✓ Finish & Send to Shipping")}
                  </Button>
                  <Button data-testid="report-issue" className="bg-red-600 hover:bg-red-700 text-white" onClick={() => setStopOpen(true)}>
                    {t("🛑 Stop / Report Issue")}
                  </Button>
                </div>
              </div>
            );
          })()}
            </>
          )}

          {/* Stamp when finished */}
          {(o.inventory_packed_by || o.packed_by) && (
            <div className="mt-4 inline-flex items-center gap-3 border border-emerald-200 bg-emerald-50 rounded p-3" data-testid="packing-stamp">
              <span className="text-[10px] uppercase tracking-widest font-mono text-emerald-700">Packed by</span>
              <span className="font-display font-semibold text-emerald-800">{o.inventory_packed_by || o.packed_by}</span>
              <span className="text-xs font-mono text-emerald-700">{(o.inventory_packed_at || o.packed_at)?.slice(0, 19).replace("T", " ")}</span>
            </div>
          )}
        </TabsContent>
      </Tabs>

      <SendToPickupDialog order={o} open={pickupOpen} onOpenChange={setPickupOpen} onDone={goBackToList} fixedWarehouse={pickupFixed} />

      {/* Missing-fields guard modal — blocks moving Advance → Production /
          Inventory Packing until Catalog # + Spacer + Spacer Qty are all set. */}
      <Dialog open={missingOpen} onOpenChange={setMissingOpen}>
        <DialogContent data-testid="missing-fields-modal">
          <DialogHeader>
            <DialogTitle>Missing required fields</DialogTitle>
            <p className="text-xs text-slate-500 mt-1">
              This order can't move to <b>{pendingTarget === "pickup_canada" ? "Canada storage pick-up" : pendingTarget === "pickup" ? "warehouse pick-up" : "Production"}</b> yet.
              Please fill in the following before trying again:
            </p>
          </DialogHeader>
          <ul className="list-disc pl-5 space-y-1 text-sm text-red-700" data-testid="missing-fields-list">
            {missingList.map((m) => (<li key={m}>{m}</li>))}
          </ul>
          <div className="mt-2 text-[11px] text-slate-500 leading-relaxed">
            Tip: If Catalog # is missing, click <b>Re-run Match</b> in the Inventory Cross-Reference panel to auto-match by
            Front + Rear cover codes, or use <b>Assign CAT # Manually</b>. Spacer + Qty are on the same row as VIN / Rim
            Size / Color / Logo.
          </div>
          <div className="flex justify-end gap-2 pt-2">
            {!(o?.catalog_number || "").trim() && (
              <Button
                variant="outline"
                data-testid="missing-open-assign-cat"
                onClick={() => { setMissingOpen(false); setAssignCatOpen(true); }}
              >
                Assign CAT # Manually
              </Button>
            )}
            <Button
              data-testid="missing-modal-close"
              className="bg-slate-900 hover:bg-slate-800 text-white"
              onClick={() => setMissingOpen(false)}
            >
              OK — I'll fill it in
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Manual CAT # assignment (BO Administrator only, when auto-match fails) */}
      <Dialog open={assignCatOpen} onOpenChange={setAssignCatOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign CAT # manually</DialogTitle>
            <p className="text-xs text-slate-500 mt-1">
              Use this only when auto-match can't find a row but you know the right catalog number.
              This override is logged in the order's history.
            </p>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Catalog # <span className="text-red-600">*</span></Label>
              <Input
                data-testid="assign-cat-input"
                value={assignCatValue}
                onChange={(e) => setAssignCatValue(e.target.value)}
                placeholder="e.g. KIATL018"
                autoFocus
              />
              {inventoryList.length > 0 && (
                <div className="mt-2">
                  <Label className="text-[10px] text-slate-500">Or pick from inventory ({inventoryList.length})</Label>
                  <select
                    className="mt-1 w-full h-9 border border-slate-300 rounded-md px-2 text-sm bg-white"
                    value={assignCatValue}
                    onChange={(e) => setAssignCatValue(e.target.value)}
                    data-testid="assign-cat-picker"
                  >
                    <option value="">— choose —</option>
                    {inventoryList.map((i) => (
                      <option key={i.id} value={i.cat_number}>
                        {i.cat_number} · {i.brand} {i.model} · F={i.front_cover_code} R={i.rear_cover_code}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
            <Button
              data-testid="assign-cat-submit"
              disabled={!assignCatValue.trim() || assignCat.isPending}
              onClick={() => assignCat.mutate()}
              className="w-full bg-slate-900 hover:bg-slate-800"
            >
              {assignCat.isPending ? "Assigning…" : "Assign"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>


      {/* Edit Customer Name dialog — Super Admin only. Scoped to `name` per
          user rule Feb 28, 2026 ("allow super admin only to change client
          name by adding edit button"). */}
      <Dialog open={editNameOpen} onOpenChange={setEditNameOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Customer Name</DialogTitle>
            <p className="text-xs text-slate-500 mt-1">Super Admin only.</p>
          </DialogHeader>
          <div className="space-y-2 mt-2">
            <label className="text-xs text-slate-500 uppercase tracking-widest">Name</label>
            <Input
              data-testid="edit-customer-name-input"
              value={editNameValue}
              onChange={(e) => setEditNameValue(e.target.value)}
              autoFocus
              placeholder="Customer full name"
            />
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setEditNameOpen(false)}>Cancel</Button>
            <Button
              data-testid="edit-customer-name-save"
              disabled={!editNameValue.trim() || editCustomerName.isPending}
              onClick={() => editCustomerName.mutate({ name: editNameValue.trim() })}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              Save
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Lead dialog — Super Admin only. Reuses the existing hard-
          delete endpoint (DELETE /orders/{oid}?confirm_po=<PO>). User must
          re-type the PO to guard against fat-finger deletes. */}
      <Dialog open={deleteLeadOpen} onOpenChange={setDeleteLeadOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-700">🗑 Delete Lead · {o.po_number}</DialogTitle>
            <p className="text-xs text-slate-500 mt-1">
              This permanently removes the lead order from Mongo. Cannot be undone.
              Type the PO number to confirm.
            </p>
          </DialogHeader>
          <div className="space-y-2 mt-2">
            <label className="text-xs text-slate-500 uppercase tracking-widest">
              Type <span className="font-mono">{o.po_number}</span> to confirm
            </label>
            <Input
              data-testid="delete-lead-confirm-input"
              value={deleteLeadConfirm}
              onChange={(e) => setDeleteLeadConfirm(e.target.value)}
              autoFocus
              placeholder={o.po_number || ""}
            />
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setDeleteLeadOpen(false)}>Cancel</Button>
            <Button
              data-testid="delete-lead-confirm-btn"
              disabled={deleteLeadConfirm.trim() !== (o.po_number || "").trim() || deleteLead.isPending}
              onClick={() => deleteLead.mutate(o.po_number)}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete Permanently
            </Button>
          </div>
        </DialogContent>
      </Dialog>


      {/* Edit Codes on a Lead — compact dialog for BO to fix bogus codes
          without leaving the header. (Mar 2026) */}
      <Dialog open={leadEditCodesOpen} onOpenChange={setLeadEditCodesOpen}>
        <DialogContent data-testid="lead-edit-codes-dialog">
          <DialogHeader>
            <DialogTitle>Edit Codes · {o.po_number}</DialogTitle>
            <p className="text-xs text-slate-500 mt-1">
              Use <b>FF</b> in Front Cover / <b>RR</b> in Rear Cover to mark a
              single-side order (customer ordered only one pair). Clamp on
              the marker's side may stay blank.
            </p>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Front Cover Code</Label>
              <Input
                data-testid="lead-code-front"
                value={leadCodesForm.front_cover_code}
                onChange={(e) => setLeadCodesForm({ ...leadCodesForm, front_cover_code: e.target.value.toUpperCase() })}
                placeholder="e.g. IN 11 or FF"
              />
            </div>
            <div>
              <Label className="text-xs">F Clamp Code</Label>
              <ClampSelect
                testid="lead-code-fclamp"
                value={leadCodesForm.f_clamp_code}
                onChange={(v) => setLeadCodesForm({ ...leadCodesForm, f_clamp_code: v })}
              />
            </div>
            <div>
              <Label className="text-xs">Rear Cover Code</Label>
              <Input
                data-testid="lead-code-rear"
                value={leadCodesForm.rear_cover_code}
                onChange={(e) => setLeadCodesForm({ ...leadCodesForm, rear_cover_code: e.target.value.toUpperCase() })}
                placeholder="e.g. IN 12 or RR"
              />
            </div>
            <div>
              <Label className="text-xs">R Clamp Code</Label>
              <ClampSelect
                testid="lead-code-rclamp"
                value={leadCodesForm.r_clamp_code}
                onChange={(v) => setLeadCodesForm({ ...leadCodesForm, r_clamp_code: v })}
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setLeadEditCodesOpen(false)}>Cancel</Button>
            <Button
              data-testid="lead-code-save-btn"
              disabled={saveLeadCodes.isPending}
              onClick={() => saveLeadCodes.mutate()}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              {saveLeadCodes.isPending ? "Saving…" : "Save Codes"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>


      {/* Approve Lead dialog — Back Office Administrator promotes a Lead to Pre Order */}
      <Dialog open={leadApproveOpen} onOpenChange={setLeadApproveOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Convert LD → PO · {o.po_number}</DialogTitle>
            <p className="text-xs text-slate-500 mt-1">
              Assign a real PO number + Shopify order number. The order keeps its current stage
              (usually Advance) and is free to travel through Production, Packing, Shipping etc.
              Photos already uploaded stay attached.
            </p>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">PO Number <span className="text-red-600">*</span></Label>
                <Input
                  data-testid="lead-approve-po"
                  value={leadApproveForm.po_number}
                  onChange={(e) => setLeadApproveForm({ ...leadApproveForm, po_number: e.target.value })}
                  placeholder="e.g. 1234 → PO-1234"
                  autoFocus
                  className="font-mono"
                />
              </div>
              <div>
                <Label className="text-xs">Shopify Order # <span className="text-slate-400">(optional)</span></Label>
                <Input
                  data-testid="lead-approve-shopify"
                  value={leadApproveForm.shopify_order_number}
                  onChange={(e) => setLeadApproveForm({ ...leadApproveForm, shopify_order_number: e.target.value })}
                  placeholder="e.g. 5024"
                  className="font-mono"
                />
              </div>
            </div>
            <div className="border border-emerald-200 bg-emerald-50/50 rounded-md p-3 space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium text-emerald-900 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="lead-approve-payment-confirmed"
                  checked={leadApproveForm.payment_confirmed}
                  onChange={(e) => setLeadApproveForm({ ...leadApproveForm, payment_confirmed: e.target.checked })}
                />
                Payment received & confirmed
              </label>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <Label className="text-[10px]">Amount</Label>
                  <Input
                    data-testid="lead-approve-amount"
                    value={leadApproveForm.payment_amount}
                    onChange={(e) => setLeadApproveForm({ ...leadApproveForm, payment_amount: e.target.value })}
                    placeholder="1200"
                  />
                </div>
                <div>
                  <Label className="text-[10px]">Method</Label>
                  <Input
                    data-testid="lead-approve-method"
                    value={leadApproveForm.payment_method}
                    onChange={(e) => setLeadApproveForm({ ...leadApproveForm, payment_method: e.target.value })}
                    placeholder="e.g. Zelle, Stripe"
                  />
                </div>
                <div>
                  <Label className="text-[10px]">Reference #</Label>
                  <Input
                    data-testid="lead-approve-reference"
                    value={leadApproveForm.payment_reference}
                    onChange={(e) => setLeadApproveForm({ ...leadApproveForm, payment_reference: e.target.value })}
                    placeholder="TXN-1234"
                  />
                </div>
              </div>
            </div>
            <div>
              <Label className="text-xs">Notes (optional)</Label>
              <Textarea
                data-testid="lead-approve-notes"
                value={leadApproveForm.notes}
                onChange={(e) => setLeadApproveForm({ ...leadApproveForm, notes: e.target.value })}
                placeholder="Any notes for the record"
              />
            </div>
            <Button
              data-testid="lead-approve-submit"
              disabled={!leadApproveForm.po_number.trim() || !leadApproveForm.payment_confirmed || approveLead.isPending}
              onClick={() => approveLead.mutate(leadApproveForm)}
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              {approveLead.isPending ? "Converting…" : "Convert LD → PO"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <DialogContent data-testid="cancel-order-modal" className="max-w-2xl">
          <DialogHeader><DialogTitle>Cancel Order · {o.po_number}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-slate-500">
              {CANCEL_STOCK_RULE[o.status]
                ? <>The order <span className="font-semibold">stays in {STAGE_LABEL[o.status] || o.status}</span> until the station confirms the 🛑 STOP (where the set is stored) — only then it moves to <span className="font-semibold">Cancelled Orders</span>.</>
                : <>The order will move to <span className="font-semibold">Cancelled Orders</span> with a{" "}<span className="font-semibold text-red-700">REFUNDED</span> badge.</>}
              {" "}Select one or more reasons — each ticked reason sends its own customer email. A refund request is
              auto-generated in Billing (amount derived from the batch line price) awaiting
              Factory 1 approval.
            </p>

            {/* Reason checklist */}
            <div className="border border-slate-200 rounded-md p-3 bg-slate-50">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs uppercase tracking-widest font-semibold text-slate-700">Reasons</Label>
                <Button
                  data-testid="cancel-add-reason-btn"
                  type="button"
                  size="sm"
                  variant="outline"
                  className="h-7 text-xs"
                  onClick={() => setCancelAddOpen(true)}
                >+ Add reason</Button>
              </div>
              {cancelReasonsQuery.isLoading && <div className="text-xs text-slate-400">Loading…</div>}
              {(cancelReasonsQuery.data || []).map((r) => (
                <CancelReasonRow
                  key={r.id}
                  reason={r}
                  checked={cancelReasonIds.includes(r.id)}
                  onToggle={(on) => setCancelReasonIds((prev) => (on ? (prev.includes(r.id) ? prev : [...prev, r.id]) : prev.filter((x) => x !== r.id)))}
                />
              ))}
              {(cancelReasonsQuery.data || []).length === 0 && !cancelReasonsQuery.isLoading && (
                <div className="text-xs text-slate-400 italic">No reasons configured yet — click "+ Add reason" above.</div>
              )}
              <RemovedCancelReasons enabled={cancelOpen} />
            </div>

            {/* Send-emails toggle */}
            <label className="flex items-center gap-2 text-xs cursor-pointer">
              <input
                type="checkbox"
                data-testid="cancel-send-emails"
                checked={cancelSendEmails}
                onChange={(e) => setCancelSendEmails(e.target.checked)}
              />
              <span>Send the checked-reason email(s) to the customer now (uses each reason's template — you can edit templates in Settings).</span>
            </label>

            {/* Shopify cancel + full refund — automatic for No Mold / before-start reasons and for every pre-production cancel (rules Oct 8, Oct 16 + Oct 27, 2026) */}
            {(cancelReasonIds.some((id) => REFUND_CANCEL_REASON_IDS.includes(id)) || PRE_PRODUCTION_STAGES.includes(o.status)) && (
              <label className={`flex items-start gap-2 text-xs cursor-pointer rounded-md border p-2 ${cancelShopifyRefund ? "border-emerald-400 bg-emerald-50" : "border-slate-200 bg-slate-50"}`} data-testid="cancel-shopify-refund-row">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  data-testid="cancel-shopify-refund"
                  checked={cancelShopifyRefund}
                  disabled={!o.shopify_order_id}
                  onChange={(e) => setCancelShopifyRefund(e.target.checked)}
                />
                <span>
                  <span className="font-semibold">💳 Cancel + full refund in Shopify</span> — cancels the Shopify order and refunds the whole amount to the customer's original payment method. Shopify also sends its own cancellation e-mail.
                  {PRE_PRODUCTION_STAGES.includes(o.status) && <span className="block text-slate-600 mt-0.5">Pre-production cancel: nothing was produced, so the order is cancelled and confirmed in Shopify.</span>}
                  {!o.shopify_order_id && <span className="block text-rose-700 mt-0.5">This order has no Shopify order ID — cancel / refund it manually in Shopify.</span>}
                </span>
              </label>
            )}

            <div>
              <Label className="text-xs">Additional note (optional — appended to reason & attached to emails)</Label>
              <Textarea
                data-testid="cancel-reason"
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="e.g. Customer requested refund within 14 days"
              />
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded-md p-3 text-xs">
              <span className="text-slate-500 uppercase tracking-widest font-mono text-[10px]">Cancelling as</span>
              <div className="font-semibold mt-0.5">{mySignature}</div>
            </div>
            {(() => {
              const ruleDest = CANCEL_STOCK_RULE[o.status] || "none";
              const dest = cancelStockWarehouse === "auto" ? ruleDest : cancelStockWarehouse;
              const hasCat = !!(o.catalog_number || "").trim();
              return (
                <div className={`rounded-md border p-3 text-xs ${dest !== "none" ? "border-sky-300 bg-sky-50" : "border-slate-200 bg-slate-50"}`} data-testid="cancel-stock-row">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <span className="text-slate-600 uppercase tracking-widest font-mono text-[10px]">📦 Where does the set go?</span>
                    <select
                      data-testid="cancel-stock-warehouse"
                      value={dest}
                      onChange={(e) => setCancelStockWarehouse(e.target.value)}
                      className="h-8 rounded-md border border-slate-300 bg-white px-2 text-xs font-semibold"
                    >
                      {Object.entries(CANCEL_STOCK_LABEL).map(([k, l]) => <option key={k} value={k}>{l}{k === ruleDest ? " · rule" : ""}</option>)}
                    </select>
                  </div>
                  <div className="mt-1.5 text-[11px] text-slate-700" data-testid="cancel-stock-hint">
                    {dest === "none"
                      ? (ruleDest === "none" ? "Nothing produced yet at this stage — no stock added. The order goes to Cancelled Orders now." : "The set will NOT be added to stock — the order goes to Cancelled Orders now.")
                      : <>
                          The order <b>stays in {STAGE_LABEL[o.status] || o.status}</b> and is {CANCEL_STOCK_WHEN[o.status]?.includes("packing") ? "packed" : "received"} as usual.
                          {" "}<b>{CANCEL_STOCK_WHEN[o.status] || "Then"}</b> the station gets a full-screen 🛑 STOP: <i>"this order stays in the {dest === "on_arrival" ? "USA / CANADIAN" : CANCEL_STOCK_LABEL[dest].replace(/^\S+\s/, "").toUpperCase()}"</i> with CAT # <b className="font-mono">{o.catalog_number || "—"}</b> · <b>{o.color || "—"}</b> to write on the box; once they confirm, +1 stock is added there.
                          {!hasCat && <span className="block text-rose-700 mt-0.5">⚠ No CAT # on this order — the station cannot confirm the STOP until Back Office adds it.</span>}
                        </>}
                    <div className="text-[10px] text-slate-500 mt-1">Rule: Production / Transfer / Packing → China · Inventory Packing → Canada · Shipping or received → USA if 🇺🇸 flag, else Canada · before Production → nothing (Shopify cancel + refund).</div>
                  </div>
                </div>
              );
            })()}
            <Button
              data-testid="cancel-order-submit"
              className="w-full bg-red-600 hover:bg-red-700 text-white"
              disabled={cancelOrder.isPending || previewCancelEmails.isPending || (cancelReasonIds.length === 0 && !cancelReason.trim())}
              onClick={() => {
                // If the user asked for emails AND ticked at least one reason,
                // show the per-email preview modal first. Otherwise fire the
                // cancel straight through (no emails to review).
                if (cancelSendEmails && cancelReasonIds.length > 0) {
                  previewCancelEmails.mutate({ combined: false });
                } else {
                  cancelOrder.mutate({
                    reason: cancelReason,
                    reason_ids: cancelReasonIds,
                    send_emails: false,
                  });
                }
              }}
            >
              {cancelOrder.isPending
                ? "Cancelling..."
                : previewCancelEmails.isPending
                  ? "Building preview…"
                  : (cancelSendEmails && cancelReasonIds.length > 0)
                    ? `👁 Preview ${cancelReasonIds.length} Email${cancelReasonIds.length === 1 ? "" : "s"} → Cancel`
                    : `✕ Confirm Cancel`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Cancel — Email Preview modal
          Renders after the user clicks "Preview N Emails → Cancel" in the
          Cancel modal. Shows each reason's rendered email (subject + body)
          with a checkbox to include/exclude that email from the send. The
          bottom action button fires the actual cancel + email send. */}
      <Dialog open={cancelPreviewOpen} onOpenChange={(v) => {
        setCancelPreviewOpen(v);
        if (!v) { setCancelPreviewEmails([]); setCancelPreviewChecked({}); setCancelCombined(false); }
      }}>
        <DialogContent data-testid="cancel-preview-modal" className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Review outgoing cancel emails · {o.po_number}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-xs text-slate-500">
              Tick each email you want to send. Untick to skip it. When you click
              <span className="font-semibold"> Send &amp; Cancel Order</span> below, the order will move
              to <span className="font-semibold">Cancelled Orders</span> and only the ticked emails will
              be delivered to <span className="font-mono">{(cancelPreviewEmails[0]?.to) || (o.customer?.email || "customer")}</span>.
            </p>

            {/* Mode toggle — combined vs per-reason */}
            {cancelReasonIds.length > 1 && (
              <div
                data-testid="cancel-preview-mode-toggle"
                className="flex items-center justify-between gap-3 border border-sky-200 bg-sky-50 rounded-md px-3 py-2"
              >
                <div className="text-xs text-slate-700">
                  <div className="font-semibold">
                    {cancelCombined ? "One combined email" : `${cancelReasonIds.length} separate emails`}
                  </div>
                  <div className="text-slate-500">
                    {cancelCombined
                      ? "The customer gets ONE email listing every checked reason as a bullet list."
                      : "The customer gets one email per checked reason (each uses its own template)."}
                  </div>
                </div>
                <Button
                  data-testid="cancel-preview-toggle-combined"
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={previewCancelEmails.isPending || cancelOrder.isPending}
                  onClick={() => previewCancelEmails.mutate({ combined: !cancelCombined })}
                  className="border-sky-500 text-sky-800 hover:bg-sky-100 whitespace-nowrap"
                >
                  {cancelCombined ? "→ Switch to separate emails" : "→ Combine into one email"}
                </Button>
              </div>
            )}

            {cancelPreviewEmails.length === 0 && (
              <div className="text-sm text-slate-400 italic">No emails to preview.</div>
            )}
            {cancelPreviewEmails.map((em, idx) => {
              const key = em.template_key || `no-tpl-${idx}`;
              const checked = !!cancelPreviewChecked[em.template_key];
              const hasError = !!em.error;
              const isCombined = !!em.combined;
              return (
                <div
                  key={key}
                  data-testid={`cancel-preview-email-${idx}`}
                  className={`border rounded-lg overflow-hidden ${checked && !hasError ? "border-emerald-500" : hasError ? "border-red-300 bg-red-50/40" : "border-slate-200"}`}
                >
                  <label className={`flex items-start gap-3 px-3 py-2 border-b ${checked && !hasError ? "bg-emerald-50" : "bg-slate-50"} ${hasError ? "cursor-not-allowed opacity-70" : "cursor-pointer"}`}>
                    <input
                      type="checkbox"
                      className="mt-1 shrink-0"
                      data-testid={`cancel-preview-toggle-${idx}`}
                      disabled={hasError || !em.template_key}
                      checked={checked}
                      onChange={(e) => {
                        if (!em.template_key) return;
                        setCancelPreviewChecked((prev) => ({ ...prev, [em.template_key]: e.target.checked }));
                      }}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-slate-800">
                        {em.reason_label}
                        {em.template_key && (
                          <span className="ml-2 text-[10px] uppercase tracking-widest font-mono text-slate-500">· {em.template_key}</span>
                        )}
                      </div>
                      {isCombined && (em.reason_labels || []).length > 0 && (
                        <div className="text-[11px] text-slate-500 mt-0.5">
                          Reasons included: {(em.reason_labels || []).join(" · ")}
                        </div>
                      )}
                      {hasError ? (
                        <div className="text-xs text-red-700 mt-0.5">⚠ {em.error}</div>
                      ) : (
                        <div className="text-xs text-slate-500 mt-0.5">To: {em.to || "—"}</div>
                      )}
                    </div>
                  </label>
                  {!hasError && em.subject && (
                    <div className="px-3 py-2 text-sm">
                      <div className="text-[10px] uppercase tracking-widest font-mono text-slate-500 mb-1">Subject</div>
                      <div className="font-semibold text-slate-800 mb-2">{em.subject}</div>
                      <div className="text-[10px] uppercase tracking-widest font-mono text-slate-500 mb-1">Preview</div>
                      <div
                        className="border border-slate-200 rounded bg-white p-3 text-sm max-h-64 overflow-y-auto"
                        // eslint-disable-next-line react/no-danger
                        dangerouslySetInnerHTML={{ __html: em.body_html || "" }}
                      />
                    </div>
                  )}
                </div>
              );
            })}
            <div className="flex flex-col sm:flex-row gap-2 pt-2">
              <Button
                variant="outline"
                data-testid="cancel-preview-back"
                onClick={() => setCancelPreviewOpen(false)}
                disabled={cancelOrder.isPending}
                className="border-slate-400"
              >
                ← Back to Edit
              </Button>
              <Button
                data-testid="cancel-preview-confirm"
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                disabled={cancelOrder.isPending}
                onClick={() => {
                  const keys_to_send = Object.entries(cancelPreviewChecked)
                    .filter(([, v]) => v)
                    .map(([k]) => k);
                  // Always send_emails=true so the backend produces a per-reason
                  // audit list (each unticked one comes back status='skipped').
                  // The `email_template_keys` filter is what actually decides
                  // which ones are delivered. In combined mode, backend ignores
                  // `email_template_keys` and sends ONE cancel_combined email.
                  cancelOrder.mutate({
                    reason: cancelReason,
                    reason_ids: cancelReasonIds,
                    send_emails: true,
                    email_template_keys: keys_to_send,
                    combined_email: cancelCombined && keys_to_send.length > 0,
                  });
                }}
              >
                {cancelOrder.isPending
                  ? "Cancelling…"
                  : (() => {
                      const n = Object.values(cancelPreviewChecked).filter(Boolean).length;
                      if (n === 0) return "✕ Cancel Order without Emails";
                      if (cancelCombined) return `✉ Send 1 Combined Email & Cancel Order`;
                      return `✉ Send ${n} Email${n === 1 ? "" : "s"} & Cancel Order`;
                    })()}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add-a-reason mini dialog */}
      <Dialog open={cancelAddOpen} onOpenChange={setCancelAddOpen}>
        <DialogContent data-testid="cancel-add-reason-modal" className="max-w-lg">
          <DialogHeader><DialogTitle>Add a new cancel reason</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-slate-500">
              This new reason will appear as a checkbox in every future Cancel modal, and its email
              template will be created automatically. You can edit the template later in Settings → Email
              Templates.
            </p>
            <div>
              <Label className="text-xs">Reason label <span className="text-red-500">*</span></Label>
              <Input
                data-testid="new-reason-label"
                value={newReasonForm.label}
                onChange={(e) => setNewReasonForm((f) => ({ ...f, label: e.target.value }))}
                placeholder="e.g. Vehicle Discontinued"
              />
            </div>
            <div>
              <Label className="text-xs">Short description (optional — shown under the checkbox)</Label>
              <Input
                data-testid="new-reason-desc"
                value={newReasonForm.description}
                onChange={(e) => setNewReasonForm((f) => ({ ...f, description: e.target.value }))}
                placeholder="e.g. Manufacturer no longer produces this model"
              />
            </div>
            <div>
              <Label className="text-xs">Email subject (optional)</Label>
              <Input
                data-testid="new-reason-email-subject"
                value={newReasonForm.email_subject}
                onChange={(e) => setNewReasonForm((f) => ({ ...f, email_subject: e.target.value }))}
                placeholder="Defaults to 'Order #{order_number} — Update on <reason>'"
              />
            </div>
            <div>
              <Label className="text-xs">Email body (optional — paragraphs separated by blank line)</Label>
              <Textarea
                data-testid="new-reason-email-body"
                value={newReasonForm.email_body}
                onChange={(e) => setNewReasonForm((f) => ({ ...f, email_body: e.target.value }))}
                rows={5}
                placeholder="Leave blank to use our default cancellation wording."
              />
              <p className="text-[11px] text-slate-400 mt-1">Merge tags supported: <span className="font-mono">{"{first_name}"}</span>, <span className="font-mono">{"{order_number}"}</span></p>
            </div>
            <div className="flex gap-2 justify-end pt-2">
              <Button variant="outline" onClick={() => setCancelAddOpen(false)}>Cancel</Button>
              <Button
                data-testid="new-reason-submit"
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                disabled={addCancelReason.isPending || !newReasonForm.label.trim()}
                onClick={() => addCancelReason.mutate(newReasonForm)}
              >
                {addCancelReason.isPending ? "Adding…" : "+ Add Reason"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={delayOpen} onOpenChange={setDelayOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Report Production Delay</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-slate-500">Super Admin and Back Office will be alerted with the new estimated completion date.</p>
            <div>
              <Label className="text-xs">New estimated completion date</Label>
              <Input data-testid="delay-eta" type="date" value={delayEta} onChange={(e) => setDelayEta(e.target.value)} />
            </div>
            <div>
              <Label className="text-xs">Reason / Note</Label>
              <Textarea data-testid="delay-reason" value={delayReason} onChange={(e) => setDelayReason(e.target.value)} placeholder="Explain the cause of the delay" />
            </div>
            <Button
              data-testid="delay-submit"
              disabled={!delayEta}
              onClick={() => productionDelay.mutate({ new_eta: delayEta, reason: delayReason })}
              className="w-full bg-sky-600 hover:bg-sky-700 text-white"
            >
              ⏰ Submit Delay Report
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={stopOpen} onOpenChange={(v) => { setStopOpen(v); if (!v) setStopFiles([]); }}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t("Stop & Report Issue")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">{t("Reason")}</Label>
              <Textarea data-testid="stop-reason" value={stopReason} onChange={(e) => setStopReason(e.target.value)} placeholder="Describe what went wrong (e.g. wrong front cover code, damaged item, missing logo)" />
            </div>
            <div>
              <Label className="text-xs">{t("Photos (optional, up to 5)")}</Label>
              <div className="mt-1 flex flex-wrap items-center gap-2" data-testid="stop-photo-list">
                {stopFiles.map((f, i) => (
                  <div key={`${f.name}-${i}`} className="relative">
                    <img src={URL.createObjectURL(f)} alt="" className="h-16 w-16 object-cover rounded border border-slate-300" />
                    <button type="button" data-testid={`stop-photo-remove-${i}`} onClick={() => setStopFiles(stopFiles.filter((_, j) => j !== i))}
                            className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-red-600 text-white text-xs leading-none" title={t("Remove")}>×</button>
                  </div>
                ))}
                {stopFiles.length < 5 && (
                  <label className="h-16 w-16 rounded border-2 border-dashed border-slate-300 hover:border-red-400 flex items-center justify-center text-[10px] text-slate-500 cursor-pointer text-center leading-tight" data-testid="stop-photo-add">
                    {t("📷 Add photo")}
                    <input type="file" accept="image/*" multiple className="hidden" data-testid="stop-photo-input"
                           onChange={(e) => { const picked = Array.from(e.target.files || []); setStopFiles([...stopFiles, ...picked].slice(0, 5)); e.target.value = ""; }} />
                  </label>
                )}
              </div>
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded-md p-3 text-xs" data-testid="stop-signed-as">
              <span className="text-slate-500 uppercase tracking-widest font-mono text-[10px]">{t("Signed as")}</span>
              <div className="font-semibold text-slate-900 mt-0.5">{mySignature}</div>
            </div>
            <Button
              data-testid="stop-submit"
              disabled={!stopReason.trim() || stopReport.isPending}
              onClick={() => stopReport.mutate({ reason: stopReason.trim(), files: stopFiles })}
              className="w-full bg-red-600 hover:bg-red-700 text-white"
            >
              {t("🛑 Submit Issue Report")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Rim capture modal — device camera with alignment box */}
      <RimCaptureModal
        open={rimCaptureOpen}
        onOpenChange={setRimCaptureOpen}
        orderId={id}
        folderId={o.drive_folder_id}
      />
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-widest font-mono text-slate-500">{label}</div>
      <div className="mt-0.5 text-sm">{value || <span className="text-slate-400">—</span>}</div>
    </div>
  );
}

// ============================================================================
// Rim Scanner Modal — click any Drive photo (or upload one) and the server
// auto-detects the black rubber tire sidewall, crops to that region, then
// runs OCR with rotation + adaptive-light retries. Zero clicks after
// picking the photo — the AI does the framing.
// ============================================================================
function RimCaptureModal({ open, onOpenChange, orderId, folderId }) {
  const qc = useQueryClient();
  const [scanning, setScanning] = React.useState(null); // { source, id } while running
  const [lastResult, setLastResult] = React.useState(null);

  // Pull the Drive photo list for this order so the user can pick one.
  const { data: files, isLoading } = useQuery({
    queryKey: ["drive-files-for-rim", orderId, folderId],
    enabled: open && !!orderId,
    queryFn: async () => {
      const r = (await api.get(`/orders/${orderId}/drive-photos`)).data || {};
      return Array.isArray(r) ? r : (r.files || []);
    },
    staleTime: 60_000,
  });

  React.useEffect(() => {
    if (!open) {
      setScanning(null);
      setLastResult(null);
    }
  }, [open]);

  const handleScanResult = (r, closeOnSuccess = true) => {
    setLastResult(r);
    if (r?.ok && r?.applied) {
      const badge = r.tire_isolated ? " · tire auto-framed" : "";
      toast.success(`Rim ${r.rim_size}${r.confidence ? ` (${r.confidence})` : ""}${badge} — saved`);
      if (closeOnSuccess) onOpenChange(false);
    } else if (r?.ok && !r?.applied) {
      toast.info(`Read rim ${r.rim_size}${r.confidence ? ` (${r.confidence})` : ""}, but the field was already filled.`);
      if (closeOnSuccess) onOpenChange(false);
    } else {
      const hint = r?.raw_text ? ` — model read: "${r.raw_text}"` : "";
      const stage = r?.tire_isolated ? " (tire was found but no size text)" : "";
      toast.warning(`Couldn't parse rim${stage}${hint}. Try a different photo.`);
    }
  };

  // Server-side flow — one click on the Drive tile triggers the full
  // tire-locate + OCR pipeline on the backend. No canvas, no crop, no CORS.
  const scanDrive = useMutation({
    mutationFn: async (file) => {
      setScanning({ source: "drive", id: file.id });
      const r = await api.post(`/orders/${orderId}/scan-rim-from-drive/${file.id}`);
      return r.data;
    },
    onSuccess: (r) => {
      // Only refresh the specific order — invalidating the entire cache
      // (no queryKey) would nuke unrelated Drive lists / order grids.
      qc.invalidateQueries({ queryKey: ["order", orderId] });
      handleScanResult(r);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || e?.message || "Rim scan failed"),
    onSettled: () => setScanning(null),
  });

  const scanUpload = useMutation({
    mutationFn: async (file) => {
      setScanning({ source: "upload", id: "upload" });
      const b64 = await new Promise((resolve, reject) => {
        const fr = new FileReader();
        fr.onload = () => resolve(String(fr.result).split(",")[1] || "");
        fr.onerror = reject;
        fr.readAsDataURL(file);
      });
      const r = await api.post(`/orders/${orderId}/scan-rim-photo`, {
        image_b64: b64, mime_type: file.type || "image/jpeg", apply: true,
      });
      return r.data;
    },
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["order", orderId] });
      handleScanResult(r);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || e?.message || "Rim scan failed"),
    onSettled: () => setScanning(null),
  });

  const busy = scanDrive.isPending || scanUpload.isPending;

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    scanUpload.mutate(f);
    e.target.value = "";
  };

  const imageFiles = (files || []).filter((f) => (f.mimeType || "").startsWith("image/"));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="rim-capture-dialog">
        <DialogHeader>
          <DialogTitle>Scan Rim Size</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="text-xs text-slate-600 leading-relaxed">
            Click any photo below. The AI finds the <strong>black rubber tire sidewall</strong>,
            ignores the alloy/rim and background, then reads the <span className="font-mono bg-red-50 border border-red-200 px-1 rounded text-red-700">R</span> + two-digit size.
          </div>
          {busy && (
            <div
              className="text-xs text-sky-700 bg-sky-50 border border-sky-200 rounded px-3 py-2 flex items-center gap-2"
              data-testid="rim-scan-progress"
            >
              <div className="animate-spin h-3.5 w-3.5 border-2 border-sky-600 border-t-transparent rounded-full"></div>
              Analyzing tire sidewall… this takes about 30&nbsp;seconds.
            </div>
          )}
          {isLoading && <div className="text-sm text-slate-400 italic">Loading photos…</div>}
          {!isLoading && imageFiles.length === 0 && (
            <div className="text-sm text-slate-500 italic">No photos in the Drive folder yet — use the upload below.</div>
          )}
          {imageFiles.length > 0 && (
            <div className="grid grid-cols-4 sm:grid-cols-5 gap-2 max-h-[460px] overflow-auto pr-1" data-testid="rim-photo-grid">
              {imageFiles.map((f) => {
                const isThisScanning = busy && scanning?.source === "drive" && scanning?.id === f.id;
                return (
                  <button
                    key={f.id}
                    type="button"
                    onClick={() => !busy && scanDrive.mutate(f)}
                    disabled={busy}
                    data-testid={`rim-photo-tile-${f.id}`}
                    title="Click to auto-scan this tire"
                    className={`relative aspect-square rounded overflow-hidden border-2 outline-none group transition-all ${
                      isThisScanning
                        ? "border-sky-500 ring-2 ring-sky-400"
                        : busy
                        ? "border-slate-200 opacity-40 cursor-not-allowed"
                        : "border-slate-200 hover:border-sky-500 focus:border-sky-500 cursor-pointer"
                    }`}
                  >
                    {isImageFile(f) ? (
                      <img
                        src={photoUrl(orderId, f.id, "thumb")}
                        alt={f.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                      />
                    ) : (
                      <div className="w-full h-full bg-slate-100 flex items-center justify-center text-xs text-slate-400">no preview</div>
                    )}
                    {isThisScanning && (
                      <div className="absolute inset-0 bg-black/55 flex flex-col items-center justify-center text-white text-[11px] font-mono uppercase tracking-widest gap-1">
                        <div className="animate-spin h-6 w-6 border-2 border-white border-t-transparent rounded-full"></div>
                        Scanning…
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
          <div className="pt-2 border-t border-slate-200 flex items-center justify-between gap-3 flex-wrap">
            <label
              className={`inline-flex items-center gap-2 rounded-md border border-slate-300 px-3 py-2 text-sm ${busy ? "opacity-50 cursor-not-allowed" : "cursor-pointer hover:bg-slate-50"}`}
              data-testid="rim-upload-fallback"
            >
              📁 Upload from device
              <input type="file" accept="image/*" className="hidden" onChange={onPickFile} disabled={busy} />
            </label>
            {lastResult && !lastResult.ok && (
              <div className="text-xs text-slate-500" data-testid="rim-last-fail">
                {lastResult.tire_isolated
                  ? "Tire found but the text was unreadable. Try a sharper photo."
                  : "Couldn't detect the tire in the last photo — try one showing the sidewall clearly."}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}



// Compact info bar shown above the tabs for Pre Orders that are still missing
// photos. Displays reminder count, last-sent, next-due, and a manual send.
function PhotoReminderStrip({ o }) {
  const count = o.photo_reminder_count || 0;
  const last = o.photo_reminder_last_at;
  const fmt = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch { return iso.slice(0, 16); }
  };
  const daysSinceCreation = (() => {
    try {
      const c = new Date(o.created_at).getTime();
      return Math.floor((Date.now() - c) / 86400000);
    } catch { return null; }
  })();
  const isAbandoned = o.status === "abandoned";
  const STAGES = ["Week 1", "Week 2", "Week 4 (final)"];
  const nextStep = (() => {
    if (isAbandoned || count >= 3) return count >= 3 ? "Next: moves to Abandoned one week after the final reminder" : null;
    const days = [7, 14, 28][count];
    return `Next: ${STAGES[count]} reminder on day ${days}`;
  })();
  return (
    <div
      data-testid="photo-reminder-strip"
      className={`rounded-md border px-4 py-3 flex flex-wrap items-center gap-4 ${
        isAbandoned ? "bg-rose-50 border-rose-200" : count >= 3 ? "bg-amber-50 border-amber-200" : "bg-sky-50 border-sky-200"
      }`}
    >
      <div className="flex-1 min-w-[220px]">
        <div className="text-[11px] uppercase tracking-widest font-mono text-slate-500">Photo Reminder Emails · Week 1 → 2 → 4 → Abandoned</div>
        <div className="mt-0.5 text-sm">
          {isAbandoned ? (
            <span className="font-semibold text-rose-700">
              ⚠ Auto-abandoned — no photos after {count} reminder{count === 1 ? "" : "s"}. Photos landing in Drive bring it back to Pre Orders.
            </span>
          ) : count === 0 ? (
            <span className="text-slate-700">No reminders sent yet · Order age: <b>{daysSinceCreation ?? "—"} day(s)</b></span>
          ) : (
            <span className="text-slate-700">
              <b>{count}</b> reminder{count === 1 ? "" : "s"} sent ({STAGES.slice(0, Math.min(count, 3)).join(", ")}) · last on <b>{fmt(last)}</b>
            </span>
          )}
          {!isAbandoned && nextStep && !(o.is_lead || o.status === "lead") && (
            <span className="ml-2 text-xs text-slate-500" data-testid="photo-reminder-next">{nextStep}</span>
          )}
        </div>
      </div>
      {!o.customer?.email && (
        <span className="text-xs text-rose-600 italic">Customer has no email on file</span>
      )}
      {(o.is_lead || o.status === "lead") && !isAbandoned && (
        <span className="text-xs text-slate-500 italic" data-testid="photo-reminder-lead-note">Leads get no automatic reminders — use Send Email → Lead to Order</span>
      )}
    </div>
  );
}

// Static per-step "expected value" formulas + special hooks (photo,
// logo-swatch). Keyed by the same stable key that the template uses — any
// new custom step added by Super Admin will fall through to a null `exp`
// (no expected value shown, just the question).
const PACKING_STEP_META = {
  po_cat:           { exp: (o, inv) => `${o.po_number || "—"} / ${o.catalog_number || inv?.cat_number || "—"}` },
  color:            { exp: (o) => effectiveColor(o).color || "—" },
  front_cover:      { exp: (o) => o.front_cover_code || "—" },
  f_clamp:          { exp: (o) => o.f_clamp_code || "—" },
  rear_cover:       { exp: (o) => o.rear_cover_code || "—" },
  r_clamp:          { exp: (o) => o.r_clamp_code || "—" },
  quality:          { exp: () => null },
  lp_label:         { exp: () => null },
  clamp_label:      { exp: () => null },
  logo_label:       { exp: (o) => o.customer?.logo_url ? "see logo →" : (o.logo || "no logo") },
  picture_to_drive: { exp: () => null, isPhoto: true },
  spacers:          { exp: (o, inv) => {
      if (o?.spacer) return o.spacer_qty ? `${o.spacer} × ${o.spacer_qty}` : o.spacer;
      return inv?.spacers ? `${inv.spacers} × ${inv.spacers_qty ?? "—"}` : "—";
    } },
  tool_kit:         { exp: () => null },
  gift:             { exp: () => null },
  clamp_count:      { exp: (o) => clampCountExpected(o) },
  ships_with:       { exp: (o) => o?.ships_inside?.po_number ? `📦 ${o.ships_inside.po_number}` : null },
};

// Legacy hard-coded fallback — only used if the /packing-checklist-template
// fetch fails (offline / initial load). Keeps the wizard functional even
// before the template arrives from the server.
const PACKING_STEPS = [
  { key: "po_cat",           title: "PO / Catalog",       question: "Confirm the PO number and catalog on the label matches the cover.",          exp: (o, inv) => `${o.po_number || "—"} / ${o.catalog_number || inv?.cat_number || "—"}` },
  { key: "color",            title: "Color",              question: "Is the cover the correct color?",                                             exp: (o) => effectiveColor(o).color || "—" },
  { key: "front_cover",      title: "Front Cover Code",   question: "Read the front cover tag — does it match?",                                   exp: (o) => o.front_cover_code || "—" },
  { key: "f_clamp",          title: "Front Clamp Type",   question: "Verify the front clamp type matches.",                                        exp: (o) => o.f_clamp_code || "—" },
  { key: "rear_cover",       title: "Rear Cover Code",    question: "Read the rear cover tag — does it match?",                                    exp: (o) => o.rear_cover_code || "—" },
  { key: "r_clamp",          title: "Rear Clamp Type",    question: "Verify the rear clamp type matches.",                                         exp: (o) => o.r_clamp_code || "—" },
  { key: "quality",          title: "Quality Check",      question: "Inspect Paint finish & edges. Any defects?",                                  exp: () => null },
  { key: "lp_label",         title: "Leprecon+ Label",    question: "Is the Leprecon+ label affixed and legible?",                                 exp: () => null },
  { key: "clamp_label",      title: "Clamp Label",        question: "Is the Clamp Label Type present on both Front and Rear covers?",              exp: () => null },
  { key: "logo_label",       title: "Logo Label",         question: "Match the customer logo label — is it correct?",                              exp: (o) => o.customer?.logo_url ? "see logo →" : (o.logo || "no logo") },
  { key: "picture_to_drive", title: "Picture to Drive",   question: "Take a photo of the packed cover and upload it to Drive.",                    exp: () => null, isPhoto: true },
  { key: "spacers",          title: "Spacer Type",        question: "Include the correct spacer type and quantity per this order.",                 exp: (o, inv) => {
      if (o?.spacer) return o.spacer_qty ? `${o.spacer} × ${o.spacer_qty}` : o.spacer;
      return inv?.spacers ? `${inv.spacers} × ${inv.spacers_qty ?? "—"}` : "—";
    } },
  { key: "tool_kit",         title: "Tool Kit",           question: "Include the tool kit (Allen keys, wrench).",                                  exp: () => null },
  { key: "gift",             title: "Gift",               question: "Include the branded gift in the box.",                                        exp: () => null },
];

// Packing note button — visible only on the packing wizard (Packing Orders
// and Inventory Packing tabs). Notes get pushed to the shared `notes_log`
// so every other tab sees them under the order's Notes section.
function PackingNoteButton({ order }) {
  const qc = useQueryClient();
  const { t, lang } = useT();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const packingNotes = (order.notes_log || []).filter((n) => (n.tag || "").includes("packing"));

  const save = useMutation({
    mutationFn: async () => (await api.patch(`/orders/${order.id}`, {
      note: text.trim(),
      note_tag: "packing",
    })).data,
    onSuccess: () => {
      qc.invalidateQueries();
      toast.success("Packing note added");
      setText("");
      setOpen(false);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed to save note"),
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          size="sm"
          variant="outline"
          className="border-amber-400 text-amber-800 hover:bg-amber-50 h-7 text-xs whitespace-nowrap"
          data-testid="packing-note-btn"
        >
          📝 {t("Note")}{packingNotes.length > 0 ? ` · ${packingNotes.length}` : ""}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md" data-testid="packing-note-dialog">
        <DialogHeader>
          <DialogTitle>{t("Add a packing note")}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 pt-2">
          <p className="text-xs text-slate-500">{t("Shared with Back Office and every other tab under this order's")} <b>{t("Notes")}</b> {t("section.")}</p>
          <Textarea
            data-testid="packing-note-input"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="e.g. Front-cover clamp was tight — used the shim from bin B3."
            className="min-h-[110px]"
            autoFocus
          />
          {packingNotes.length > 0 && (
            <div className="max-h-[140px] overflow-auto border rounded p-2 bg-slate-50 text-xs space-y-1.5">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">{t("Previous packing notes")}</div>
              {packingNotes.slice().reverse().map((n, i) => (
                <div key={i} className="border-b border-slate-200 pb-1 last:border-b-0" data-testid={`prev-packing-note-${i}`}>
                  <div className="font-mono text-[10px] text-slate-500">{(n.by || "").split("@")[0]} · {(n.at || "").slice(0, 16).replace("T", " ")}</div>
                  <NoteText n={n} lang={lang} className="text-slate-800" />
                </div>
              ))}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setOpen(false)}>{t("Cancel")}</Button>
            <Button
              className="bg-amber-600 hover:bg-amber-700 text-white"
              disabled={!text.trim() || save.isPending}
              onClick={() => save.mutate()}
              data-testid="packing-note-save"
            >
              {save.isPending ? t("Saving…") : t("Save note")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


function PackingWizard({ order, invForOrder, mySignature, onConfirm, onUncheck, onFinish, onPickupReady, pickupBusy, onUploadPhoto, onReportIssue, uploading }) {
  const { t } = useT();
  // Fetch the (mutable) template so Super-Admin edits propagate live to any
  // wizard already open. Falls back to the hard-coded PACKING_STEPS if the
  // template isn't loaded yet (e.g. first render).
  const { data: tmplRows = [] } = useQuery({
    queryKey: ["packing-template"],
    queryFn: async () => (await api.get("/packing-checklist-template")).data,
    staleTime: 60_000,
  });
  const steps = React.useMemo(() => {
    const base = (tmplRows && tmplRows.length > 0) ? tmplRows : PACKING_STEPS;
    const byKey = Object.fromEntries(base.map((r) => [r.key, r]));
    // Clamps-only warranty (no covers, no own box) → reduced, fixed step list.
    // Single-side order rule (Mar 2026): FF front / RR rear → that pair's steps are skipped.
    const keys = packingFieldsFor(order, base.filter((r) => r.active !== false).sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)).map((r) => r.key));
    return keys.map((key) => {
      const r = byKey[key] || EXTRA_STEP_META[key] || { title: key };
      const meta = PACKING_STEP_META[key] || {};
      return {
        key,
        title: r.title,
        question: r.question || "",
        exp: meta.exp || (() => null),
        isPhoto: r.is_photo || meta.isPhoto || false,
      };
    });
  }, [tmplRows, order?.front_cover_code, order?.rear_cover_code, order?.replacement_scope, order?.replacement_clamps_side]);

  // Revise-with-note dialog state
  const [reviseOpen, setReviseOpen] = useState(false);
  const [reviseKey, setReviseKey] = useState(null);
  const [reviseTargetValue, setReviseTargetValue] = useState(true);
  const [reviseNote, setReviseNote] = useState("");
  const qc2 = useQueryClient();
  const revise = useMutation({
    mutationFn: async () => (await api.post(`/orders/${order.id}/packing-check`, {
      field: reviseKey,
      value: reviseTargetValue,
      employee_signature: mySignature,
      note: reviseNote.trim(),
    })).data,
    onSuccess: () => {
      qc2.invalidateQueries();
      toast.success("Step revised — note recorded");
      setReviseOpen(false); setReviseNote(""); setReviseKey(null);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Revision failed"),
  });
  // Determine starting step = first unchecked item
  const isDone = (k) => !!order.packing_checklist?.[k]?.value;
  const firstUnchecked = steps.findIndex((s) => !isDone(s.key));
  const [idx, setIdx] = useState(firstUnchecked === -1 ? steps.length : firstUnchecked);
  const [shipDialogOpen, setShipDialogOpen] = useState(false);
  const [selfCheckoutOpen, setSelfCheckoutOpen] = useState(false);
  const [shopifyShipOpen, setShopifyShipOpen] = useState(false);
  // US destination → the USMCA Certificate of Origin is NOT printed
  // automatically any more (user rule Oct 6, 2026). Each shipping dialog shows
  // a "Print USMCA declaration" checkbox; the amber box below just reminds the
  // operator and offers an "open now" shortcut.
  const isUsShipping = React.useMemo(() => isUsOrder(order), [order.shipping_address, order.us_flag_override]);

  const handleOpenShipStation = () => setShipDialogOpen(true);
  const handleOpenShopifyShip = () => setShopifyShipOpen(true);
  const handleOpenSelfCheckout = () => setSelfCheckoutOpen(true);

  const qc = useQueryClient();

  const allChecked = steps.every((s) => isDone(s.key));
  const atFinish = idx >= steps.length;
  const isPacked = !!(order.packed_at || order.inventory_packed_at);
  const hasLabel = !!(order.shipping_label_url || order.shopify_label_url || order.shipping_tracking_number);

  const step = steps[Math.min(idx, steps.length - 1)];
  const expected = step ? step.exp(order, invForOrder) : null;
  const isConfirmed = step ? isDone(step.key) : false;
  const progress = steps.filter((s) => isDone(s.key)).length;

  const confirmAndAdvance = () => {
    if (!isConfirmed && step) onConfirm(step.key);
    // Advance to next unchecked step
    let next = idx + 1;
    while (next < steps.length && isDone(steps[next].key)) next += 1;
    setIdx(next);
  };
  const goBack = () => {
    if (idx > 0) setIdx(idx - 1);
  };

  if (atFinish) {
    // Final stage — signature + shipping action. Behaviour differs per station:
    //  • packing (Packing Orders) → hand-off to the dedicated Shipping team.
    //    No ShipStation label creation here. Just show "packed, ready for
    //    shipping" and one big button that pushes the order to Shipping.
    //  • inventory_packing (Inv Packing) → self-fulfilled path. The operator
    //    can still create the ShipStation / Shopify / Self-checkout label
    //    right on the station.
    const isPackingStation = order.status === "packing";
    return (
      <div className="max-w-2xl mx-auto py-6" data-testid="packing-wizard-finish">
        {!isPacked ? (
          <>
            <div className="border border-emerald-400 bg-emerald-50 rounded-lg p-8 text-center">
              <div className="text-5xl mb-3">✅</div>
            <div className="text-2xl font-bold text-emerald-800">{t("All")} {steps.length} {t("checks completed")}</div>
              <div className="text-sm text-emerald-700 mt-1">
                {clampsOnly(order) && order.ships_inside?.po_number
                  ? `${clampsLabel(order)} — ${t("travelling inside the box of")} ${order.ships_inside.po_number}.`
                  : isPackingStation
                  ? t("Order is packed and ready for shipping.")
                  : order.status === "inventory_packing"
                  ? t("Mark the order as Ready to be Shipped — you can create the label and ship later from the Inv Packing list.")
                  : t("Sign your name, then create the ShipStation label to close out this station.")}
              </div>
            </div>
            <div className="mt-6 space-y-3">
              <label className="text-xs uppercase tracking-widest font-mono text-slate-500">{t("Employee Signature")}</label>
              <div
                className="text-lg h-12 rounded-md border border-slate-300 bg-white flex items-center px-3 font-mono text-slate-800"
                data-testid="packing-employee-sig"
              >
                <span className="text-slate-400 text-[11px] uppercase tracking-widest mr-2">{t("Signed as")}</span>
                <span className="font-semibold">{mySignature}</span>
              </div>
              {order.status === "inventory_packing" && (
                <div className={onPickupReady ? "grid grid-cols-1 md:grid-cols-2 gap-3" : ""}>
                  <Button
                    data-testid="mark-ready-to-ship-btn"
                    onClick={() => onFinish()}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white h-14 text-lg w-full"
                  >
                    {t("✅ Mark Ready to be Shipped")}
                  </Button>
                  {onPickupReady && (
                    <Button
                      data-testid="wizard-pickup-ready-btn"
                      onClick={() => onPickupReady()}
                      disabled={pickupBusy}
                      className="bg-violet-600 hover:bg-violet-700 text-white h-14 text-lg w-full"
                      title="Email the client that the order is waiting for pick-up and move it to Order Fulfilled"
                    >
                      {pickupBusy ? t("Sending…") : t("🏬 PICK UP READY")}
                    </Button>
                  )}
                </div>
              )}
              {isPackingStation && (
                <Button
                  data-testid="send-to-shipping-btn"
                  onClick={() => onFinish()}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white h-14 text-lg w-full"
                >
                  {clampsOnly(order) && order.ships_inside?.po_number ? `✅ ${t("READY TO SHIP")} → ${t("inside")} ${order.ships_inside.po_number}` : t("✅ READY TO SHIP")}
                </Button>
              )}
              {isUsShipping && !isPackingStation && (
                <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" data-testid="usmca-preflight">
                  <div className="font-semibold inline-flex items-center gap-2"><UsFlagIcon className="h-3.5" />{t("US destination")}</div>
                  <div className="text-xs mt-0.5">{t("The USMCA declaration is NOT printed automatically. Tick “Print USMCA declaration” inside the shipping dialog if you need the 3 copies.")}</div>
                </div>
              )}
              {!isPackingStation && (
                <div className="flex flex-col md:flex-row gap-3">
                  <Button
                    data-testid="packing-finish-btn"
                    onClick={handleOpenShipStation}
                    className="bg-slate-900 hover:bg-slate-800 text-white h-14 text-lg flex-1"
                  >
                    {t("🚚 Create ShipStation Label")}
                  </Button>
                  <Button
                    data-testid="packing-finish-shopify-btn"
                    onClick={handleOpenShopifyShip}
                    className="bg-emerald-700 hover:bg-emerald-800 text-white h-14 text-lg flex-1"
                  >
                    {t("🛍 Ship from Shopify")}
                  </Button>
                  <Button
                    data-testid="packing-finish-self-btn"
                    onClick={handleOpenSelfCheckout}
                    className="bg-slate-600 hover:bg-slate-700 text-white h-14 text-lg flex-1"
                  >
                    {t("✓ Self Checkout")}
                  </Button>
                  <Button
                    data-testid="report-issue"
                    onClick={onReportIssue}
                    className="bg-red-600 hover:bg-red-700 text-white h-14"
                  >
                    {t("🛑 Report Issue")}
                  </Button>
                </div>
              )}
              {isPackingStation && (
                <Button
                  data-testid="report-issue"
                  variant="outline"
                  onClick={onReportIssue}
                  className="border-red-300 text-red-700 hover:bg-red-50 h-12 w-full"
                >
                  {t("🛑 Report Issue")}
                </Button>
              )}
              {!isPackingStation && (
                <p className="text-[11px] text-slate-500 mt-1 leading-relaxed">
                  {t("Any of these three actions closes this station and moves the order to")} <b>{t("Order Fulfilled")}</b>.
                </p>
              )}
              <button
                data-testid="wizard-review"
                onClick={() => setIdx(0)}
                className="text-xs text-slate-500 hover:text-slate-800 underline"
              >
                ◀ {t("Review all")} {steps.length} {t("items")}
              </button>
            </div>
          </>
        ) : (
          // Post-finalize: packed stamp + reprint option
          <>
            <div className="border border-emerald-400 bg-emerald-50 rounded-lg p-8 text-center" data-testid="packing-stamp-final">
              <div className="text-5xl mb-3">📦</div>
              <div className="text-2xl font-bold text-emerald-800">{order.is_pickup ? "Order Packed & Ready for Pick Up" : "Order Packed & Ready to Ship"}</div>
              <div className="text-sm text-emerald-700 mt-2 font-mono">
                Packed by <span className="font-semibold">{order.inventory_packed_by || order.packed_by || mySignature}</span>
                {(order.inventory_packed_at || order.packed_at) && (
                  <> on <span className="font-semibold">{(order.inventory_packed_at || order.packed_at).slice(0, 10)}</span></>
                )}
              </div>
            </div>
            {onPickupReady && (
              <div className="mt-6 border-2 border-violet-300 rounded-lg p-6 bg-violet-50" data-testid="wizard-pickup-panel">
                <div className="text-[11px] uppercase tracking-widest font-mono text-violet-700 mb-1">Pick-up order</div>
                <div className="text-lg font-bold text-slate-900 mb-3">🏬 Tell the client it's ready</div>
                <p className="text-sm text-slate-600 mb-4">
                  Sends the "Pick-up Ready" email (waiting for pick-up + installation offer) and moves the order to Order Fulfilled as READY FOR PICK UP.
                </p>
                <Button
                  data-testid="wizard-pickup-ready-btn"
                  onClick={() => onPickupReady()}
                  disabled={pickupBusy}
                  className="bg-violet-600 hover:bg-violet-700 text-white h-14 text-lg w-full"
                >
                  {pickupBusy ? "Sending…" : "🏬 PICK UP READY"}
                </Button>
              </div>
            )}
            <div className="mt-6 border-2 border-slate-200 rounded-lg p-6 bg-white">
              <div className="text-[11px] uppercase tracking-widest font-mono text-slate-500 mb-1">Final Step</div>
              {order.shopify_label_url ? (
                <>
                  <div className="text-lg font-bold text-slate-900 mb-3">Shopify Shipping label</div>
                  <p className="text-sm text-slate-600 mb-4">
                    Label bought through Shopify{order.shipping_carrier ? ` · ${order.shipping_carrier}` : ""}{order.shipping_tracking_number ? ` · ${order.shipping_tracking_number}` : ""}. Reprint it below.
                  </p>
                  <Button
                    data-testid="wizard-shopify-label-print"
                    onClick={() => window.open(order.shopify_label_url, "_blank", "noopener")}
                    className="bg-emerald-700 hover:bg-emerald-800 text-white h-14 text-lg w-full"
                  >
                    🖨️ Print Shopify Label
                  </Button>
                </>
              ) : (
                <>
              <div className="text-lg font-bold text-slate-900 mb-3">Print ShipStation Label</div>
              <p className="text-sm text-slate-600 mb-4">
                {hasLabel
                  ? "A shipping label has already been created for this order. You can reprint it below."
                  : "Generate the shipping label from ShipStation. This closes out the packing station."}
              </p>
              <Button
                data-testid="wizard-shipstation-btn"
                onClick={handleOpenShipStation}
                className="bg-slate-900 hover:bg-slate-800 text-white h-14 text-lg w-full"
              >
                {hasLabel ? "🖨️ Reprint Shipping Label" : "🚚 Create ShipStation Label"}
              </Button>
                </>
              )}
              {!hasLabel && (
                <div className="grid grid-cols-2 gap-3 mt-3">
                  <Button
                    data-testid="wizard-shopify-btn"
                    onClick={handleOpenShopifyShip}
                    className="bg-emerald-700 hover:bg-emerald-800 text-white h-12"
                  >
                    🛍 Ship from Shopify
                  </Button>
                  <Button
                    data-testid="wizard-self-btn"
                    onClick={handleOpenSelfCheckout}
                    className="bg-slate-600 hover:bg-slate-700 text-white h-12"
                  >
                    ✓ Self Checkout
                  </Button>
                </div>
              )}
              {hasLabel && order.shipping_tracking_number && (
                <div className="mt-3 text-xs font-mono text-slate-500" data-testid="wizard-tracking">
                  Tracking · <TrackingLink number={order.shipping_tracking_number} carrier={order.shipping_carrier} className="text-slate-900" testid="wizard-tracking-link" />
                </div>
              )}
            </div>
            <button
              data-testid="wizard-review"
              onClick={() => setIdx(0)}
              className="mt-4 text-xs text-slate-500 hover:text-slate-800 underline"
            >
              ◀ Review all 14 items
            </button>
          </>
        )}
        {shipDialogOpen && (
          <ShipStationDialog
            order={order}
            open={shipDialogOpen}
            onOpenChange={setShipDialogOpen}
            onSuccess={() => {
              // Backend transitions the order to "fulfilled" AND sets
              // packed_at as part of the label creation — nothing more to do
              // here. Just refresh the local query cache so the wizard
              // re-renders in its "post-finalize" state showing the label
              // reprint option.
              qc.invalidateQueries();
            }}
          />
        )}
        {shopifyShipOpen && (
          <ShipFromShopifyDialog
            order={order}
            open={shopifyShipOpen}
            onOpenChange={setShopifyShipOpen}
            onSuccess={() => qc.invalidateQueries()}
          />
        )}
        {selfCheckoutOpen && (
          <SelfCheckoutDialog
            order={order}
            open={selfCheckoutOpen}
            onOpenChange={setSelfCheckoutOpen}
            onSuccess={() => qc.invalidateQueries()}
          />
        )}
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto py-4" data-testid="packing-wizard">
      {/* Progress bar */}
      <div className="mb-4">
        <div className="flex items-baseline justify-between mb-1 gap-2">
          <span className="text-xs uppercase tracking-widest font-mono text-slate-500">{t("Step")} {idx + 1} {t("of")} {steps.length}</span>
          <div className="flex items-center gap-3">
            <PackingNoteButton order={order} />
            <span className="text-xs font-mono text-slate-500" data-testid="wizard-progress">{progress}/{steps.length} {t("completed")}</span>
          </div>
        </div>
        <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden">
          <div className="h-full bg-emerald-500 transition-all" style={{ width: `${(progress / steps.length) * 100}%` }} />
        </div>
        {/* Compact step index — click any tile to jump back and revise. Green
            = completed, slate = pending, ring = current. Revising a completed
            step requires a note (enforced by the Revise dialog on the card). */}
        <div className="mt-2 flex flex-wrap gap-1" data-testid="wizard-step-index">
          {steps.map((s, i) => {
            const done = isDone(s.key);
            const isCurrent = i === idx;
            return (
              <button
                key={s.key}
                type="button"
                onClick={() => setIdx(i)}
                data-testid={`wizard-jump-${s.key}`}
                title={`${done ? "✓ " : ""}${s.title}`}
                className={`w-7 h-7 rounded text-[10px] font-mono font-semibold border transition-all ${
                  done
                    ? "bg-emerald-500 text-white border-emerald-600 hover:bg-emerald-600"
                    : "bg-white text-slate-500 border-slate-300 hover:bg-slate-50"
                } ${isCurrent ? "ring-2 ring-slate-900 ring-offset-1" : ""}`}
              >
                {i + 1}
              </button>
            );
          })}
        </div>
      </div>

      {/* Step card */}
      <div className={`border-2 rounded-lg p-8 ${isConfirmed ? "border-emerald-400 bg-emerald-50/40" : "border-slate-200 bg-white"}`} data-testid={`wizard-step-${step.key}`}>
        <div className="text-[11px] uppercase tracking-widest font-mono text-slate-500 mb-1">{t(step.title)}</div>
        {(step.key === "color" || step.key === "logo_label") ? (
          <div className="flex items-center justify-between gap-4 mt-1" data-testid={`wizard-${step.key}-row`}>
            <div className="min-w-0">
              {expected && (
                <div className="mb-2 font-mono font-bold text-slate-900 leading-tight break-words text-4xl md:text-5xl" data-testid="wizard-expected">
                  {expected}
                </div>
              )}
              <p className="text-base md:text-lg text-slate-800 leading-snug">{t(step.question)}</p>
            </div>
            {step.key === "color" ? (
              <div className="flex items-start gap-2 shrink-0">
                <ColorSwatchImage color={effectiveColor(order).color} className="h-28 shrink-0" testid="wizard-color-swatch" />
                <CustomColorNote ec={effectiveColor(order)} testid="wizard-custom-color-note" />
              </div>
            ) : (
              <>
                <LogoLabelPreview logo={order.logo} brand={order.brand} className="h-28 w-44" testid="wizard-logo-label" />
                {order.customer?.logo_url && (
                  <img src={order.customer.logo_url} alt="logo" className="w-24 h-24 shrink-0 border-2 border-slate-200 rounded p-2 bg-white" data-testid="wizard-logo" />
                )}
              </>
            )}
          </div>
        ) : (
          <>
            {expected && (
              <div className="mt-1 mb-4 font-mono font-bold text-slate-900 leading-tight break-words text-4xl md:text-5xl" data-testid="wizard-expected">
                {expected}
              </div>
            )}
            <p className="text-base md:text-lg text-slate-800 leading-snug">{t(step.question)}</p>
          </>
        )}

        {step.isPhoto && (
          <Button
            data-testid="upload-drive-photo"
            className="mt-4 w-full h-14 bg-emerald-600 hover:bg-emerald-700 text-white text-lg"
            onClick={onUploadPhoto}
            disabled={uploading}
          >
            {uploading ? t("Uploading…") : `📷 ${t("Take Photo")} (${(order.packing_photos || []).length + (order.photos || []).length} ${t("uploaded")})`}
          </Button>
        )}

        {step.key === "ships_with" && <ShipsWithPicker order={order} mySignature={mySignature} />}

        {/* Photo step — block Confirm until at least one photo exists. */}
        {(() => {
          if (!step.isPhoto) return null;
          const photoCount = (order.packing_photos || []).length + (order.photos || []).length;
          if (photoCount > 0) return null;
          return (
            <div
              className="mt-3 rounded-md border border-amber-300 bg-amber-50 text-amber-900 px-3 py-2 text-sm font-medium"
              data-testid="photo-required-hint"
            >
              📷 {t("A photo is required before you can confirm this step.")}
            </div>
          );
        })()}

        <div className="mt-6 flex flex-col md:flex-row gap-3">
          {(() => {
            const photoCount = (order.packing_photos || []).length + (order.photos || []).length;
            const photoRequiredBlock = step.isPhoto && photoCount === 0;
            const hostRequiredBlock = step.key === "ships_with" && !order.ships_inside?.order_id;
            const blocked = photoRequiredBlock || hostRequiredBlock;
            return (
              <Button
                data-testid="wizard-confirm"
                onClick={confirmAndAdvance}
                disabled={blocked}
                title={photoRequiredBlock ? t("Take at least one photo before confirming") : hostRequiredBlock ? t("Pick the order whose box the clamps travel in") : undefined}
                className={`h-14 text-lg flex-1 ${
                  blocked
                    ? "bg-slate-300 text-slate-500 cursor-not-allowed"
                    : isConfirmed
                    ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                    : "bg-slate-900 hover:bg-slate-800 text-white"
                }`}
              >
                {isConfirmed ? t("✓ Confirmed — Next") : t("✓ Confirm")}
              </Button>
            );
          })()}
          <Button
            data-testid="report-issue"
            variant="outline"
            onClick={onReportIssue}
            className="border-red-300 text-red-700 hover:bg-red-50 h-14"
          >
            {t("🛑 Report Issue")}
          </Button>
        </div>
        {isConfirmed && (
          <div className="mt-3 space-y-2">
            <button
              data-testid="wizard-revise"
              onClick={() => {
                setReviseKey(step.key);
                setReviseTargetValue(!isDone(step.key)); // toggle current value by default
                setReviseNote("");
                setReviseOpen(true);
              }}
              className="text-xs text-amber-800 hover:text-amber-900 underline font-medium"
            >
              ↩️ Revise this step (adds a note)
            </button>
            {/* Revision history — small, collapsed by default. Shows every
                change with actor + timestamp + note so the audit trail is
                readable inline while packing. */}
            {(() => {
              const entry = order.packing_checklist?.[step.key];
              const revisions = entry?.revisions || [];
              if (revisions.length === 0) return null;
              return (
                <details className="text-[11px] text-slate-500" data-testid="wizard-revisions">
                  <summary className="cursor-pointer hover:text-slate-700">
                    {revisions.length} revision(s) · click to expand
                  </summary>
                  <ul className="mt-1 space-y-1 pl-3">
                    {revisions.map((r, i) => (
                      <li key={i} className="border-l-2 border-amber-200 pl-2">
                        <span className="font-mono">
                          {r.previous_value ? "✓" : "✗"} → {r.new_value ? "✓" : "✗"}
                        </span>
                        {" · "}
                        <span>{r.by}</span>
                        {" · "}
                        <span>{new Date(r.at).toLocaleString()}</span>
                        {r.note && <div className="italic pl-3 text-slate-600">"{r.note}"</div>}
                      </li>
                    ))}
                  </ul>
                </details>
              );
            })()}
          </div>
        )}
      </div>

      {/* Revise dialog — required note when going back and changing a step */}
      <Dialog open={reviseOpen} onOpenChange={setReviseOpen}>
        <DialogContent className="max-w-md" data-testid="wizard-revise-dialog">
          <DialogHeader>
            <DialogTitle>↩️ Revise · {steps.find((s) => s.key === reviseKey)?.title || reviseKey}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <div className="text-xs text-slate-600 leading-relaxed">
              Going back to change your mark on a previously-confirmed step. The change is
              logged with your signature, timestamp, and this required note.
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest font-mono">New mark</Label>
              <div className="mt-1 flex gap-2">
                <Button
                  size="sm"
                  onClick={() => setReviseTargetValue(true)}
                  className={reviseTargetValue ? "bg-emerald-600 hover:bg-emerald-700 text-white" : "bg-slate-200 text-slate-700 hover:bg-slate-300"}
                  data-testid="revise-mark-checked"
                >
                  ✓ Confirmed
                </Button>
                <Button
                  size="sm"
                  onClick={() => setReviseTargetValue(false)}
                  className={!reviseTargetValue ? "bg-red-600 hover:bg-red-700 text-white" : "bg-slate-200 text-slate-700 hover:bg-slate-300"}
                  data-testid="revise-mark-unchecked"
                >
                  ✗ Unconfirmed
                </Button>
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest font-mono">Reason for change <span className="text-red-600">*</span></Label>
              <Textarea
                data-testid="revise-note"
                value={reviseNote}
                onChange={(e) => setReviseNote(e.target.value)}
                placeholder="e.g. Realized the front cover tag was misread — re-checked and it does match."
                className="mt-1"
                rows={3}
              />
            </div>
            <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
              <Button variant="outline" onClick={() => setReviseOpen(false)}>Cancel</Button>
              <Button
                onClick={() => revise.mutate()}
                disabled={!reviseNote.trim() || revise.isPending}
                data-testid="revise-submit"
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                {revise.isPending ? "Saving…" : "Save Revision"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Nav */}
      <div className="mt-4 flex items-center justify-between">
        <Button
          variant="ghost"
          onClick={goBack}
          disabled={idx === 0}
          data-testid="wizard-back"
          className="text-slate-500"
        >
          ◀ {t("Back")}
        </Button>
        <Button
          variant="ghost"
          onClick={() => setIdx(steps.length)}
          data-testid="wizard-skip-to-finish"
          disabled={!allChecked}
          className="text-slate-500"
        >
          {t("Skip to sign-off")} →
        </Button>
      </div>
    </div>
  );
}

function AddInventoryQuickCreate({ order, onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    cat_number: `LP-NEW-${Date.now().toString().slice(-5)}`,
    min_rim_size: order.rim_size || "20",
    spacers: order.spacer || "5mm",
    spacers_qty: order.spacer_qty || 4,
    warehouse: "canada",
    qty: 1,
  });
  const create = useMutation({
    mutationFn: () => api.post("/inventory", {
      cat_number: form.cat_number,
      brand: order.brand || "Unknown",
      model: order.model || "Unknown",
      year: order.year || "2024",
      trim: order.trim || "",
      color: order.color,
      front_cover_code: order.front_cover_code,
      f_clamp_code: order.f_clamp_code,
      rear_cover_code: order.rear_cover_code,
      r_clamp_code: order.r_clamp_code,
      min_rim_size: form.min_rim_size,
      spacers: form.spacers,
      spacers_qty: parseInt(form.spacers_qty) || 0,
      current_stock: parseInt(form.qty) || 0,
      stock_by_warehouse: order.color && (parseInt(form.qty) || 0) > 0 ? { [order.color]: { canada: 0, usa: 0, china: 0, [form.warehouse]: parseInt(form.qty) || 0 } } : null,
    }),
    onSuccess: async () => {
      // Also assign this new catalog number to the order
      await api.patch(`/orders/${order.id}`, { catalog_number: form.cat_number });
      toast.success("New inventory item created and assigned");
      setOpen(false);
      onCreated && onCreated();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="add-inventory-btn" className="bg-slate-900 hover:bg-slate-800">+ Create new Inventory item</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>New Inventory Item</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500">Brand/Model/Color/Codes are pulled from this order. Set the new catalog number, minimum rim, spacers, warehouse and starting qty.</p>
        <div className="space-y-3 mt-3">
          <div><Label className="text-xs">Catalog #</Label><Input data-testid="inv-cat" value={form.cat_number} onChange={(e) => setForm({ ...form, cat_number: e.target.value })} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Min Rim Size</Label><Input data-testid="inv-rim" value={form.min_rim_size} onChange={(e) => setForm({ ...form, min_rim_size: e.target.value })} /></div>
            <div><Label className="text-xs">Spacer Type</Label><Input data-testid="inv-spacers" value={form.spacers} onChange={(e) => setForm({ ...form, spacers: e.target.value })} placeholder="e.g. 5mm" /></div>
            <div><Label className="text-xs">Spacers Qty (per order)</Label><Input data-testid="inv-spacers-qty" type="number" value={form.spacers_qty} onChange={(e) => setForm({ ...form, spacers_qty: e.target.value })} /></div>
            <div><Label className="text-xs">Warehouse</Label>
              <select data-testid="inv-wh" value={form.warehouse} onChange={(e) => setForm({ ...form, warehouse: e.target.value })} className="mt-1 w-full h-9 rounded-md border border-slate-300 bg-white px-2 text-sm">
                {WAREHOUSES.map((w) => <option key={w} value={w}>{WAREHOUSE_FLAG[w]} {WAREHOUSE_LABEL[w]}</option>)}
              </select>
            </div>
            <div><Label className="text-xs">Starting Qty ({order.color || "colour"})</Label><Input data-testid="inv-qty" type="number" value={form.qty} onChange={(e) => setForm({ ...form, qty: e.target.value })} /></div>
          </div>
          <Button data-testid="inv-submit" onClick={() => create.mutate()} className="w-full bg-slate-900 hover:bg-slate-800">Create &amp; Assign</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---- Inventory Cross-Reference "no match" diagnostic ----
function NoMatchDiagnostic({ invMatch, order }) {
  if (!invMatch) {
    return (
      <div className="text-sm text-slate-600 italic" data-testid="inv-nomatch-loading">
        Checking inventory…
      </div>
    );
  }
  const reason = invMatch.reason;
  const searched = invMatch.searched || {
    front_cover_code: order.front_cover_code, rear_cover_code: order.rear_cover_code, color: order.color,
  };
  const partialHits = invMatch.partial_hits || [];
  
  if (reason === "missing_codes") {
    return (
      <div className="text-sm text-slate-600 italic" data-testid="inv-nomatch-missing">
        Codes not yet submitted — Factory 1 will fill them in on the Pending tab.
      </div>
    );
  }
  if (reason === "missing_vehicle") {
    return (
      <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-3" data-testid="inv-nomatch-vehicle">
        No brand / model / year on this order — the catalog is not looked up from codes alone. Fill the car info (VIN decode) or use <b>Assign CAT # Manually</b>.
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="inv-nomatch-diagnostic">
      <div className="text-sm text-slate-800 bg-white/70 border border-red-200 rounded-md p-3">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-[11px] uppercase tracking-widest font-mono text-red-700 font-bold">No auto-match</span>
        </div>
        <div className="text-xs text-slate-600 font-mono">
          Searched for: front = <b>{searched.front_cover_code || "—"}</b> · rear = <b>{searched.rear_cover_code || "—"}</b> · color = <b>{searched.color || "—"}</b>
        </div>
        <div className="text-xs mt-1">
          No inventory row has this front+rear code pair (colour is not part of the CAT #). See partial hits below or assign manually.
        </div>
      </div>

      {partialHits.length > 0 && (
        <div>
          <div className="text-[11px] uppercase tracking-widest font-mono text-slate-600 mb-1">
            Partial matches · one code only · {partialHits.length}
          </div>
          <div className="space-y-1">
            {partialHits.map((m) => (
              <div key={m.id} className="text-xs bg-slate-50 border border-slate-200 rounded px-2 py-1.5 flex items-center gap-3 font-mono" data-testid={`inv-partial-${m.cat_number}`}>
                <span className="font-bold">{m.cat_number}</span>
                <span className="text-slate-600">{m.brand} {m.model}</span>
                <span className="text-slate-500">F={m.front_cover_code} · R={m.rear_cover_code}</span>
                <span className="text-slate-500 ml-auto">stock: {Object.entries(m.stock_by_color || {}).map(([c, q]) => `${c}=${q}`).join(", ") || "—"}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {partialHits.length === 0 && (
        <div className="text-xs text-slate-500 italic">
          No related inventory rows found. Check the codes on the order or add the missing catalog entry in Inventory.
        </div>
      )}
    </div>
  );
}


// ---- Order-Info-tab compact photos gallery ----
// Renders a tight preview of the same photos as the Photos tab, sharing the
// exact React Query cache key (`["drive-photos", orderId]`) so no duplicate
// network request happens. Clicking a tile opens the shared PhotoLightbox
// (fullscreen, ← → navigation, Esc to close). Silently hides itself when
// no photos are linked yet — Back Office sees nothing distracting on empty
// orders, and gets a rich preview once photos arrive.
function OrderInfoPhotos({ orderId }) {
  const { user } = useAuth();
  const { data, isLoading } = useQuery({
    queryKey: ["drive-photos", orderId],
    queryFn: async () => (await api.get(`/orders/${orderId}/drive-photos`)).data,
    refetchOnWindowFocus: false,
  });
  const [lightboxIdx, setLightboxIdx] = React.useState(null);
  const files = data?.files || [];
  // Nothing to show → don't render anything (keeps Order Info clean on empty orders).
  if (isLoading || files.length === 0) return null;
  return (
    <div className="mt-6 pt-5 border-t border-slate-200" data-testid="order-info-photos">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[11px] uppercase tracking-widest font-mono text-slate-500">
          Photos <span className="text-slate-400">· {files.length}</span>
        </div>
        {data?.folder_url && canOpenDrive(user) && (
          <a
            href={data.folder_url}
            target="_blank"
            rel="noreferrer"
            className="text-xs text-slate-500 hover:text-slate-900 underline"
            data-testid="order-info-photos-open-drive"
          >
            Open folder in Drive ↗
          </a>
        )}
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2">
        {files.map((f, idx) => (
          <button
            key={f.id}
            type="button"
            onClick={() => setLightboxIdx(idx)}
            data-testid={`order-info-photo-${f.id}`}
            className="group aspect-square bg-slate-100 border border-slate-200 rounded-md overflow-hidden hover:shadow-md hover:border-slate-400 transition-all cursor-zoom-in relative"
            title={`Click to enlarge · ${f.name}`}
          >
            {isImageFile(f) ? (
              <img
                src={photoUrl(orderId, f.id, "thumb")}
                alt={f.name}
                className="w-full h-full object-cover"
                loading="lazy"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-2xl">
                {f.mimeType?.startsWith("video/") ? "🎬" : f.mimeType === "application/pdf" ? "📄" : "🖼️"}
              </div>
            )}
            {/* Zoom hint on hover */}
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/25 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
              <span className="text-white text-xl drop-shadow" aria-hidden="true">⛶</span>
            </div>
          </button>
        ))}
      </div>
      {lightboxIdx !== null && files[lightboxIdx] && (
        <PhotoLightbox orderId={orderId}
          files={files}
          index={lightboxIdx}
          onIndexChange={setLightboxIdx}
          onClose={() => setLightboxIdx(null)}
        />
      )}
    </div>
  );
}


// ---- Drive Photos Panel (Order Detail → Drive tab) ----
function DrivePhotosPanel({ orderId, order }) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const canUpload = ["packing_orders", "inventory_packing", "back_office", "super_admin"].includes(user?.role);
  const fileInputRef = React.useRef(null);
  const cameraInputRef = React.useRef(null);
  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: ["drive-photos", orderId],
    queryFn: async () => (await api.get(`/orders/${orderId}/drive-photos`)).data,
    refetchOnWindowFocus: false,
  });
  const uploadKind = uploadKindFor(order, user);
  const upload = useDriveUpload(orderId, { kind: uploadKind });
  const sync = useMutation({
    mutationFn: async () => (await api.post("/drive/sync")).data,
    onSuccess: (s) => {
      const linked = s?.newly_linked ?? 0;
      toast.success(`Drive sync: ${linked} newly linked · ${s?.already_linked ?? 0} already linked · ${s?.scanned_po_folders ?? 0} scanned`);
      qc.invalidateQueries({ queryKey: ["order", orderId] });
      qc.invalidateQueries({ queryKey: ["drive-photos", orderId] });
      qc.invalidateQueries({ queryKey: ["orders"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Drive not connected"),
  });

  // ---- Lightbox state ----
  // When set, opens a full-screen viewer for `files[lightboxIdx]`. Keyboard:
  // Escape closes, ←/→ navigate to the prev/next image (with wrap-around).
  const [lightboxIdx, setLightboxIdx] = React.useState(null);

  // ---- AI auto-decode state ----
  // The button now decodes AND immediately applies the suggestions to the order
  // (blank fields only, so we never blow away user-entered values). The card
  // that appears afterwards is a READ-ONLY summary of what was filled + what
  // was skipped, with a "Re-apply with overwrite" retry option for conflicts.
  const [lastResult, setLastResult] = React.useState(null); // { ok, decoded, applied: [], skipped: [] }

  const decode = useMutation({
    mutationFn: async () => {
      const decoded = (await api.post(`/orders/${orderId}/auto-decode`, {}, { timeout: 90000 })).data;
      const suggestions = decoded.suggestions || {};
      if (Object.keys(suggestions).length === 0) {
        return { ok: false, decoded, applied: [], skipped: [] };
      }
      const applyRes = (await api.post(`/orders/${orderId}/apply-suggestions`, {
        fields: suggestions,
        overwrite: false,
      })).data;
      return { ok: true, decoded, applied: applyRes.applied || [], skipped: applyRes.skipped || [] };
    },
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["order", orderId] });
      qc.invalidateQueries({ queryKey: ["orders"] });
      setLastResult(r);
      if (!r.ok) {
        toast.warning(`AI analyzed ${r.decoded?.images_analyzed ?? 0} photos but couldn't extract any fields.`);
        return;
      }
      const parts = [];
      if (r.applied.length) parts.push(`✓ Filled ${r.applied.length}: ${r.applied.join(", ")}`);
      if (r.skipped.length) parts.push(`${r.skipped.length} skipped (already had values)`);
      toast.success(parts.join(" · "));
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Auto-decode failed"),
  });

  const reapplyOverwrite = useMutation({
    mutationFn: async () => {
      const fields = lastResult?.decoded?.suggestions || {};
      return (await api.post(`/orders/${orderId}/apply-suggestions`, { fields, overwrite: true })).data;
    },
    onSuccess: (d) => {
      const applied = d.applied?.length || 0;
      toast.success(`Overwrote ${applied} field(s) with AI values`);
      setLastResult((prev) => (prev ? { ...prev, applied: d.applied || [], skipped: [] } : prev));
      qc.invalidateQueries({ queryKey: ["order", orderId] });
      qc.invalidateQueries({ queryKey: ["orders"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed to overwrite"),
  });

  // Placeholder seed URLs (…/folders/mock-N) are not real folders.
  const realUrl = (u) => (u && !/\/folders\/mock-\d+$/.test(u) ? u : null);
  const folderUrl = realUrl(data?.folder_url) || realUrl(order.drive_folder_url);
  const folderName = data?.folder_name || order.drive_folder_name;
  const files = data?.files || [];
  const canDecode = !!order.drive_folder_id && files.length > 0;

  return (
    <div>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <p className="text-sm text-slate-600" data-testid="drive-panel-title">
            Photos & documents synced from Google Drive
            {folderName ? <> · <span className="font-mono text-slate-800">{folderName}</span></> : null}
          </p>
          {order.photos_received_at && (
            <p className="text-[11px] uppercase tracking-widest text-emerald-700 mt-1 font-mono">
              📸 Received · {order.photos_received_at.slice(0, 16).replace("T", " ")}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {canDecode && (
            <Button
              onClick={() => decode.mutate()}
              disabled={decode.isPending}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              size="sm"
              data-testid="auto-decode-btn"
              title="Extract VIN + rim size using Claude Vision, then look up brand/model/year from NHTSA"
            >
              🤖 {decode.isPending ? "Decoding…" : "Auto-decode from photos"}
            </Button>
          )}
          {folderUrl && canOpenDrive(user) && (
            <a
              href={folderUrl}
              target="_blank"
              rel="noreferrer"
              data-testid="drive-link"
              className="inline-flex items-center gap-2 px-3 py-2 border border-slate-300 rounded-md text-sm hover:bg-slate-50"
            >
              📁 Open in Drive
            </a>
          )}
          {["back_office", "super_admin"].includes(user?.role) && (
            <LinkDriveFolderButton orderId={orderId} poNumber={order.po_number} hasFolder={!!folderUrl} />
          )}
          {canUpload && (
            <>
              {/* File picker (gallery / downloads / files app) — a Drive folder is created on first upload if missing */}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,video/*"
                multiple
                onChange={(e) => {
                  const files = Array.from(e.target.files || []);
                  if (files.length) upload.mutate(files);
                  e.target.value = "";
                }}
                className="hidden"
                data-testid="drive-upload-input"
              />
              {/* Camera capture (opens the rear camera directly on mobile) */}
              <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={(e) => {
                  const files = Array.from(e.target.files || []);
                  if (files.length) upload.mutate(files);
                  e.target.value = "";
                }}
                className="hidden"
                data-testid="drive-camera-input"
              />
              <Button
                size="sm"
                onClick={() => cameraInputRef.current?.click()}
                disabled={upload.isPending}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="drive-camera-btn"
                title="Take a photo with your device camera"
              >
                {upload.isPending ? "Uploading…" : "📷 Take Photo"}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={upload.isPending}
                data-testid="drive-upload-btn"
                title="Choose a file from your gallery, downloads, or files app"
              >
                {upload.isPending ? "Uploading…" : "📤 Choose File"}
              </Button>
            </>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => sync.mutate()}
            disabled={sync.isPending}
            data-testid="drive-sync-btn"
          >
            🔄 {sync.isPending ? "Syncing…" : "Sync from Drive"}
          </Button>
        </div>
      </div>

      {/* --- AI Result Summary Card (read-only view of what was auto-applied) --- */}
      {lastResult && (
        <div className="mb-6 border-2 border-indigo-300 bg-indigo-50/40 rounded-lg p-5" data-testid="ai-result-card">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div>
              <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                🤖 Auto-Decode Result
                <span className="text-[10px] uppercase tracking-widest bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded font-mono">
                  {lastResult.decoded?.images_analyzed || 0} photos analyzed
                </span>
              </h3>
              {lastResult.decoded?.ai?.notes && (
                <p className="text-xs text-slate-600 mt-1 italic max-w-2xl">{lastResult.decoded.ai.notes}</p>
              )}
            </div>
            <button
              onClick={() => setLastResult(null)}
              className="text-slate-400 hover:text-slate-700 text-sm bg-transparent border-none p-0 cursor-pointer"
              data-testid="ai-result-dismiss"
            >
              ✕ Dismiss
            </button>
          </div>

          {!lastResult.ok && (
            <div className="text-sm text-slate-600 italic">No usable fields could be extracted from the photos.</div>
          )}

          {lastResult.ok && (
            <div className="space-y-1.5">
              {Object.entries(lastResult.decoded?.suggestions || {}).map(([field, value]) => {
                const wasApplied = lastResult.applied.includes(field);
                const wasSkipped = lastResult.skipped.includes(field);
                const conf = field === "vin" ? lastResult.decoded?.ai?.vin_confidence : field === "rim_size" ? lastResult.decoded?.ai?.rim_confidence : null;
                return (
                  <div key={field} className="flex items-center gap-3 p-2 rounded bg-white/60" data-testid={`ai-result-row-${field}`}>
                    <span className={`text-lg leading-none ${wasApplied ? "text-emerald-600" : wasSkipped ? "text-amber-600" : "text-slate-400"}`} title={wasApplied ? "Filled" : wasSkipped ? "Skipped (already filled)" : ""}>
                      {wasApplied ? "✓" : wasSkipped ? "⚠" : "·"}
                    </span>
                    <span className="text-[11px] uppercase tracking-widest text-slate-500 font-mono w-24">{field.replace("_", " ")}</span>
                    <span className="font-medium text-slate-900 font-mono text-sm truncate flex-1" data-testid={`ai-result-val-${field}`}>{String(value)}</span>
                    {conf && (
                      <span className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded font-mono ${
                        conf === "high" ? "bg-emerald-100 text-emerald-700" :
                        conf === "medium" ? "bg-amber-100 text-amber-700" :
                        "bg-slate-100 text-slate-500"
                      }`}>
                        {conf}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {lastResult.ok && (
            <div className="mt-4 pt-3 border-t border-indigo-200 flex items-center justify-between flex-wrap gap-3">
              <div className="text-xs text-slate-600">
                <span className="font-semibold text-emerald-700">{lastResult.applied.length}</span> filled
                {lastResult.skipped.length > 0 && (
                  <> · <span className="font-semibold text-amber-700">{lastResult.skipped.length}</span> skipped (existing values kept)</>
                )}
              </div>
              {lastResult.skipped.length > 0 && (
                <Button
                  onClick={() => reapplyOverwrite.mutate()}
                  disabled={reapplyOverwrite.isPending}
                  variant="outline"
                  size="sm"
                  data-testid="ai-result-reapply-overwrite"
                >
                  {reapplyOverwrite.isPending ? "Overwriting…" : `↻ Re-apply and overwrite ${lastResult.skipped.length} field(s)`}
                </Button>
              )}
            </div>
          )}
        </div>
      )}

      {isLoading || isFetching ? (
        <div className="text-sm text-slate-500 py-8 text-center" data-testid="drive-loading">Loading photos…</div>
      ) : !folderUrl ? (
        <div className="text-sm text-slate-500 py-8 border border-dashed border-slate-300 rounded-md text-center" data-testid="drive-empty">
          No Drive folder linked yet for <span className="font-mono">{order.po_number}</span>.
          <br />Photos land in Drive when the customer submits the Jotform — click <b>Sync from Drive</b> above (auto-sync runs every 5 min).
          {canUpload && <><br />Or use <b>Upload photos</b> above: the Drive folder is created automatically on the first upload.</>}
        </div>
      ) : data?.error === "drive_not_connected" ? (
        <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-4" data-testid="drive-not-connected">
          Google Drive is not connected. Ask Super Admin to connect it in <b>Settings → Integrations</b>.
        </div>
      ) : data?.error === "folder_missing" ? (
        <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-md p-4" data-testid="drive-folder-missing">
          The linked Drive folder{folderName ? <> <span className="font-mono">{folderName}</span></> : null} was deleted, moved or un-shared — Drive can no longer open it.
          <br />Use <b>🔗 Link Drive folder</b> above to paste the link of the folder that holds the photos, or upload photos to create a new one.
        </div>
      ) : files.length === 0 ? (
        <div className="text-sm text-slate-500 py-8 border border-dashed border-slate-300 rounded-md text-center" data-testid="drive-no-files">
          Folder linked but no image/video files inside yet.
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3" data-testid="drive-photos-grid">
          {files.map((f, idx) => (
            <button
              key={f.id}
              type="button"
              onClick={() => setLightboxIdx(idx)}
              data-testid={`drive-photo-${f.id}`}
              className="group text-left border border-slate-200 rounded-lg overflow-hidden bg-white hover:shadow-md hover:border-slate-400 transition-all cursor-zoom-in"
              title={`Click to enlarge · ${f.name}`}
            >
              <div className="aspect-square bg-slate-100 flex items-center justify-center relative">
                {isImageFile(f) ? (
                  <img
                    src={photoUrl(orderId, f.id, "thumb")}
                    alt={f.name}
                    className="w-full h-full object-cover"
                    loading="lazy"
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                  />
                ) : (
                  <span className="text-3xl">{f.mimeType?.startsWith("video/") ? "🎬" : f.mimeType === "application/pdf" ? "📄" : "🖼️"}</span>
                )}
                {/* Zoom hint on hover */}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                  <span className="text-white text-2xl drop-shadow" aria-hidden="true">⛶</span>
                </div>
              </div>
              <div className="p-2 border-t border-slate-100">
                <div className="text-[11px] text-slate-700 truncate font-medium">{f.name}</div>
                <div className="text-[10px] text-slate-400 font-mono">{(f.modifiedTime || "").slice(0, 10)}</div>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* --- Lightbox overlay --- */}
      {lightboxIdx !== null && files[lightboxIdx] && (
        <PhotoLightbox orderId={orderId}
          files={files}
          index={lightboxIdx}
          onIndexChange={setLightboxIdx}
          onClose={() => setLightboxIdx(null)}
        />
      )}
    </div>
  );
}

// Shows the (already cached) 400 px thumb instantly, swaps in the 1600 px image
// the moment it arrives — the viewer never opens on a black box.
function ProgressiveImage({ thumbSrc, largeSrc, alt, style, onMouseDown, onClick }) {
  const [largeReady, setLargeReady] = React.useState(false);
  const [failed, setFailed] = React.useState(false);
  if (failed) return null;
  return (
    <div className="relative max-w-full max-h-full flex items-center justify-center" style={{ width: "100%", height: "100%" }}>
      <img
        src={largeReady ? largeSrc : thumbSrc}
        alt={alt}
        draggable={false}
        style={{ ...style, filter: largeReady ? "none" : "blur(2px)" }}
        onMouseDown={onMouseDown}
        onClick={onClick}
        className="max-w-full max-h-full object-contain shadow-2xl"
        data-testid="lightbox-image"
        data-resolution={largeReady ? "large" : "thumb"}
        onError={() => { if (largeReady) setFailed(true); }}
      />
      {!largeReady && (
        <>
          <img src={largeSrc} alt="" className="hidden" onLoad={() => setLargeReady(true)} onError={() => setLargeReady(true)} />
          <div className="absolute bottom-3 right-3 flex items-center gap-2 text-[11px] font-mono text-white/80 bg-black/50 px-2 py-1 rounded" data-testid="lightbox-loading">
            <span className="inline-block w-3 h-3 border-2 border-white/40 border-t-white rounded-full animate-spin" />
            loading full size…
          </div>
        </>
      )}
    </div>
  );
}

// Warms the browser cache for the previous / next photo so ← → feel instant.
function LightboxPreload({ orderId, files, index }) {
  React.useEffect(() => {
    const total = files.length;
    if (total < 2) return;
    [(index + 1) % total, (index - 1 + total) % total].forEach((i) => {
      const f = files[i];
      if (f && (f.mimeType || "").startsWith("image/")) {
        const img = new Image();
        img.src = photoUrl(orderId, f.id, "large");
      }
    });
  }, [orderId, files, index]);
  return null;
}

// ---- Photo Lightbox (in-app enlarged viewer with prev/next navigation) ----
// Keyboard: Escape closes · ← previous · → next · +/− zoom · 0 reset
// Interactions:
//   • Click the dark backdrop to close.
//   • Drag the top bar to slide the whole viewer around the screen.
//   • When zoomed in, click-and-drag inside the image to pan.
//   • PDFs render inline in a preview iframe (Drive preview URL).
function PhotoLightbox({ orderId, files, index, onIndexChange, onClose }) {
  const { user } = useAuth();
  const total = files.length;
  const cur = files[index];
  const [zoom, setZoom] = React.useState(1);
  // Image pan offset (in unscaled px, applied via translate)
  const [imgPan, setImgPan] = React.useState({ x: 0, y: 0 });
  // Viewer window offset (drag the whole panel around)
  const [viewerPan, setViewerPan] = React.useState({ x: 0, y: 0 });
  // Active drag mode: 'image' | 'viewer' | null
  const dragRef = React.useRef({ mode: null, startX: 0, startY: 0, baseX: 0, baseY: 0 });

  const prev = React.useCallback(() => {
    onIndexChange((index - 1 + total) % total);
  }, [index, total, onIndexChange]);
  const next = React.useCallback(() => {
    onIndexChange((index + 1) % total);
  }, [index, total, onIndexChange]);

  // Reset zoom + pan every time we swap to a different photo
  React.useEffect(() => { setZoom(1); setImgPan({ x: 0, y: 0 }); }, [index]);

  const zoomIn = React.useCallback(() => setZoom((z) => Math.min(6, +(z + 0.25).toFixed(2))), []);
  const zoomOut = React.useCallback(() => {
    setZoom((z) => {
      const nz = Math.max(0.25, +(z - 0.25).toFixed(2));
      if (nz <= 1) setImgPan({ x: 0, y: 0 });
      return nz;
    });
  }, []);
  const zoomReset = React.useCallback(() => { setZoom(1); setImgPan({ x: 0, y: 0 }); }, []);

  React.useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === "ArrowRight") next();
      else if (e.key === "+" || e.key === "=") { e.preventDefault(); zoomIn(); }
      else if (e.key === "-" || e.key === "_") { e.preventDefault(); zoomOut(); }
      else if (e.key === "0") { e.preventDefault(); zoomReset(); }
    };
    window.addEventListener("keydown", onKey);
    // Note: body scroll is *not* locked — the user needs to be able to
    // scroll the underlying order detail page and edit fields while the
    // viewer is open (per user request). The backdrop is also
    // pointer-transparent for the same reason.
    return () => window.removeEventListener("keydown", onKey);
  }, [prev, next, onClose, zoomIn, zoomOut, zoomReset]);

  // Global mousemove/up so a drag started inside still resolves cleanly
  React.useEffect(() => {
    const onMove = (e) => {
      const d = dragRef.current;
      if (!d.mode) return;
      const dx = e.clientX - d.startX;
      const dy = e.clientY - d.startY;
      if (d.mode === "image") setImgPan({ x: d.baseX + dx, y: d.baseY + dy });
      else if (d.mode === "viewer") setViewerPan({ x: d.baseX + dx, y: d.baseY + dy });
    };
    const onUp = () => { dragRef.current = { mode: null, startX: 0, startY: 0, baseX: 0, baseY: 0 }; };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, []);

  const startImageDrag = (e) => {
    if (zoom <= 1) return;
    e.preventDefault();
    e.stopPropagation();
    dragRef.current = {
      mode: "image", startX: e.clientX, startY: e.clientY,
      baseX: imgPan.x, baseY: imgPan.y,
    };
  };
  const startViewerDrag = (e) => {
    e.stopPropagation();
    dragRef.current = {
      mode: "viewer", startX: e.clientX, startY: e.clientY,
      baseX: viewerPan.x, baseY: viewerPan.y,
    };
  };
  const resetViewerPosition = () => setViewerPan({ x: 0, y: 0 });

  if (!cur) return null;
  const isImage = (cur.mimeType || "").startsWith("image/");
  const isPdf = cur.mimeType === "application/pdf";
  const isVideo = (cur.mimeType || "").startsWith("video/");
  // Everything streams from our own origin — no Google URLs (blocked in China).
  const largeSrc = isImage ? photoUrl(orderId, cur.id, "large") : null;
  const thumbSrc = isImage ? photoUrl(orderId, cur.id, "thumb") : null;
  const rawSrc = photoUrl(orderId, cur.id, "raw");
  const showDrive = canOpenDrive(user);

  return (
    <div
      className="fixed inset-0 z-[100] select-none pointer-events-none"
      role="dialog"
      aria-modal="false"
      data-testid="drive-lightbox"
    >
      <LightboxPreload orderId={orderId} files={files} index={index} />
      {/* Floating viewer panel — draggable via the top bar. The rest of the
          screen stays fully clickable so the user can edit the order info
          while the viewer is open. */}
      <div
        className="absolute inset-0 flex items-center justify-center pointer-events-none"
      >
        <div
          className="relative bg-neutral-900 rounded-lg shadow-2xl pointer-events-auto flex flex-col overflow-hidden"
          style={{
            width: "min(94vw, calc(92vh * 4 / 3), 1200px)",
            aspectRatio: "4 / 3",
            transform: `translate(${viewerPan.x}px, ${viewerPan.y}px)`,
          }}
          onClick={(e) => e.stopPropagation()}
          data-testid="lightbox-panel"
        >
          {/* Top bar (drag handle) */}
          <div
            className="relative h-14 px-5 flex items-center justify-between text-white bg-neutral-800/95 border-b border-white/10 cursor-move"
            onMouseDown={startViewerDrag}
            onDoubleClick={resetViewerPosition}
            title="Drag to move the viewer · double-click to recenter"
            data-testid="lightbox-drag-handle"
          >
            <div className="text-sm font-mono truncate max-w-[35%]" data-testid="lightbox-title">
              {cur.name}
            </div>

            {/* Centered close button — sits in the middle of the top bar */}
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onClose(); }}
              onMouseDown={(e) => e.stopPropagation()}
              aria-label="Close viewer"
              className="absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-red-600 hover:bg-red-500 text-white text-2xl leading-none flex items-center justify-center shadow-lg border-2 border-white cursor-pointer transition-transform hover:scale-110"
              data-testid="lightbox-close"
            >
              ×
            </button>

            <div className="flex items-center gap-3 text-xs" onMouseDown={(e) => e.stopPropagation()}>
              <span className="font-mono text-white/70" data-testid="lightbox-counter">
                {index + 1} / {total}
              </span>
              {isImage && (
                <div className="flex items-center gap-1 bg-white/10 rounded-full px-2 py-1" data-testid="lightbox-zoom-controls">
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); zoomOut(); }}
                    className="w-6 h-6 hover:bg-white/20 rounded-full flex items-center justify-center text-base leading-none"
                    aria-label="Zoom out"
                    data-testid="lightbox-zoom-out"
                  >
                    −
                  </button>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); zoomReset(); }}
                    className="px-1.5 font-mono text-[11px] hover:bg-white/20 rounded"
                    title="Reset zoom (press 0)"
                    data-testid="lightbox-zoom-reset"
                  >
                    {Math.round(zoom * 100)}%
                  </button>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); zoomIn(); }}
                    className="w-6 h-6 hover:bg-white/20 rounded-full flex items-center justify-center text-base leading-none"
                    aria-label="Zoom in"
                    data-testid="lightbox-zoom-in"
                  >
                    +
                  </button>
                </div>
              )}
              <a
                href={rawSrc}
                download
                className="underline hover:text-white/70"
                data-testid="lightbox-download"
                onClick={(e) => e.stopPropagation()}
              >
                ⬇ Download
              </a>
              {cur.webViewLink && showDrive && (
                <a
                  href={cur.webViewLink}
                  target="_blank"
                  rel="noreferrer"
                  className="underline hover:text-white/70"
                  data-testid="lightbox-open-in-drive"
                  onClick={(e) => e.stopPropagation()}
                >
                  Open in Drive ↗
                </a>
              )}
            </div>
          </div>

          {/* Prev / Next arrows */}
          {total > 1 && (
            <>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); prev(); }}
                aria-label="Previous photo"
                className="absolute left-3 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-black/60 hover:bg-black/80 text-white text-2xl flex items-center justify-center cursor-pointer transition-colors z-10 border border-white/20"
                data-testid="lightbox-prev"
              >
                ‹
              </button>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); next(); }}
                aria-label="Next photo"
                className="absolute right-3 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-black/60 hover:bg-black/80 text-white text-2xl flex items-center justify-center cursor-pointer transition-colors z-10 border border-white/20"
                data-testid="lightbox-next"
              >
                ›
              </button>
            </>
          )}

          {/* Content area */}
          <div
            className="flex-1 flex items-center justify-center overflow-hidden bg-neutral-950"
            onWheel={(e) => {
              if (!isImage) return;
              if (e.deltaY < 0) zoomIn(); else zoomOut();
            }}
          >
            {isImage && largeSrc && (
              <ProgressiveImage
                key={cur.id}
                thumbSrc={thumbSrc}
                largeSrc={largeSrc}
                alt={cur.name}
                style={{
                  transform: `translate(${imgPan.x}px, ${imgPan.y}px) scale(${zoom})`,
                  transformOrigin: "center center",
                  transition: dragRef.current.mode === "image" ? "none" : "transform 120ms ease-out",
                  cursor: zoom > 1 ? (dragRef.current.mode === "image" ? "grabbing" : "grab") : "zoom-in",
                }}
                onMouseDown={startImageDrag}
                onClick={(e) => { e.stopPropagation(); if (zoom === 1) zoomIn(); }}
              />
            )}

            {isPdf && (
              <iframe
                key={cur.id}
                title={cur.name}
                src={rawSrc}
                className="w-full h-full bg-white"
                data-testid="lightbox-pdf-iframe"
              />
            )}

            {isVideo && (
              <video
                key={cur.id}
                src={rawSrc}
                controls
                className="w-full h-full bg-black"
                data-testid="lightbox-video"
              />
            )}

            {!isImage && !isPdf && !isVideo && (
              <div className="text-white text-center p-8">
                <div className="text-6xl mb-4">📎</div>
                <div className="text-sm text-white/70 font-mono">{cur.name}</div>
                <a href={rawSrc} download className="mt-4 inline-block underline text-white/90" data-testid="lightbox-download-file">
                  ⬇ Download file
                </a>
                {cur.webViewLink && showDrive && (
                  <a
                    href={cur.webViewLink}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-4 inline-block underline text-white/90"
                  >
                    Open in Drive to view →
                  </a>
                )}
              </div>
            )}
          </div>

          {/* Footer hint */}
          <div className="h-8 flex items-center justify-center text-[11px] text-white/50 bg-neutral-900/90 border-t border-white/5 font-mono">
            {(cur.modifiedTime || "").slice(0, 10)} · drag top bar to move · ← → navigate · +/− zoom · 0 reset · Esc close
          </div>
        </div>
      </div>
    </div>
  );
}

// Change History — collapsible timeline of every tracked field edit,
// including automatic ones (e.g. catalog auto-fill) which show up under
// `system`. Rendered inside the Change History tab (BO/SA-only).
function ChangeHistoryPanel({ changeLog }) {
  const entries = Array.isArray(changeLog) ? [...changeLog].reverse() : [];
  const FIELD_LABEL = {
    customer_id: "Customer",
    assigned_factory: "Factory",
    status: "Stage",
    brand: "Brand", model: "Model", year: "Year", trim: "Trim", vin: "VIN", rim_size: "Rim Size",
    color: "Color", logo: "Logo",
    front_cover_code: "Front Cover Code", f_clamp_code: "F-Clamp Code",
    rear_cover_code: "Rear Cover Code", r_clamp_code: "R-Clamp Code",
    catalog_number: "Catalog #",
    spacer: "Spacer", spacer_qty: "Spacer Qty",
    note: "Note", notes: "Notes",
    bin_number: "BIN #", drive_folder_url: "Drive Folder",
  };
  const fmt = (v) => {
    if (v === null || v === undefined || v === "") return <span className="text-slate-400">(empty)</span>;
    if (typeof v === "object") return <code className="text-xs">{JSON.stringify(v).slice(0, 60)}</code>;
    return <span className="font-mono text-xs">{String(v)}</span>;
  };
  if (!entries.length) {
    return (
      <div className="border border-slate-200 rounded-md p-6 text-center text-sm text-slate-400" data-testid="change-history-empty">
        No tracked changes on this order yet.
      </div>
    );
  }
  return (
    <div className="space-y-3" data-testid="change-history-list">
      <p className="text-xs text-slate-500">
        Tracks edits to codes, catalog #, status, customer, factory, notes, and other business fields.
        Newest first.
      </p>
      {entries.map((e, i) => {
        const isSystem = (e.by_role || "").toLowerCase() === "system" || (e.by_email || "").includes("auto-fill");
        return (
          <div
            key={i}
            className={`border rounded-md p-3 ${
              isSystem ? "border-indigo-200 bg-indigo-50/40" : "border-slate-200 bg-white"
            }`}
            data-testid={`change-history-entry-${i}`}
          >
            <div className="flex items-baseline justify-between gap-2 flex-wrap">
              <div className="text-[11px] uppercase tracking-widest font-mono text-slate-500">
                {isSystem ? "🤖 SYSTEM" : `👤 ${e.by || "unknown"}`}
                {e.by_role && !isSystem && <span className="ml-2 text-slate-400">· {e.by_role}</span>}
              </div>
              <div className="text-xs font-mono text-slate-500">
                {(e.at || "").slice(0, 19).replace("T", " ")}
              </div>
            </div>
            {e.reason && (
              <div className="text-xs text-slate-600 italic mt-1">{e.reason}</div>
            )}
            <ul className="mt-2 space-y-1">
              {(e.changes || []).map((ch, j) => (
                <li key={j} className="text-sm flex flex-wrap items-baseline gap-2">
                  <span className="font-semibold text-slate-800">{FIELD_LABEL[ch.field] || ch.field}:</span>
                  {fmt(ch.from)}
                  <span className="text-slate-400">→</span>
                  {fmt(ch.to)}
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );
}

// ============================================================================
// Photo Review checklist — reviewer ticks what IS present. Anything left
// unchecked is what's missing. Sending "Missing Info" builds the email from
// the unchecked items and flips the order to "Waiting Photos" until new files
// arrive on Drive or the VIN/rim fields are manually filled in.
// ============================================================================
// Single-side orders: the pair the customer did not buy is never a "missing photo".
function sideNotOrdered(o, k) {
  if (k === "front") return String(o?.front_cover_code || "").trim().toUpperCase() === "FF";
  if (k === "rear") return String(o?.rear_cover_code || "").trim().toUpperCase() === "RR";
  return false;
}

function PhotoReviewPanel({ order }) {
  const qc = useQueryClient();
  const initial = order.photo_checklist || { vin: false, rim: false, front: false, rear: false, custom_logo: false };
  // NB: `check[k] === true` now means "this info is MISSING and should be
  // included in the Missing-Info email". Flipped from the earlier semantic
  // ("present") to match the customer's expectation that ticking a box adds
  // that section to the outgoing email.
  const [check, setCheck] = useState({
    vin: !!initial.vin,
    rim: !!initial.rim,
    front: !!initial.front,
    rear: !!initial.rear,
    custom_logo: !!initial.custom_logo,
  });
  useEffect(() => {
    setCheck({
      vin: !!(order.photo_checklist || {}).vin,
      rim: !!(order.photo_checklist || {}).rim,
      front: !!(order.photo_checklist || {}).front,
      rear: !!(order.photo_checklist || {}).rear,
      custom_logo: !!(order.photo_checklist || {}).custom_logo,
    });
  }, [order.id, order.photo_checklist]);

  // Preview dialog state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewSubject, setPreviewSubject] = useState("");
  const [customNote, setCustomNote] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);

  const save = useMutation({
    mutationFn: async (checklist) => (await api.post(`/orders/${order.id}/photo-review`, { checklist })).data,
    onSuccess: () => { qc.invalidateQueries(); toast.success("Photo review saved"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Save failed"),
  });

  // Now: items MARKED (checked) are the ones missing → included in email
  const missingList = Object.keys(check).filter((k) => check[k]);
  const nothingMarked = missingList.length === 0;
  // After the Missing-Info email is sent the order flips to "waiting_reply"
  // (formerly it was "photos_incomplete" — that state was retired).
  const isWaiting = !!order.waiting_reply;

  const openPreview = async () => {
    if (nothingMarked) return;
    setPreviewOpen(true);
    setPreviewLoading(true);
    try {
      const firstName = (order.customer?.name || "").trim().split(" ")[0] || "there";
      const orderNumber = String(order.po_number || "").replace(/^PO-?/, "");
      const r = await api.post("/email-templates/missing_info/preview", {
        first_name: firstName,
        order_number: orderNumber,
        missing_fields: missingList,
        custom_note: customNote || null,
      });
      setPreviewHtml(r.data.html || "");
      setPreviewSubject(r.data.subject || "");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
      setPreviewOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  // Re-render preview whenever the note changes (light debounce)
  useEffect(() => {
    if (!previewOpen) return;
    const t = setTimeout(async () => {
      setPreviewLoading(true);
      try {
        const firstName = (order.customer?.name || "").trim().split(" ")[0] || "there";
        const orderNumber = String(order.po_number || "").replace(/^PO-?/, "");
        const r = await api.post("/email-templates/missing_info/preview", {
          first_name: firstName,
          order_number: orderNumber,
          missing_fields: missingList,
          custom_note: customNote || null,
        });
        setPreviewHtml(r.data.html || "");
        setPreviewSubject(r.data.subject || "");
      } catch {}
      setPreviewLoading(false);
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customNote, previewOpen]);

  const send = useMutation({
    mutationFn: async () => {
      return (await api.post(`/orders/${order.id}/send-email`, {
        template_key: "missing_info",
        missing_fields: missingList,
        custom_note: customNote || null,
      })).data;
    },
    onSuccess: () => {
      qc.invalidateQueries();
      toast.success("📧 Missing-info email sent · order flagged Waiting Photos");
      setPreviewOpen(false);
      setCustomNote("");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || e?.message || "Send failed"),
  });

  const toggle = (k) => setCheck((c) => ({ ...c, [k]: !c[k] }));
  const LABELS = { vin: "VIN photo", rim: "Rim size", front: "Front caliper photo", rear: "Rear caliper photo", custom_logo: "Custom logo" };

  return (
    <div
      data-testid="photo-review-panel"
      className={`rounded-md border px-4 py-3 ${isWaiting ? "bg-amber-50 border-amber-200" : nothingMarked ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"}`}
    >
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-widest font-mono text-slate-500">Photo Review — check items that are MISSING</div>
          <div className="mt-0.5 text-sm">
            {isWaiting ? (
              <span className="font-semibold text-violet-800">⏳ Waiting Reply — {order.waiting_reply_note || "email sent"} {order.waiting_reply_at ? "on " + order.waiting_reply_at.slice(0, 16).replace("T", " ") : ""}</span>
            ) : nothingMarked ? (
              <span className="font-semibold text-emerald-800">✓ Nothing marked missing</span>
            ) : (
              <span className="text-amber-800"><b>{missingList.length}</b> item(s) will be included in the email</span>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            data-testid="save-photo-review"
            variant="outline"
            className="border-slate-400"
            onClick={() => save.mutate(check)}
            disabled={save.isPending}
          >
            {save.isPending ? "Saving…" : "💾 Save Review"}
          </Button>
          <Button
            data-testid="send-missing-info"
            onClick={openPreview}
            disabled={nothingMarked}
            className="bg-amber-600 hover:bg-amber-700 text-white"
          >
            {nothingMarked ? "Nothing to send" : "📧 Preview & Send Missing Info"}
          </Button>
        </div>
      </div>
      <div className="mt-3 grid grid-cols-2 sm:grid-cols-5 gap-2">
        {["vin", "rim", "front", "rear", "custom_logo"].filter((k) => !sideNotOrdered(order, k)).map((k) => (
          <label
            key={k}
            data-testid={`photo-check-${k}`}
            className={`flex items-center gap-2 rounded border px-3 py-2 text-sm cursor-pointer ${check[k] ? "border-amber-400 bg-amber-100 text-amber-900" : "border-slate-300 bg-white text-slate-700"}`}
          >
            <input type="checkbox" checked={check[k]} onChange={() => toggle(k)} />
            <span>{LABELS[k]}</span>
          </label>
        ))}
      </div>

      {/* Preview + confirm dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-3xl" data-testid="missing-info-preview">
          <DialogHeader>
            <DialogTitle>Preview Missing-Info email</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-xs font-mono text-slate-500 flex items-center gap-2">
              <span>To:</span>
              <span className="text-slate-800">{order.customer?.email || "—"}</span>
              <span className="ml-4">Subject:</span>
              <span className="text-slate-800 truncate">{previewSubject || "—"}</span>
            </div>
            <div>
              <Label className="text-xs">Add a personal note <span className="text-slate-400">(optional — will appear under the greeting)</span></Label>
              <textarea
                data-testid="missing-info-note"
                className="w-full min-h-[72px] rounded border border-slate-300 px-2 py-1.5 text-sm mt-1"
                value={customNote}
                onChange={(e) => setCustomNote(e.target.value)}
                placeholder="e.g. Hi Steve — thanks so much for your patience! One quick clarification on the front caliper photo…"
              />
            </div>
            <div className="border rounded max-h-[420px] overflow-auto bg-white">
              {previewLoading ? (
                <div className="p-6 text-sm text-slate-400 italic">Loading preview…</div>
              ) : (
                <iframe
                  title="email-preview"
                  className="w-full h-[420px]"
                  srcDoc={previewHtml}
                  data-testid="missing-info-preview-iframe"
                />
              )}
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setPreviewOpen(false)} data-testid="missing-info-preview-cancel">Cancel</Button>
              <Button
                onClick={() => send.mutate()}
                disabled={send.isPending}
                className="bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="missing-info-preview-confirm"
              >
                {send.isPending ? "Sending…" : "✓ Confirm & Send"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ============================================================================
// Emails tab — full audit trail of every email sent to the customer for this
// order. Click a row to view the exact HTML that was sent.
// ============================================================================
function OrderEmailsPanel({ orderId, order }) {
  const [expanded, setExpanded] = useState({});   // { logId: true|false }
  const [bodies, setBodies] = useState({});       // { logId: htmlSnapshot }
  const emailsQ = useQuery({
    queryKey: ["order-emails", orderId],
    queryFn: async () => (await api.get(`/orders/${orderId}/emails`)).data,
    refetchInterval: 15000,
  });
  const rows = emailsQ.data || [];
  const toggle = async (id) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
    if (!bodies[id]) {
      try {
        const { data } = await api.get(`/emails/${id}`);
        setBodies((prev) => ({ ...prev, [id]: data }));
      } catch (e) {
        toast.error("Could not load email");
      }
    }
  };
  const fmt = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
    catch { return iso.slice(0, 16); }
  };
  const templateName = (r) => {
    const base = ({
      photo_reminder: "Photo Reminder",
      thank_you_production: "Thank You – In Production",
      missing_info: "Missing Info",
      lead_to_order: "Lead to Order",
      installation_guide: "Fulfilled Email",
      google_review: "Photo Review",
      photos_received_internal: "Client responded (shop)",
      sdc_outbound: "Sent by Service Desk",
    })[r.template_key || r.kind] || r.template_key || r.kind || "—";
    if (r.by === "auto:installation-resend") return `${base} · re-send`;
    return r.reminder_no ? `${base} #${r.reminder_no}` : base;
  };
  const bodyHtml = (body) => {
    if (body === undefined) return "<em>Loading…</em>";
    if (body?.body_html) return body.body_html;
    const text = body?.body_text || body?.body;
    if (text) return `<pre style="font-family:system-ui,sans-serif;white-space:pre-wrap;padding:16px;margin:0">${String(text).replace(/</g, "&lt;")}</pre>`;
    return "<em>No body was stored for this email (sent before copies were kept).</em>";
  };

  return (
    <div data-testid="order-emails-panel">
      <div className="mb-3">
        <div className="text-sm font-semibold">Emails sent to {order.customer?.email || "—"}</div>
        <div className="text-xs text-slate-500">Every reminder, thank-you, missing-info, and manual message logged with full copy. Click any row to expand.</div>
      </div>
      {emailsQ.isLoading ? (
        <p className="text-sm text-slate-500">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-slate-500 italic">No emails have been sent for this order yet.</p>
      ) : (
        <div className="divide-y divide-slate-200 border border-slate-200 rounded" data-testid="emails-accordion">
          {rows.map((r) => {
            const isOpen = !!expanded[r.id];
            const body = bodies[r.id];
            return (
              <div key={r.id} data-testid={`email-row-${r.id}`}>
                <button
                  type="button"
                  onClick={() => toggle(r.id)}
                  className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-slate-50"
                  data-testid={`email-toggle-${r.id}`}
                >
                  <span className="text-slate-400 text-xs w-3 shrink-0">{isOpen ? "▼" : "▶"}</span>
                  <span className="text-xs font-mono text-slate-500 whitespace-nowrap w-40 shrink-0">{fmt(r.sent_at)}</span>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 border border-slate-200 font-mono whitespace-nowrap shrink-0">{templateName(r)}</span>
                  <span className="text-sm flex-1 truncate">{r.subject}</span>
                  {r.opened_at ? (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-800 border border-emerald-200 font-mono whitespace-nowrap shrink-0" title={`Opened ${fmt(r.opened_at)}${r.clicked_at ? ` · clicked ${fmt(r.clicked_at)}` : ""}`} data-testid={`email-opened-chip-${r.id}`}>
                      ✓ {r.clicked_at ? "clicked" : "opened"}
                    </span>
                  ) : r.source === "SDC" ? (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-violet-50 text-violet-800 border border-violet-200 font-mono whitespace-nowrap shrink-0" title={`Filed by the Service Desk${r.by ? ` · sent by ${r.by}` : ""}${r.attachments?.length ? ` · ${r.attachments.length} attachment(s)` : ""}`} data-testid={`email-sdc-chip-${r.id}`}>
                      SDC{r.attachments?.length ? ` · 📎${r.attachments.length}` : ""}
                    </span>
                  ) : (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-50 text-slate-500 border border-slate-200 font-mono whitespace-nowrap shrink-0" title="No open or click recorded yet (some mail apps block the open pixel)" data-testid={`email-unopened-chip-${r.id}`}>
                      not opened
                    </span>
                  )}
                  {r.cta_url && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-sky-50 text-sky-800 border border-sky-200 font-mono whitespace-nowrap shrink-0" title={r.cta_url} data-testid={`email-link-chip-${r.id}`}>
                      🔗 {r.link_preview?.site || (() => { try { return new URL(r.cta_url).hostname; } catch { return "link"; } })()}
                    </span>
                  )}
                  <span className="text-xs text-slate-500 truncate max-w-[200px] shrink-0">{r.to}</span>
                </button>
                {isOpen && (
                  <div className="px-3 pb-4 bg-slate-50 border-t border-slate-100">
                    <div className="text-xs font-mono text-slate-500 my-2">
                      By {body?.by || r.by || "—"}{r.from_email ? ` · From ${r.from_email}` : ""}{r.cc?.length ? ` · CC ${r.cc.join(", ")}` : ""}{r.bcc ? ` · BCC ${r.bcc}` : ""}{r.suppressed ? " · PREVIEW — not actually sent" : ""}{r.source === "SDC" ? ` · filed by Service Desk${r.gmail_message_id ? ` · Gmail ${r.gmail_message_id}` : ""}` : ""}
                    </div>
                    {r.attachments?.length > 0 && (
                      <div className="mb-2 text-xs text-slate-700 flex flex-wrap gap-1.5" data-testid={`email-attachments-${r.id}`}>
                        {r.attachments.map((a, i) => (
                          <span key={i} className="px-2 py-0.5 rounded border border-slate-300 bg-white font-mono" title={a.type || ""}>📎 {a.name || "attachment"}{a.size ? ` · ${a.size > 1048576 ? `${(a.size / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(a.size / 1024))} KB`}` : ""}</span>
                        ))}
                      </div>
                    )}
                    {r.cta_url && (
                      <div className="mb-2 rounded-md border border-sky-200 bg-white p-2 flex gap-3 items-start" data-testid={`email-link-${r.id}`}>
                        {r.link_preview?.image && <img src={r.link_preview.image} alt="" className="w-16 h-16 object-cover rounded border border-slate-200 shrink-0" />}
                        <div className="min-w-0 flex-1">
                          <div className="text-[10px] uppercase tracking-widest font-mono text-sky-800">🔗 Link the customer received{r.cta_label ? ` · button "${r.cta_label}"` : ""}</div>
                          {r.link_preview?.title && <div className="text-sm font-semibold text-slate-900 truncate">{r.link_preview.title}</div>}
                          <a href={r.cta_url} target="_blank" rel="noreferrer" className="text-[11px] font-mono text-sky-700 underline break-all">{r.cta_url}</a>
                        </div>
                      </div>
                    )}
                    <iframe
                      title={`email-${r.id}`}
                      className="w-full min-h-[420px] bg-white border border-slate-200 rounded"
                      srcDoc={bodyHtml(body)}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


// ============================================================================
// Send Email dialog — template picker + live preview + one-off overrides
// (subject or body edits that apply just to this ONE send without touching
// the saved template).
// ============================================================================
function SendEmailDialog({ open, onOpenChange, order }) {
  const qc = useQueryClient();
  const [templateKey, setTemplateKey] = useState(order.is_lead || order.status === "lead" ? "lead_to_order" : "photo_reminder");
  const [missing, setMissing] = useState({ vin: false, rim: false, front: false, rear: false, custom_logo: false });
  const [subjectOverride, setSubjectOverride] = useState("");
  // Lead-to-Order rule (Mar 2026): Back Office can paste a manual product
  // link so the CTA button in the email lands the customer straight on the
  // checkout page. Only shown for the `lead_to_order` template — blank
  // string keeps the default photo-upload URL for every other template.
  const [purchaseUrl, setPurchaseUrl] = useState("");
  // Manual recipient override — used when the linked customer has no email
  // on file (e.g. Drive-sync LD placeholder). Prefilled with the current
  // customer email so the operator can also correct typos before sending.
  const currentCustEmail = (order.customer?.email || "").trim();
  const [toEmailOverride, setToEmailOverride] = useState(currentCustEmail);
  const isLeadToOrder = templateKey === "lead_to_order";
  const needsRecipient = !currentCustEmail;
  const recipientValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(toEmailOverride.trim());

  const listQ = useQuery({
    queryKey: ["email-templates"],
    queryFn: async () => (await api.get("/email-templates")).data,
    enabled: open,
  });

  const previewQ = useQuery({
    queryKey: ["send-preview", templateKey, order.id, subjectOverride, missing, isLeadToOrder ? purchaseUrl : ""],
    queryFn: async () => (await api.post(`/email-templates/${templateKey}/preview`, {
      first_name: (order.customer?.name || "").split(" ")[0] || "there",
      order_number: (order.po_number || "").replace(/^PO[- ]?/, ""),
      missing_fields: templateKey === "missing_info" ? Object.keys(missing).filter((k) => missing[k]) : undefined,
      template: subjectOverride ? { subject: subjectOverride } : undefined,
      purchase_url: isLeadToOrder ? (purchaseUrl.trim() || undefined) : undefined,
    })).data,
    enabled: open && !!templateKey,
  });

  const send = useMutation({
    mutationFn: async () => (await api.post(`/orders/${order.id}/send-email`, {
      template_key: templateKey,
      missing_fields: templateKey === "missing_info" ? Object.keys(missing).filter((k) => missing[k]) : undefined,
      overrides: subjectOverride ? { subject: subjectOverride } : undefined,
      purchase_url: isLeadToOrder ? (purchaseUrl.trim() || undefined) : undefined,
      to_email_override: toEmailOverride.trim() !== currentCustEmail ? toEmailOverride.trim() : undefined,
    })).data,
    onSuccess: () => {
      qc.invalidateQueries();
      toast.success("📧 Email sent · logged in Emails tab");
      onOpenChange(false);
      setSubjectOverride("");
      setPurchaseUrl("");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Send failed"),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="send-email-dialog">
        <DialogHeader>
          <DialogTitle>Send Email — {order.customer?.name || "—"} &lt;{order.customer?.email || "no email"}&gt;</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 flex-1 overflow-hidden">
          <div className="space-y-3 overflow-y-auto pr-2">
            <div>
              <Label className="text-xs">Template</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {(listQ.data || []).map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => setTemplateKey(t.key)}
                    data-testid={`send-tab-${t.key}`}
                    className={`px-3 py-1.5 rounded-full text-xs border ${templateKey === t.key ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300 hover:border-slate-500"}`}
                  >
                    {t.name}
                  </button>
                ))}
              </div>
            </div>
            {templateKey === "missing_info" && (
              <div className="rounded border border-amber-200 bg-amber-50 p-3">
                <div className="text-[11px] uppercase tracking-widest font-mono text-amber-800">Check the items that ARE MISSING (unchecked = customer already provided)</div>
                <div className="mt-2 grid grid-cols-2 gap-2">
                  {["vin", "rim", "front", "rear", "custom_logo"].filter((k) => !sideNotOrdered(order, k)).map((k) => (
                    <label key={k} className="inline-flex items-center gap-1 text-sm bg-white rounded px-2 py-1 border border-amber-300">
                      <input type="checkbox" checked={missing[k]} onChange={(e) => setMissing((m) => ({ ...m, [k]: e.target.checked }))} />
                      <span>{k === "custom_logo" ? "Custom Logo" : k.toUpperCase()}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
            {isLeadToOrder && (
              <div className="rounded border border-emerald-200 bg-emerald-50 p-3" data-testid="purchase-url-block">
                <Label className="text-[11px] uppercase tracking-widest font-mono text-emerald-800">
                  🛒 Product Link (optional)
                </Label>
                <Input
                  data-testid="purchase-url-input"
                  value={purchaseUrl}
                  placeholder="https://leprecon.plus/products/…"
                  onChange={(e) => setPurchaseUrl(e.target.value)}
                  className="mt-1 bg-white"
                />
                <p className="text-[11px] text-emerald-800 mt-1 leading-snug">
                  Paste the direct-to-checkout URL from your website. The <b>Place Your Order</b> button in the email will link here.
                  Leave blank to use the default upload page. Must start with <code>https://</code>.
                </p>
                {purchaseUrl.trim() && !/^https?:\/\//i.test(purchaseUrl.trim()) && (
                  <p className="text-[11px] text-rose-700 mt-1" data-testid="purchase-url-invalid">
                    ⚠️ URL must start with <code>http://</code> or <code>https://</code> — the CTA button won't use this link until it's a valid URL.
                  </p>
                )}
                {purchaseUrl.trim() && /^https?:\/\//i.test(purchaseUrl.trim()) && (
                  <div className="mt-2 rounded-md border border-emerald-300 bg-white p-2 flex gap-3 items-start" data-testid="purchase-link-preview">
                    {previewQ.data?.link_preview?.image && (
                      <img src={previewQ.data.link_preview.image} alt="" className="w-20 h-20 object-cover rounded border border-slate-200 shrink-0" data-testid="purchase-link-preview-image" />
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="text-[10px] uppercase tracking-widest font-mono text-emerald-800">🔗 Link in this email {previewQ.isFetching ? "· loading preview…" : ""}</div>
                      <div className="text-sm font-semibold text-slate-900 truncate" data-testid="purchase-link-preview-title">
                        {previewQ.data?.link_preview?.title || (previewQ.data?.link_preview && !previewQ.isFetching ? "No page preview available — the link still works" : "…")}
                      </div>
                      {previewQ.data?.link_preview?.site && <div className="text-[11px] text-slate-500">{previewQ.data.link_preview.site}</div>}
                      <a href={purchaseUrl.trim()} target="_blank" rel="noreferrer" className="text-[11px] font-mono text-sky-700 underline break-all" data-testid="purchase-link-preview-url">
                        {purchaseUrl.trim()}
                      </a>
                    </div>
                  </div>
                )}
              </div>
            )}
            <div>
              <Label className="text-xs">Recipient email <span className="text-slate-400">(saved to customer for future sends)</span></Label>
              <Input
                data-testid="send-email-recipient"
                value={toEmailOverride}
                placeholder={needsRecipient ? "e.g. customer@example.com" : "customer@example.com"}
                onChange={(e) => setToEmailOverride(e.target.value)}
                className={needsRecipient && !recipientValid ? "bg-rose-50 border-rose-300" : ""}
              />
              {needsRecipient && (
                <p className="text-[11px] text-rose-700 mt-1" data-testid="send-email-recipient-hint">
                  ⚠️ This lead has no customer email on file. Type the recipient here — we'll save it to the customer record so you don't have to re-type it next time.
                </p>
              )}
            </div>
            <div>
              <Label className="text-xs">Subject <span className="text-slate-400">(optional override — leave blank to use template subject)</span></Label>
              <Input
                data-testid="send-subject-override"
                value={subjectOverride}
                placeholder={previewQ.data?.subject || "…"}
                onChange={(e) => setSubjectOverride(e.target.value)}
              />
            </div>
            <div className="pt-2 flex gap-2">
              <Button
                data-testid="send-email-confirm"
                onClick={() => send.mutate()}
                disabled={send.isPending || !recipientValid}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                {send.isPending ? "Sending…" : "📧 Send Email Now"}
              </Button>
              <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            </div>
            {!recipientValid && (
              <p className="text-xs text-rose-600 italic">Enter a valid recipient email to enable Send.</p>
            )}
          </div>
          <div className="border border-slate-200 rounded overflow-hidden bg-white flex flex-col">
            <div className="px-3 py-2 border-b border-slate-200 bg-slate-50 text-xs text-slate-500">
              Preview · Subject: <span className="text-slate-700">{previewQ.data?.subject || "…"}</span>
            </div>
            <iframe
              title="preview"
              className="flex-1 min-h-[420px] bg-white"
              srcDoc={previewQ.data?.html || "<em>Loading…</em>"}
            />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


// ============================================================================
// Rush flags control — button + popover with 3 independent checkboxes that
// can be combined in any way:
//   • Highlight  → PO # rendered red everywhere
//   • Top        → Order is bubbled to the top of every list
//   • Urgent     → Row painted light-red + 🚩 flag on the line
// ============================================================================
function RushLevelButton({ order }) {
  const qc = useQueryClient();
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const flags = {
    highlight: !!order.rush_highlight,
    top: !!order.rush_top,
    urgent: !!order.rush_urgent,
  };
  const [local, setLocal] = useState(flags);
  React.useEffect(() => {
    setLocal({
      highlight: !!order.rush_highlight,
      top: !!order.rush_top,
      urgent: !!order.rush_urgent,
    });
  }, [order.id, order.rush_highlight, order.rush_top, order.rush_urgent]);

  const set = useMutation({
    mutationFn: async (payload) => (await api.post(`/orders/${order.id}/rush`, payload)).data,
    onSuccess: (d) => {
      qc.invalidateQueries();
      const on = [];
      if (d.rush_highlight) on.push("Highlight");
      if (d.rush_top) on.push("Top");
      if (d.rush_urgent) on.push("Urgent");
      toast.success(on.length ? `Rush · ${on.join(" + ")}` : "Rush cleared");
      setOpen(false);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Update failed"),
  });

  const anyOn = flags.highlight || flags.top || flags.urgent;
  const label = !anyOn
    ? `🚩 ${t("Rush")}`
    : `🚩 ${t("Rush")} · ${[flags.urgent && "U", flags.top && "T", flags.highlight && "H"].filter(Boolean).join("+")}`;
  const btnColor = flags.urgent
    ? "bg-red-700 hover:bg-red-800 text-white animate-pulse"
    : anyOn
    ? "bg-red-500 hover:bg-red-600 text-white"
    : "bg-white hover:bg-red-50 text-red-700 border border-red-300";

  const toggle = (k) => setLocal((s) => ({ ...s, [k]: !s[k] }));

  const OPTIONS = [
    { key: "highlight", label: "Highlight PO # in red", desc: "PO number is rendered red on every screen." },
    { key: "top", label: "Float to the top", desc: "Order is bubbled to the top of every stage tab." },
    { key: "urgent", label: "Urgent — red row + 🚩", desc: "Entire row painted light red and flagged on the line." },
  ];

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="rush-level-btn" className={`${btnColor} whitespace-nowrap`}>
          {label}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md" data-testid="rush-level-dialog">
        <DialogHeader>
          <DialogTitle>Rush this order</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 pt-2">
          <p className="text-xs text-slate-500">Tick any combination — flags stack.</p>
          {OPTIONS.map((opt) => (
            <label
              key={opt.key}
              data-testid={`rush-flag-${opt.key}`}
              className={`flex items-start gap-3 rounded border-2 p-3 cursor-pointer transition-all ${
                local[opt.key]
                  ? "border-red-500 bg-red-50 ring-1 ring-red-300"
                  : "border-slate-200 hover:bg-slate-50"
              }`}
            >
              <input
                type="checkbox"
                checked={local[opt.key]}
                onChange={() => toggle(opt.key)}
                className="mt-1 h-4 w-4"
                data-testid={`rush-check-${opt.key}`}
              />
              <div>
                <div className={`font-semibold ${local[opt.key] ? "text-red-800" : "text-slate-800"}`}>{opt.label}</div>
                <div className="text-xs text-slate-600 mt-0.5">{opt.desc}</div>
              </div>
            </label>
          ))}
          <div className="flex gap-2 pt-2 justify-end">
            <Button
              variant="outline"
              data-testid="rush-clear-all"
              onClick={() => set.mutate({ highlight: false, top: false, urgent: false })}
              disabled={set.isPending || !anyOn}
            >
              Clear all
            </Button>
            <Button
              data-testid="rush-confirm"
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={() => set.mutate(local)}
              disabled={set.isPending}
            >
              {set.isPending ? "Saving…" : "Apply"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


// Manual "Reorder" mark (Back Office / Super Admin) — pins the order to the top
// of every production tab (after Rush) in light red, like claim replacements.
// Hidden when the order is already a warranty / re-order replacement.
function ReorderFlagButton({ order }) {
  const qc = useQueryClient();
  const { t } = useT();
  const on = !!order.reorder_flag;
  const set = useMutation({
    mutationFn: async () => (await api.post(`/orders/${order.id}/reorder-flag`, { on: !on })).data,
    onSuccess: (d) => { qc.invalidateQueries(); toast.success(d.reorder_flag ? t("Marked as Reorder — pinned to the top in light red") : t("Reorder mark removed")); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Update failed"),
  });
  if (order.is_warranty || order.is_reorder) return null;
  return (
    <Button
      data-testid="reorder-flag-btn"
      onClick={() => set.mutate()}
      disabled={set.isPending}
      title={on ? t("Remove the Reorder mark") : t("Mark as Reorder — top of every production tab, light red row")}
      className={`whitespace-nowrap ${on ? "bg-red-600 hover:bg-red-700 text-white" : "bg-white hover:bg-red-50 text-red-700 border border-red-300"}`}
    >
      🔁 {t("Reorder")}{on ? " · ON" : ""}
    </Button>
  );
}


// ============================================================================
// Waiting for Customer Response — compact button + dialog. Rendered inline
// next to "Approve → Pending" in the Pre Order action row. Rep clicks the
// button after placing a phone call or sending a custom (non-template) email,
// picks a channel and drops a note. The order gets tagged and stays flagged
// until either (a) a new file lands in the Drive folder, (b) VIN/rim is
// manually filled in, or (c) the rep clicks the same button again to clear.
// ============================================================================
function WaitingReplyButton({ order }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const [channel, setChannel] = useState("email");

  const set = useMutation({
    mutationFn: async (payload) => (await api.post(`/orders/${order.id}/waiting-reply`, payload)).data,
    onSuccess: (d) => {
      qc.invalidateQueries();
      toast.success(d.waiting_reply ? "⏳ Order tagged Waiting Reply" : "✓ Waiting Reply cleared");
      setOpen(false);
      setNote("");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed to update"),
  });

  const isWaiting = !!order.waiting_reply;

  if (isWaiting) {
    return (
      <Button
        data-testid="clear-waiting-reply"
        aria-pressed
        title="Waiting for the customer's reply — click to clear"
        className="bg-violet-600 hover:bg-violet-700 text-white whitespace-nowrap animate-pulse shadow-[0_0_16px_rgba(139,92,246,0.7)] ring-2 ring-violet-300"
        onClick={() => set.mutate({ active: false })}
        disabled={set.isPending}
      >
        {set.isPending ? "…" : "⏳ Waiting Reply · ON"}
      </Button>
    );
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="mark-waiting-reply" className="whitespace-nowrap bg-white hover:bg-violet-50 text-violet-700 border border-violet-300">
          ⏳ Waiting Reply
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Tag order as Waiting for Customer Reply</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">How did you reach out?</Label>
            <Select value={channel} onValueChange={setChannel}>
              <SelectTrigger data-testid="waiting-channel"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="email">Custom email</SelectItem>
                <SelectItem value="phone">Phone call</SelectItem>
                <SelectItem value="sms">Text message / SMS</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Note (what did you ask them?)</Label>
            <textarea
              data-testid="waiting-note"
              className="w-full min-h-[80px] rounded border border-slate-300 px-2 py-1 text-sm"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. Called Steve to confirm his 20 inch rim size. Left voicemail."
            />
          </div>
          <div className="flex gap-2 pt-2 justify-end">
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button
              data-testid="waiting-confirm"
              className="bg-violet-600 hover:bg-violet-700 text-white"
              onClick={() => set.mutate({ active: true, channel, note })}
              disabled={set.isPending}
            >
              {set.isPending ? "Saving…" : "Tag Order"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}



// ---------------------------------------------------------------------------
// Reminder button + modal — pinned to the order-title row.
// Shows 🔔 with a red badge for active (undismissed) reminders. Click opens
// a Dialog that lists existing reminders (with delete for SA/BO) and a form
// to create a new one (reason from a saved preset list + free-text "add new
// reason", multi-role recipients, days from now, and an optional note).
// 📄 Lead PDF status — did the Drive-folder PDF fill the customer's name / email / phone?
function LeadPdfChip({ order, canScan, className = "" }) {
  const qc = useQueryClient();
  const { t } = useT();
  const scan = useMutation({
    mutationFn: () => api.post(`/orders/${order.id}/lead-rescan-pdf`),
    onSuccess: (r) => {
      qc.invalidateQueries();
      const d = r.data || {};
      if (d.parsed) toast.success(`📄 PDF scanned${d.mode === "vision" ? " (image)" : ""} · ${[d.customer?.name, d.customer?.email, d.customer?.phone].filter(Boolean).join(" · ") || "no contact found"}`);
      else toast.warning(`📄 ${d.error || "PDF could not be read"}`);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Scan failed"),
  });
  const f = order.lead_pdf_fields || {};
  const got = ["name", "email", "phone"].filter((k) => f[k]);
  const parsed = !!order.lead_pdf_parsed_at && !order.lead_pdf_error;
  const label = parsed
    ? `📄 ${t("PDF")} · ${got.length ? got.map((k) => t(k)).join(" · ") : t("no contact found")}`
    : order.lead_pdf_error
      ? `📄 ${t("PDF")} · ${t(order.lead_pdf_error)}`
      : `📄 ${t("PDF")} · ${t("not scanned yet")}`;
  const cls = parsed ? "border-emerald-500 bg-emerald-50 text-emerald-900" : order.lead_pdf_error ? "border-amber-500 bg-amber-50 text-amber-900" : "border-slate-300 bg-slate-50 text-slate-600";
  return (
    <span className={`inline-flex items-center gap-1 rounded-md text-xs font-bold border ${className || "px-2 py-0.5"} ${cls}`} data-testid="detail-lead-pdf-chip"
          title={`${order.lead_pdf_file_name || "PDF"}${order.lead_pdf_parsed_at ? " · parsed " + order.lead_pdf_parsed_at.slice(0, 16).replace("T", " ") : ""}${order.lead_pdf_mode === "vision" ? " · read as image" : ""}${order.lead_pdf_confidence ? " · " + order.lead_pdf_confidence : ""}`}>
      {order.lead_pdf_url ? <a href={order.lead_pdf_url} target="_blank" rel="noopener noreferrer" className="hover:underline" onClick={(e) => e.stopPropagation()}>{label}</a> : label}
      {canScan && (
        <button type="button" data-testid="lead-pdf-rescan-btn" onClick={() => scan.mutate()} disabled={scan.isPending}
                className="ml-1 px-1.5 rounded border border-current/40 hover:bg-white/60 disabled:opacity-50" title={t("Scan the folder PDF again for name / email / phone")}>
          {scan.isPending ? "…" : `⟲ ${t("Scan")}`}
        </button>
      )}
    </span>
  );
}

// FIT mark (Back Office / Super Admin) — lit pink when on; the Report grid paints the PO cell pink.
export const FIT_PINK = "#f4a3dd";
function FitButton({ order, canEdit }) {
  const qc = useQueryClient();
  const on = !!order.fit;
  const toggle = useMutation({
    mutationFn: () => api.post(`/orders/${order.id}/fit`, { fit: !on }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["order", order.id] });
      qc.invalidateQueries({ queryKey: ["orders"] });
      toast.success(r?.data?.fit ? `FIT on · ${order.po_number}` : `FIT off · ${order.po_number}`, { position: "top-center" });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not change Fit"),
  });
  if (!canEdit && !on) return null;
  // Customer-signed fit (review link clicked / photos shared) → different sign: green with ✓ and a camera / star mark.
  const byCustomer = !!order.customer_fit_confirmed_at;
  const viaPhotos = order.customer_fit_confirmed_via === "photos";
  const custTitle = byCustomer ? ` · ✓ signed by the customer (${viaPhotos ? "shared photos" : "clicked the review link"}) ${String(order.customer_fit_confirmed_at).slice(0, 10)}` : "";
  const style = on && byCustomer ? { background: "#bbf7d0", borderColor: "#22c55e", boxShadow: "0 0 12px rgba(34,197,94,0.6)" }
    : on ? { background: FIT_PINK, borderColor: "#e57fc9", boxShadow: "0 0 12px rgba(244,163,221,0.85)" } : undefined;
  return (
    <button
      type="button"
      onClick={() => canEdit && !toggle.isPending && toggle.mutate()}
      disabled={!canEdit || toggle.isPending}
      data-testid="order-fit-btn"
      data-state={on ? (byCustomer ? "customer" : "on") : "off"}
      aria-pressed={on}
      title={(on ? `FIT · marked by ${order.fit_by || "—"}${order.fit_at ? " · " + order.fit_at.slice(0, 10) : ""}${canEdit ? " · click to turn off" : ""}` : "Mark as Fit") + custTitle}
      style={style}
      className={`inline-flex items-center gap-1 h-9 px-3 rounded-md border text-xs font-black tracking-widest transition-colors ${on ? (byCustomer ? "text-emerald-950" : "text-pink-950") : "border-slate-300 bg-white text-slate-400 hover:bg-slate-50 hover:text-slate-700"} ${!canEdit ? "cursor-default" : ""}`}
    >
      FIT
      {byCustomer && (
        <span className="inline-flex items-center gap-0.5 font-bold" data-testid="order-fit-customer-sign">
          ✓{viaPhotos ? <Camera size={13} strokeWidth={2.4} /> : <Star size={13} strokeWidth={2.4} fill="currentColor" />}
        </span>
      )}
    </button>
  );
}

// Reminders surface in the top-of-page alert ticker when due.
// ---------------------------------------------------------------------------
const REMINDER_ROLE_OPTS = [
  { key: "super_admin", label: "Super Admin" },
  { key: "back_office", label: "Back Office" },
  { key: "factory_1", label: "Factory 1" },
  { key: "packing_orders", label: "Packing" },
  { key: "inventory_packing", label: "Inv Packing" },
  { key: "shipping_order", label: "Shipping" },
];

// 🔗 Back Office pastes a Google Drive folder link → the order is linked to that folder
// (verified through Drive, photos_received refreshed). Fixes leads whose auto folder got lost.
function LinkDriveFolderButton({ orderId, poNumber, hasFolder }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState("");
  const link = useMutation({
    mutationFn: async () => (await api.post(`/orders/${orderId}/link-drive-folder`, { url: url.trim() })).data,
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["drive-photos", orderId] });
      qc.invalidateQueries({ queryKey: ["order", orderId] });
      toast.success(`🔗 Linked to ${r.folder_name || "Drive folder"} · ${r.images} image${r.images === 1 ? "" : "s"}`);
      setOpen(false); setUrl("");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not link that folder"),
  });
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button type="button" data-testid="link-drive-folder-btn" title="Paste a Google Drive folder link to use that folder for this order's photos"
                className="inline-flex items-center gap-2 px-3 py-2 border border-sky-300 text-sky-800 bg-sky-50 hover:bg-sky-100 rounded-md text-sm">
          🔗 {hasFolder ? "Change Drive folder" : "Link Drive folder"}
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-lg" data-testid="link-drive-folder-dialog">
        <DialogHeader><DialogTitle>🔗 Link a Google Drive folder · {poNumber}</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-600">Open the folder in Google Drive, copy the address bar link (…/drive/folders/…) and paste it here. The folder must be shared with the CRM's Google account.</p>
        <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://drive.google.com/drive/folders/…" data-testid="link-drive-folder-input" autoFocus />
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" size="sm" onClick={() => setOpen(false)} data-testid="link-drive-folder-cancel">Cancel</Button>
          <Button size="sm" className="bg-sky-600 hover:bg-sky-700 text-white" disabled={!url.trim() || link.isPending} onClick={() => link.mutate()} data-testid="link-drive-folder-save">
            {link.isPending ? "Linking…" : "Link folder"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// 📦 Advance → warehouse pick-up (no production): choose the warehouse + which factory's cover (F1 black / F2 white number).
// China → Packing Orders · Canada → Inventory Packing · USA → 3PL. Stock is deducted when the station confirms "Product found".
// Batch-order stock units on the shelf are listed per warehouse — picking one reserves that exact set for the order.
function SendToPickupDialog({ order, open, onOpenChange, onDone, fixedWarehouse = null }) {
  const { t } = useT();
  const qc = useQueryClient();
  const [sel, setSel] = useState(null); // { warehouse, factory, unit_id?, unit_po? }
  const warehouses = fixedWarehouse ? WAREHOUSES.filter((w) => w === fixedWarehouse) : WAREHOUSES.filter((w) => w !== "canada");
  const title = fixedWarehouse === "canada" ? t("Pick from Canada storage") : t("Pick from China / USA warehouse");
  React.useEffect(() => { if (open) setSel(null); }, [open, fixedWarehouse]);
  const { data: stock } = useQuery({
    queryKey: ["stock-availability", order.id],
    queryFn: async () => (await api.get(`/orders/${order.id}/stock-availability`)).data,
    enabled: open,
  });
  const send = useMutation({
    mutationFn: (force) => api.post(`/orders/${order.id}/send-to-pickup`, { warehouse: sel.warehouse, factory: sel.factory, force, unit_id: sel.unit_id || null }),
    onSuccess: () => { qc.invalidateQueries(); toast.success(`📦 ${order.po_number} → ${WAREHOUSE_STATION_LABEL[sel.warehouse]} — pick up ${sel.unit_po ? `unit ${sel.unit_po} ` : ""}from ${WAREHOUSE_LABEL[sel.warehouse]} (${FACTORY_SHORT[sel.factory]})`); onOpenChange(false); onDone?.(); },
    onError: (e) => {
      const detail = e?.response?.data?.detail || "Could not send for pick-up";
      if (e?.response?.status === 409 && !sel?.unit_id) { if (window.confirm(`${detail}\n\nSend anyway?`)) send.mutate(true); }
      else toast.error(detail);
    },
  });
  const qty = (w, f) => stock?.per_factory?.[w]?.[f] ?? 0;
  const isSel = (w, f) => sel?.warehouse === w && sel?.factory === f && !sel?.unit_id;
  const unitsFor = (w) => (stock?.units?.[w] || []).filter((u) => !u.reserved_for);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="send-pickup-dialog">
        <DialogHeader><DialogTitle>📦 {title} · {order.po_number}</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-600">
          {fixedWarehouse === "canada"
            ? t("The order skips Production and goes to Inventory Packing with a pick-up ticket. Choose which factory's cover to take — Inventory Packing confirms Product found (stock −1) or Not found (back to Advance as NO INVENTORY → send to Production).")
            : t("The order skips Production. Choose where the part is and which factory's cover to take — the station picks it up and confirms Product found / Not found.")}
        </p>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div className="rounded-md border border-slate-200 bg-slate-50 p-3"><div className="text-[11px] uppercase tracking-widest text-slate-500">{t("Catalog #")}</div><div className="font-mono font-bold text-lg" data-testid="send-pickup-cat">{order.catalog_number || "—"}</div></div>
          <div className="rounded-md border border-slate-200 bg-slate-50 p-3"><div className="text-[11px] uppercase tracking-widest text-slate-500">{t("Color")}</div><div className="font-bold text-lg" data-testid="send-pickup-color">{order.color || "—"}</div></div>
        </div>
        <div className={`grid grid-cols-1 gap-3 ${warehouses.length > 1 ? "md:grid-cols-2" : ""}`}>
          {warehouses.map((w) => (
            <div key={w} className="rounded-lg border-2 border-slate-800 bg-white p-3" data-testid={`send-pickup-${w}`}>
              <div className="text-[10px] uppercase tracking-widest font-mono text-slate-500">{WAREHOUSE_FLAG[w]} {WAREHOUSE_LABEL[w]} → {WAREHOUSE_STATION_LABEL[w]}</div>
              <div className="font-mono font-bold text-2xl mt-0.5" data-testid={`send-pickup-${w}-qty`}>{stock ? (stock.stock?.[w] ?? 0) : "…"}</div>
              {stock?.bins?.[w] && <div className="text-[11px] font-mono text-slate-600" data-testid={`send-pickup-${w}-bin`}>📍 Bin {stock.bins[w]}</div>}
              <div className="flex gap-2 mt-2">
                {STOCK_FACTORIES.map((f) => {
                  const isF2 = f === "factory_2";
                  const active = isSel(w, f);
                  return (
                    <button key={f} type="button" onClick={() => setSel({ warehouse: w, factory: f })} data-testid={`send-pickup-${w}-${f}`}
                            className={`flex-1 rounded-md border-2 px-2 py-1.5 font-mono text-sm font-semibold transition-colors ${active ? "border-amber-600 ring-2 ring-amber-300" : "border-neutral-300"} ${isF2 ? "bg-neutral-900 text-white" : "bg-white text-neutral-900"}`}
                            title={`${FACTORY_SHORT[f]} codes · ${qty(w, f)} in ${WAREHOUSE_LABEL[w]}`}>
                      {FACTORY_SHORT[f]} · {qty(w, f)}
                    </button>
                  );
                })}
              </div>
              {unitsFor(w).length > 0 && (
                <div className="mt-2 space-y-1" data-testid={`send-pickup-${w}-units`}>
                  <div className="text-[10px] uppercase tracking-widest font-mono text-slate-500">📦 {t("Stock units on the shelf — take a specific set")}</div>
                  {unitsFor(w).map((u) => {
                    const active = sel?.unit_id === u.id;
                    return (
                      <button key={u.id} type="button" disabled={!u.same_color} onClick={() => setSel({ warehouse: w, factory: u.factory, unit_id: u.id, unit_po: u.po_number })} data-testid={`send-pickup-unit-${u.po_number}`}
                              title={u.same_color ? `${u.po_number} · ${u.color}${u.bin ? ` · bin ${u.bin}` : ""}` : t("Different colour — use the colour override flow instead")}
                              className={`w-full text-left rounded-md border-2 px-2 py-1 font-mono text-xs flex items-center gap-2 transition-colors disabled:opacity-40 ${active ? "border-indigo-600 ring-2 ring-indigo-300 bg-indigo-50" : "border-slate-200 bg-white hover:border-slate-400"}`}>
                        <span className="font-semibold">{u.po_number}</span>
                        <span className="text-slate-600">{u.color}</span>
                        {u.bin && <span className="text-slate-500">📍 {u.bin}</span>}
                        <span className="ml-auto text-slate-500">{FACTORY_SHORT[u.factory]}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>
        {sel && !sel.unit_id && qty(sel.warehouse, sel.factory) === 0 && (
          <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-2" data-testid="send-pickup-no-stock">
            {t("Inventory shows no stock for this choice — you can still send it; the count will not go below 0.")}
          </div>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} data-testid="send-pickup-cancel">{t("Cancel")}</Button>
          <Button size="sm" className="bg-amber-700 hover:bg-amber-800 text-white" disabled={!sel || send.isPending} onClick={() => send.mutate(false)} data-testid="send-pickup-send">
            {send.isPending ? t("Sending…") : sel ? `📦 ${t("Send to")} ${WAREHOUSE_STATION_LABEL[sel.warehouse]}${sel.unit_po ? ` · ${sel.unit_po}` : ""}` : t("Choose a warehouse + factory")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function RemindersButton({ orderId, poNumber, label }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [customReason, setCustomReason] = useState("");
  const [days, setDays] = useState("1");
  const [note, setNote] = useState("");
  const [roles, setRoles] = useState({ super_admin: false, back_office: true, factory_1: false, packing_orders: false, inventory_packing: false, shipping_order: false });
  const [addingReason, setAddingReason] = useState(false);
  const canDelete = user?.role === "super_admin" || user?.role === "back_office";

  const { data: reasons = [] } = useQuery({
    queryKey: ["reminder-reasons"],
    queryFn: async () => (await api.get("/reminder-reasons")).data,
    staleTime: 60_000,
    enabled: open,
  });
  const { data: reminders = [] } = useQuery({
    queryKey: ["order-reminders", orderId],
    queryFn: async () => (await api.get("/order-reminders", { params: { order_id: orderId } })).data,
    refetchInterval: 30_000,
    enabled: !!orderId,
  });
  const activeCount = (reminders || []).filter((r) => !r.dismissed).length;

  const addReason = useMutation({
    mutationFn: (label) => api.post("/reminder-reasons", { label }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["reminder-reasons"] });
      const lbl = r?.data?.reason?.label || customReason.trim();
      setReason(lbl);
      setCustomReason("");
      setAddingReason(false);
      toast.success(`Reason added · "${lbl}"`);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not add reason"),
  });

  const createReminder = useMutation({
    mutationFn: () => {
      const remind_roles = Object.entries(roles).filter(([, v]) => v).map(([k]) => k);
      return api.post("/order-reminders", {
        order_id: orderId,
        reason: reason.trim(),
        remind_roles,
        days: Math.max(0, parseInt(days || "0", 10)),
        note: note.trim(),
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["order-reminders", orderId] });
      qc.invalidateQueries({ queryKey: ["alerts"] });
      setReason(""); setNote(""); setDays("1");
      setRoles({ super_admin: false, back_office: true, factory_1: false, packing_orders: false, inventory_packing: false, shipping_order: false });
      toast.success("Reminder saved", { position: "top-center" });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not save reminder"),
  });

  const deleteReminder = useMutation({
    mutationFn: (rid) => api.delete(`/order-reminders/${rid}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["order-reminders", orderId] });
      qc.invalidateQueries({ queryKey: ["alerts"] });
      toast.success("Reminder deleted");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not delete"),
  });

  const canSave = reason.trim() && Object.values(roles).some(Boolean) && !createReminder.isPending;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="order-reminders-btn"
        title={activeCount > 0 ? `${activeCount} reminder(s)` : "Set reminder"}
        className={`relative inline-flex items-center justify-center gap-1.5 h-9 rounded-md border bg-white hover:bg-slate-50 text-slate-700 leading-none ${label ? "px-3 text-sm font-medium" : "w-9 text-lg"} ${label && activeCount > 0 ? "border-amber-400 bg-amber-50 text-amber-900" : "border-slate-300"}`}
      >
        🔔{label ? ` ${label}` : ""}
        {activeCount > 0 && (
          <span
            data-testid="order-reminders-badge"
            className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center px-1"
          >
            {activeCount}
          </span>
        )}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="reminders-modal">
          <DialogHeader>
            <DialogTitle>🔔 Reminders · {poNumber}</DialogTitle>
          </DialogHeader>

          {(reminders || []).length > 0 && (
            <div className="mb-3 border border-slate-200 rounded-md divide-y divide-slate-100 max-h-52 overflow-auto" data-testid="reminders-list">
              {reminders.map((r) => {
                const remindAt = new Date(r.remind_at);
                const due = remindAt.getTime() <= Date.now();
                return (
                  <div key={r.id} className="p-2.5 text-sm flex items-start justify-between gap-2" data-testid={`reminder-${r.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-slate-800 truncate">{r.reason}</div>
                      <div className="text-[11px] text-slate-500 mt-0.5">
                        <span className={due ? "text-red-700 font-semibold" : ""}>
                          {due ? "DUE NOW" : `In ${Math.max(0, Math.ceil((remindAt - Date.now()) / 86400000))} day(s)`}
                        </span>
                        {" · "}
                        {(r.remind_roles || []).map((rr) => REMINDER_ROLE_OPTS.find((o) => o.key === rr)?.label || rr).join(", ")}
                        {r.created_by_name && ` · by ${r.created_by_name}`}
                      </div>
                      {r.note && <div className="text-[12px] text-slate-600 mt-1 italic">{r.note}</div>}
                    </div>
                    {canDelete && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-red-600 hover:bg-red-50 h-8"
                        onClick={() => deleteReminder.mutate(r.id)}
                        disabled={deleteReminder.isPending}
                        data-testid={`reminder-delete-${r.id}`}
                      >
                        🗑
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-widest font-mono">Reason</Label>
              <div className="mt-1 grid grid-cols-1 gap-1 max-h-40 overflow-auto border border-slate-200 rounded-md p-2">
                {(reasons || []).map((r) => (
                  <label key={r.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-slate-50 rounded px-1 py-0.5">
                    <input
                      type="radio"
                      name="reminder-reason"
                      value={r.label}
                      checked={reason === r.label}
                      onChange={() => setReason(r.label)}
                      data-testid={`reminder-reason-${r.id}`}
                    />
                    <span>{r.label}</span>
                  </label>
                ))}
                {!addingReason && (
                  <button
                    type="button"
                    className="text-left text-xs text-blue-700 hover:text-blue-900 mt-1 px-1"
                    onClick={() => setAddingReason(true)}
                    data-testid="reminder-add-reason-open"
                  >
                    ➕ Add new reason
                  </button>
                )}
                {addingReason && (
                  <div className="mt-2 flex gap-1">
                    <Input
                      value={customReason}
                      onChange={(e) => setCustomReason(e.target.value)}
                      placeholder="New reason label…"
                      className="h-8"
                      data-testid="reminder-add-reason-input"
                    />
                    <Button
                      type="button"
                      size="sm"
                      className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white"
                      disabled={!customReason.trim() || addReason.isPending}
                      onClick={() => addReason.mutate(customReason.trim())}
                      data-testid="reminder-add-reason-save"
                    >
                      Save
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="h-8"
                      onClick={() => { setAddingReason(false); setCustomReason(""); }}
                    >
                      ✕
                    </Button>
                  </div>
                )}
              </div>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-widest font-mono">Remind whom</Label>
              <div className="mt-1 grid grid-cols-2 gap-1.5">
                {REMINDER_ROLE_OPTS.map((r) => (
                  <label key={r.key} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-slate-50 rounded px-1 py-0.5">
                    <Checkbox
                      checked={!!roles[r.key]}
                      onCheckedChange={(v) => setRoles((s) => ({ ...s, [r.key]: !!v }))}
                      data-testid={`reminder-role-${r.key}`}
                    />
                    <span>{r.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-widest font-mono">In how many days</Label>
                <Input
                  type="number"
                  min="0"
                  value={days}
                  onChange={(e) => setDays(e.target.value)}
                  className="mt-1"
                  data-testid="reminder-days"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest font-mono">Note (optional)</Label>
                <Input
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Anything to remember…"
                  className="mt-1"
                  data-testid="reminder-note"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
              <Button variant="outline" onClick={() => setOpen(false)}>Close</Button>
              <Button
                onClick={() => createReminder.mutate()}
                disabled={!canSave}
                data-testid="reminder-save"
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                {createReminder.isPending ? "Saving…" : "Save Reminder"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ---------------------------------------------------------------------------
// Shipping Address card — visible on the Shopify tab of every order.
// Reads / writes `order.shipping_address` via three flows:
//   • Inline Edit  →  POST /orders/{oid}/shipping-address
//   • Re-sync from Shopify (button)  →  POST /orders/{oid}/resync-shopify
//   • Paste from Shopify (modal)  →  POST /shopify/parse-address-paste
//     then user confirms in the same form → save as inline edit.
// Editing is gated to Back Office + Super Admin (backend enforces).
// ---------------------------------------------------------------------------
function ShippingAddressCard({ order, orderId }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const canEdit = user?.role === "back_office" || user?.role === "super_admin";
  const emptyAddr = { name: "", company: "", phone: "", street1: "", street2: "", city: "", state: "", postal: "", country: "" };
  const current = order.shipping_address || emptyAddr;
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(current);
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState("");

  React.useEffect(() => {
    if (!editing) setForm(order.shipping_address || emptyAddr);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order.shipping_address, editing]);

  const save = useMutation({
    mutationFn: (payload) => api.post(`/orders/${orderId}/shipping-address`, payload),
    onSuccess: () => {
      qc.invalidateQueries();
      setEditing(false);
      toast.success("Shipping address saved");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Save failed"),
  });

  const resync = useMutation({
    mutationFn: () => api.post(`/orders/${orderId}/resync-shopify`),
    onSuccess: (r) => {
      qc.invalidateQueries();
      const n = (r?.data?.changes || []).length;
      if (n === 0) {
        toast.success("Already up-to-date — no changes from Shopify");
      } else {
        toast.success(`Synced from Shopify · ${n} field(s) updated`);
      }
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Re-sync failed"),
  });

  const parsePaste = useMutation({
    mutationFn: (text) => api.post(`/shopify/parse-address-paste`, { text }),
    onSuccess: (r) => {
      const addr = r?.data?.address || emptyAddr;
      setForm(addr);
      setEditing(true);
      setPasteOpen(false);
      setPasteText("");
      toast.success("Parsed — review and hit Save");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not parse"),
  });

  const hasAddr = Object.values(current).some((v) => (v || "").trim());

  return (
    <div className="mt-6 border border-slate-200 rounded-md" data-testid="shipping-address-card">
      <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-slate-200 bg-slate-50 rounded-t-md flex-wrap">
        <div>
          <div className="font-semibold text-slate-800">📦 Shipping Address</div>
          <div className="text-[11px] text-slate-500">Auto-imported from Shopify · editable by Back Office / Super Admin</div>
        </div>
        {canEdit && !editing && (
          <div className="flex items-center gap-2 flex-wrap">
            {(() => {
              if (!isUsOrder({ ...order, shipping_address: current })) return null;
              const onOpenUsmca = () => {
                const token = localStorage.getItem("lp_token");
                const url = `${process.env.REACT_APP_BACKEND_URL}/api/orders/${orderId}/usmca-pdf?copies=3`;
                fetch(url, { headers: { Authorization: `Bearer ${token}` } })
                  .then(async (r) => { if (!r.ok) throw new Error((await r.text()).slice(0, 200)); return r.blob(); })
                  .then((blob) => {
                    const objUrl = URL.createObjectURL(blob);
                    const w = window.open(objUrl, "_blank");
                    if (!w) toast.warning("Pop-up blocked — allow pop-ups to open the USMCA");
                  })
                  .catch((e) => toast.error(`USMCA failed: ${String(e).slice(0, 160)}`));
              };
              return (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={onOpenUsmca}
                  data-testid="ship-addr-usmca"
                  className="border-amber-400 text-amber-900 hover:bg-amber-50"
                  title="Open the USMCA Certificate of Origin (3 copies) for this US-bound order"
                >
                  📄 USMCA (3 copies)
                </Button>
              );
            })()}
            <Button
              size="sm"
              variant="outline"
              onClick={() => resync.mutate()}
              disabled={resync.isPending || !order.shopify_order_number}
              data-testid="ship-addr-resync"
              title={order.shopify_order_number ? "Re-pull the latest data from Shopify" : "This order has no Shopify order number"}
            >
              {resync.isPending ? "Syncing…" : "🔄 Re-sync from Shopify"}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => { setPasteText(""); setPasteOpen(true); }}
              data-testid="ship-addr-paste-open"
            >
              📋 Paste from Shopify
            </Button>
            <Button
              size="sm"
              className="bg-slate-900 hover:bg-slate-800 text-white"
              onClick={() => { setForm(order.shipping_address || emptyAddr); setEditing(true); }}
              data-testid="ship-addr-edit"
            >
              ✎ Edit
            </Button>
          </div>
        )}
      </div>

      {!editing && !hasAddr && (
        <div className="p-6 text-center text-sm text-slate-500" data-testid="ship-addr-empty">
          No shipping address yet.
          {canEdit && <> Click <b>Re-sync from Shopify</b>, <b>Paste from Shopify</b>, or <b>Edit</b> to add one.</>}
        </div>
      )}

      {!editing && hasAddr && (
        <div className="p-4 grid grid-cols-2 gap-x-6 gap-y-2 text-sm" data-testid="ship-addr-view">
          <div>
            <div className="font-semibold text-slate-800">{current.name || <span className="text-slate-400">(no name)</span>}</div>
            {current.company && <div className="text-slate-600">{current.company}</div>}
            <div className="text-slate-700 mt-1">{current.street1}</div>
            {current.street2 && <div className="text-slate-700">{current.street2}</div>}
            <div className="text-slate-700">
              {[current.city, current.state, current.postal].filter(Boolean).join(", ")}
            </div>
            {current.country && <div className="text-slate-600 text-xs mt-0.5">{current.country}</div>}
          </div>
          <div className="text-xs text-slate-600 space-y-1">
            <div><span className="uppercase tracking-widest font-mono text-slate-400">Phone</span> · <a href={`tel:${current.phone}`} className="text-blue-700 hover:underline font-mono">{current.phone || "—"}</a></div>
            {order.customer_email && <div><span className="uppercase tracking-widest font-mono text-slate-400">Email</span> · <a href={`mailto:${order.customer_email}`} className="text-blue-700 hover:underline">{order.customer_email}</a></div>}
          </div>
        </div>
      )}

      {editing && (
        <div className="p-4 space-y-3" data-testid="ship-addr-edit-form">
          <div className="grid grid-cols-2 gap-3">
            <Field2 label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="ship-addr-name" />
            <Field2 label="Company" value={form.company} onChange={(v) => setForm({ ...form, company: v })} testid="ship-addr-company" />
            <Field2 label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="ship-addr-phone" />
            <Field2 label="Country (ISO-2)" value={form.country} onChange={(v) => setForm({ ...form, country: v.toUpperCase() })} testid="ship-addr-country" placeholder="US / CA / MX" />
          </div>
          <Field2 label="Street 1" value={form.street1} onChange={(v) => setForm({ ...form, street1: v })} testid="ship-addr-street1" />
          <Field2 label="Street 2 (Apt / Suite)" value={form.street2} onChange={(v) => setForm({ ...form, street2: v })} testid="ship-addr-street2" />
          <div className="grid grid-cols-3 gap-3">
            <Field2 label="City" value={form.city} onChange={(v) => setForm({ ...form, city: v })} testid="ship-addr-city" />
            <Field2 label="State / Province" value={form.state} onChange={(v) => setForm({ ...form, state: v.toUpperCase() })} testid="ship-addr-state" placeholder="NY / ON" />
            <Field2 label="Postal Code" value={form.postal} onChange={(v) => setForm({ ...form, postal: v })} testid="ship-addr-postal" />
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
            <Button variant="outline" onClick={() => { setEditing(false); setForm(order.shipping_address || emptyAddr); }}>Cancel</Button>
            <Button
              onClick={() => save.mutate(form)}
              disabled={save.isPending || (!form.street1 && !form.city && !form.postal && !form.phone)}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="ship-addr-save"
            >
              {save.isPending ? "Saving…" : "Save"}
            </Button>
          </div>
        </div>
      )}

      <Dialog open={pasteOpen} onOpenChange={setPasteOpen}>
        <DialogContent className="max-w-lg" data-testid="ship-addr-paste-dialog">
          <DialogHeader>
            <DialogTitle>📋 Paste from Shopify</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <p className="text-xs text-slate-600 leading-relaxed">
              Copy the entire <b>Shipping address</b> block from a Shopify order screen and paste it below.
              We'll parse it into fields — you'll get to review and confirm before saving.
            </p>
            <Textarea
              rows={9}
              value={pasteText}
              onChange={(e) => setPasteText(e.target.value)}
              placeholder={"John Doe\nACME Corp\n123 Main St\nApt 4B\nBrooklyn, NY 11201\nUnited States\n+1 917 555 1234"}
              className="font-mono text-xs"
              data-testid="ship-addr-paste-textarea"
            />
            <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
              <Button variant="outline" onClick={() => { setPasteOpen(false); setPasteText(""); }}>Cancel</Button>
              <Button
                onClick={() => parsePaste.mutate(pasteText)}
                disabled={!pasteText.trim() || parsePaste.isPending}
                data-testid="ship-addr-paste-submit"
                className="bg-slate-900 hover:bg-slate-800 text-white"
              >
                {parsePaste.isPending ? "Parsing…" : "Parse & Review"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field2({ label, value, onChange, testid, placeholder }) {
  return (
    <div>
      <Label className="text-[11px] uppercase tracking-widest font-mono text-slate-500">{label}</Label>
      <Input
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-1"
        data-testid={testid}
      />
    </div>
  );
}


// 📦 Stock unit (batch order): destination · state · STOCK-IN · destination change while still in China (Back Office / Super Admin).
function StockUnitPanel({ o, user }) {
  const { t } = useT();
  const qc = useQueryClient();
  const [stockOpen, setStockOpen] = useState(false);
  const info = o.stock_unit_info || {};
  const isBO = ["back_office", "super_admin"].includes(user?.role);
  const changeDest = useMutation({
    mutationFn: (warehouse) => api.patch(`/orders/${o.id}/stock-dest`, { warehouse }),
    onSuccess: (r) => { qc.invalidateQueries(); if (!r.data?.unchanged) toast.success(`📦 ${r.data.previous} → ${r.data.po_number} (${WAREHOUSE_LABEL[r.data.dest]})`); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not change the destination"),
  });
  const release = useMutation({
    mutationFn: () => api.post(`/orders/${o.id}/stock-unit/release`),
    onSuccess: () => { qc.invalidateQueries(); toast.success(t("Reservation released")); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not release"),
  });
  const due = !!info.due;
  const inStock = o.status === "in_stock";
  const cls = due ? "border-red-700 bg-red-50 text-red-900" : inStock ? "border-emerald-600 bg-emerald-50 text-emerald-950" : "border-indigo-500 bg-indigo-50 text-indigo-950";
  return (
    <>
      <div className={`rounded-lg border-2 px-4 py-3 flex flex-wrap items-center justify-between gap-3 ${cls}`} data-testid="stock-unit-panel">
        <div className="min-w-0 text-sm">
          <div className="font-black uppercase tracking-widest text-xs">
            📦 {t("STOCK UNIT")} · {t("batch order")} <Link to={`/batch-orders?cat=${encodeURIComponent(o.catalog_number || "")}`} className="underline" data-testid="stock-unit-batch-link">#{info.batch_no}</Link> → {info.flag} {t(info.dest_label || "")}
            <span className="ml-2 normal-case tracking-normal font-semibold" data-testid="stock-unit-state">· {t(info.state_label || "")}{info.bin ? ` · ${t("bin")} ${info.bin}` : ""}</span>
          </div>
          <div className="mt-1 text-xs">
            {t("Not for a customer")} — {t("Catalog #")} <b className="font-mono">{o.catalog_number || "—"}</b> · {t("colour")} <b>{o.color || "—"}</b>
            {info.in_stock_by && <> · {t("stocked by")} {info.in_stock_by}{info.in_stock_at ? ` · ${new Date(info.in_stock_at).toLocaleString()}` : ""}</>}
            {info.reserved_for && <span className="ml-2" data-testid="stock-unit-reserved">· {t("reserved for")} <Link to={`/order/${info.reserved_for.order_id}`} className="underline font-semibold">{info.reserved_for.po_number}</Link></span>}
            {info.used && <span className="ml-2" data-testid="stock-unit-used">· {info.used.found ? t("used for") : t("missing when picking for")} <Link to={`/order/${info.used.order_id}`} className="underline font-semibold">{info.used.po_number}</Link></span>}
            {!inStock && !info.used && <span className="ml-2">· {t("STOCK-IN by")} <b>{t(info.station_label || "")}</b> {info.dest === "china" ? t("after Finish packing") : t("when Marked received")}</span>}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {isBO && info.dest_editable && (
            <label className="inline-flex items-center gap-1 text-xs font-semibold">
              {t("Destination")}
              <select value={info.dest} onChange={(e) => changeDest.mutate(e.target.value)} disabled={changeDest.isPending} className="h-8 rounded-md border border-slate-300 bg-white px-2 text-xs" data-testid="stock-unit-dest-select">
                {WAREHOUSES.map((w) => <option key={w} value={w}>{WAREHOUSE_FLAG[w]} {WAREHOUSE_LABEL[w]}</option>)}
              </select>
            </label>
          )}
          {isBO && info.reserved_for && <Button size="sm" variant="outline" onClick={() => release.mutate()} disabled={release.isPending} data-testid="stock-unit-release">{t("Release reservation")}</Button>}
          {(due || inStock) && canStockIn(user, info.dest) && (
            <Button className={`${due ? "bg-red-600 hover:bg-red-700" : "bg-emerald-700 hover:bg-emerald-800"} text-white font-black`} onClick={() => setStockOpen(true)} data-testid="stock-in-open-detail">
              📦 {due ? t("STOCK-IN — put it on the shelf") : t("View / print storage label")}
            </Button>
          )}
        </div>
      </div>
      <StockInDialog orderId={o.id} open={stockOpen} onOpenChange={setStockOpen} />
    </>
  );
}
